import {
	AddCompanyUserRequest,
	AddCustomDomainContract,
	AddTaxCategoryRequest,
	AddTaxRegionRequest,
	ChargebeePortalSession,
	BillingInfo,
	BillingStatusEnum,
	ChangeCompanyLogoRequest,
	ChargebeeCreditNoteDetail,
	ChargebeeCreditNoteResponse,
	ChargebeeInvoice,
	ChargebeeInvoiceResponse,
	CompanyUserCount,
	CompanyUsersRequest,
	ConfigurationSettingsResponse,
	CreateLibraryItemRequest,
	CustomDomainItem,
	DatePattern,
	EmailAliasItem,
	EmailAliasRequest,
	GetLibraryRequest,
	GetUserCompanyRequest,
	LibraryItem,
	PauseChargebeeSubscriptionRequest,
	PricingPlan,
	PricingPlanEstimateData,
	PricingPlanEstimateRequest,
	PricingPlanSummary,
	PricingPlanUserRequest,
	PricingSummary,
	TaxCategory,
	TaxRegion,
	UpdatePricingPlanRequest,
	UserCalendar,
	UserCompany,
	UserData,
	UserEmailSignature,
	UserRole,
	GetConfigurationRequest,
	PricingPlanNames,
	UpdateBillingInfoRequest,
	TrialInfoAIWriter
} from "@/Contracts/Users";
import { Action, ActionCreator, Dispatch } from "redux";
import { ThunkAction, ThunkDispatch } from "redux-thunk";

import {
	AmazonProductAppliedFilters,
	CustomDistributorUpdateRequest,
	DefaultErrorMessage,
	NotificationVerb,
	NotificationVerbToReduxKeyMapping,
	PreferenceSettings,
	ZomentumStorageKeys
} from "@/Contracts";
import {
	AddCBIntegrationRequest,
	AddConnectWiseIntegrationRequest,
	AddDAndHIntegrationRequest,
	AddEmailIntegrationRequest,
	AddHubspotIntegrationRequest,
	AddIngramIntegrationRequest,
	AddKaseyaIntegrationRequest,
	AddQuickbooksIntegrationRequest,
	AddRepairShoprIntegrationRequest,
	AddSyncroIntegrationRequest,
	AddSynnexIntegrationRequest,
	AddTechDataIntegrationRequest,
	AddWestcoastIntegrationRequest,
	AutotaskIntegrationResponse,
	ConnectCalendarPostRequest,
	DeleteZapierIntegrationRequest,
	DickerDataCreateOrUpdateRequest,
	EmailIntegration,
	HaloPSAConnectFormValue,
	HubspotMetadataResponse,
	InfusionSoftMetadataRequest,
	Integrations,
	QuickbooksMetadataResponse
} from "@/Contracts/Settings";
import {
	DefaultQuoteLabels,
	QuoteLabels
} from "@/Contracts/ProposalBuilder/Block";

import {
	addApiUser,
	addCompanyUser,
	addCustomDomainService,
	addDickerDataIntegration,
	addEmailAlias,
	confirmCardAdded,
	addTaxCategory,
	addTaxRegion,
	addUserPricingPlan,
	changeCompanyLogo,
	connectDatekDistributor,
	connectUserCalendar,
	createLibraryResource,
	deleteCompanyUser,
	deleteCustomDomainService,
	deleteCustomEmailService,
	deleteDickerDataIntegration,
	deleteEmailAlias,
	deleteLibraryResource,
	disableUser,
	editCompanyPreferences,
	editCompanyUser,
	editTaxCategory,
	editTaxRegion,
	editUserCompany,
	editUserData,
	editUserPassword,
	enableUser,
	fetchUserCalendars,
	fetchUserSignature,
	getChargebeePortalSession,
	getBillingInfo,
	getSubscriptionOverview,
	getChargebeeCreditNoteDetails,
	getChargebeeCreditNotesList,
	getChargebeeInvoiceDetails,
	getChargebeeInvoiceList,
	getCompanyIntegrations,
	getCompanyUsers,
	getConfigurationSettings,
	getDatePatterns,
	getLibraryData,
	getPricingPlans,
	getPricingPlanSummary,
	getTaxCategoriesList,
	getTaxRegionsList,
	getUserCompany,
	getUserData,
	onboardingCallScheduled,
	pauseChargebeeSubscription,
	planEstimate,
	removeUserPricingPlan,
	resendDomainVerificationEmail,
	setSsoEnable,
	setSsoMandatory,
	setTwoFactorAuth,
	updateBillingInfo,
	updateEmailSettings,
	updateLibraryResourceTags,
	updatePricingPlan,
	updateTranslateLanguage,
	updateUserSignature,
	userCompanyConfigUpdate,
	userRequestJoinCompany,
	userRequestWithdrawCompany,
	verifyUserEmailService
} from "@/Services/User";
import {
	addCBIntegration,
	addCompanyAutotaskIntegration,
	addCompanyConnectWiseIntegration,
	addCompanyEmailIntegration,
	addCompanyIngramIntegration,
	addCompanyKaseyaIntegration,
	addCompanyTechDataIntegration,
	addCustomDistributor,
	addDAndHIntegration,
	addHubspotIntegration,
	addQuickbooksIntegration,
	addRepairShoprIntegration,
	addStripeConnection,
	addSyncroIntegration,
	addSynnexIntegration,
	addWestcoastIntegration,
	deleteCBIntegration,
	deleteCompanyAutotaskIntegration,
	deleteCompanyConnectWiseIntegration,
	deleteCompanyEmailIntegration,
	deleteCompanyIngramIntegration,
	deleteCompanyKaseyaIntegration,
	deleteCompanyTechDataIntegration,
	deleteCompanyZapierIntegration,
	deleteCustomDistributor,
	deleteDAndHIntegration,
	deleteHubspotIntegration,
	deleteInfusionSoftIntegration,
	deletePaymentsUser,
	deleteQuickbooksIntegration,
	deleteRepairShoprIntegration,
	deleteStripeUser,
	deleteSyncroIntegration,
	deleteSynnexIntegration,
	deleteWestcoastIntegration,
	getHubspotMetadata,
	getQuickbooksMetadata,
	updateInfusionSoftMetadata,
	updateIntegrationPageDetails,
	deleteAmazonIntegration,
	getAmazonMetadata
} from "@/Services/Settings";

import { ChildEntityHydrationParams } from "@/Contracts/Utils";
import { cache } from "@/Contracts";
import {
	HaloIntegrationRequest,
	HaloPSAAuthDetails,
	HaloPSAMetadata,
	TaxInfo,
	TestHaloPSACredentialsResponse
} from "@pages/Integrations/HaloPSAIntegration/interface";
import {
	addHaloIntegration,
	deleteHaloPSAIntegration,
	fetchHaloPSAAuthDetails,
	fetchHaloPSAMetadata,
	importHaloPSATaxes,
	testHaloPSACredentials
} from "@/Services/User/Integrations/HaloPSA";
import { UserCompanyConfigUpdateRequest } from "@/Contracts/Login";
import { GlobalState } from "@/Reducers/interface";
import { IntegrationPageDetailsUpdateRequest } from "@/Contracts";
import LoginActionCreators from "@/Actions/Login";

interface DedupeSlot<T> {
	inFlight?: Promise<T>;
	result?: T;
	resultAt?: number;
	generation: number;
}

export const BILLING_SUBSCRIPTION_CACHE_TTL_MS = 60_000;

const billingInfoDedupeSlot: DedupeSlot<any> = { generation: 0 };
const subscriptionPlansDedupeSlot: DedupeSlot<any> = { generation: 0 };
const subscriptionDetailsDedupeSlot: DedupeSlot<any> = { generation: 0 };
const subscriptionOverviewDedupeSlot: DedupeSlot<any> = { generation: 0 };

const withDedupe = <T>(
	slot: DedupeSlot<T>,
	runFresh: () => Promise<T>
): Promise<T> => {
	if (slot.inFlight) return slot.inFlight;
	if (
		slot.result !== undefined &&
		slot.resultAt !== undefined &&
		Date.now() - slot.resultAt < BILLING_SUBSCRIPTION_CACHE_TTL_MS
	) {
		return Promise.resolve(slot.result);
	}
	const generationAtStart = slot.generation;
	const promise = (async () => {
		try {
			const result = await runFresh();
			if (slot.generation === generationAtStart) {
				slot.result = result;
				slot.resultAt = Date.now();
			}
			return result;
		} finally {
			if (slot.generation === generationAtStart) {
				slot.inFlight = undefined;
			}
		}
	})();
	slot.inFlight = promise;
	return promise;
};

const invalidateDedupeSlot = (slot: DedupeSlot<unknown>): void => {
	slot.result = undefined;
	slot.resultAt = undefined;
	slot.inFlight = undefined;
	slot.generation += 1;
};

export const invalidateBillingInfoCache = (): void => {
	invalidateDedupeSlot(billingInfoDedupeSlot);
	invalidateDedupeSlot(subscriptionOverviewDedupeSlot);
};

export const invalidateSubscriptionCaches = (): void => {
	invalidateDedupeSlot(subscriptionPlansDedupeSlot);
	invalidateDedupeSlot(subscriptionDetailsDedupeSlot);
	invalidateDedupeSlot(subscriptionOverviewDedupeSlot);
};

export const invalidateBillingAndSubscriptionCaches = (): void => {
	invalidateBillingInfoCache();
	invalidateSubscriptionCaches();
};

export enum UsersActionTypes {
	SHOW_USER_LOADER = "SHOW_USER_LOADER",
	HIDE_USER_LOADER = "HIDE_USER_LOADER",
	SHOW_LIBRARY_LOADER = "SHOW_LIBRARY_LOADER",
	HIDE_LIBRARY_LOADER = "HIDE_LIBRARY_LOADER",
	USER_DATA_REQUESTED = "USER_DATA_REQUESTED",
	USER_DATA_SUCCESSFUL = "USER_DATA_SUCCESSFUL",
	USER_DATA_FAILED = "USER_DATA_FAILED",
	USER_DATA_UPDATED = "USER_DATA_UPDATED",
	EDIT_USER_DATA_REQUESTED = "EDIT_USER_DATA_REQUESTED",
	EDIT_USER_DATA_SUCCESSFUL = "EDIT_USER_DATA_SUCCESSFUL",
	EDIT_USER_DATA_FAILED = "EDIT_USER_DATA_FAILED",
	SET_TWO_FACTOR_AUTH_REQUESTED = "SET_TWO_FACTOR_AUTH_REQUESTED",
	SET_TWO_FACTOR_AUTH_SUCCESSFUL = "SET_TWO_FACTOR_AUTH_SUCCESSFUL",
	SET_TWO_FACTOR_AUTH_FAILED = "SET_TWO_FACTOR_AUTH_FAILED",
	SET_SSO_MANDATORY_REQUESTED = "SET_SSO_MANDATORY_REQUESTED",
	SET_SSO_MANDATORY_SUCCESSFUL = "SET_SSO_MANDATORY_SUCCESSFUL",
	SET_SSO_MANDATORY_FAILED = "SET_SSO_MANDATORY_FAILED",
	SET_SSO_ENABLE_REQUESTED = "SET_SSO_ENABLE_REQUESTED",
	SET_SSO_ENABLE_SUCCESSFUL = "SET_SSO_ENABLE_SUCCESSFUL",
	SET_SSO_ENABLE_FAILED = "SET_SSO_ENABLE_FAILED",
	CONFIGURATION_SETTINGS_REQUESTED = "CONFIGURATION_SETTINGS_REQUESTED",
	CONFIGURATION_SETTINGS_SUCCESSFUL = "CONFIGURATION_SETTINGS_SUCCESSFUL",
	CONFIGURATION_SETTINGS_FAILED = "CONFIGURATION_SETTINGS_FAILED",
	USER_COMPANY_CONFIG_UPDATE_REQUESTED = "USER_COMPANY_CONFIG_UPDATE_REQUESTED",
	USER_COMPANY_CONFIG_UPDATE_SUCCESSFUL = "USER_COMPANY_CONFIG_UPDATE_SUCCESSFUL",
	USER_COMPANY_CONFIG_UPDATE_FAILED = "USER_COMPANY_CONFIG_UPDATE_FAILED",
	COMPANY_DATA_UPDATED = "COMPANY_DATA_UPDATED",
	USER_COMPANY_REQUESTED = "USER_COMPANY_REQUESTED",
	USER_COMPANY_SUCCESSFUL = "USER_COMPANY_SUCCESSFUL",
	USER_COMPANY_FAILED = "USER_COMPANY_FAILED",
	EDIT_USER_COMPANY_REQUESTED = "EDIT_USER_COMPANY_REQUESTED",
	EDIT_USER_COMPANY_SUCCESSFUL = "EDIT_USER_COMPANY_SUCCESSFUL",
	EDIT_USER_COMPANY_FAILED = "EDIT_USER_COMPANY_FAILED",
	EDIT_USER_PASSWORD_REQUESTED = "EDIT_USER_PASSWORD_REQUESTED",
	EDIT_USER_PASSWORD_SUCCESSFUL = "EDIT_USER_PASSWORD_SUCCESSFUL",
	EDIT_USER_PASSWORD_FAILED = "EDIT_USER_PASSWORD_FAILED",
	USERREQUEST_JOINCOMPANY_REQUESTED = "USERREQUEST_JOINCOMPANY_REQUESTED",
	USERREQUEST_JOINCOMPANY_SUCCESSFUL = "USERREQUEST_JOINCOMPANY_SUCCESSFUL",
	USERREQUEST_JOINCOMPANY_FAILED = "USERREQUEST_JOINCOMPANY_FAILED",
	USERREQUEST_WITHDRAWCOMPANY_REQUESTED = "USERREQUEST_WITHDRAWCOMPANY_REQUESTED",
	USERREQUEST_WITHDRAWCOMPANY_SUCCESSFUL = "USERREQUEST_WITHDRAWCOMPANY_SUCCESSFUL",
	USERREQUEST_WITHDRAWCOMPANY_FAILED = "USERREQUEST_WITHDRAWCOMPANY_FAILED",
	// Tax Module Actions
	TAX_CATEGORIES_LIST_ACTION_REQUESTED = "TAX_CATEGORIES_LIST_ACTION_REQUESTED",
	TAX_CATEGORIES_LIST_ACTION_SUCCESSFUL = "TAX_CATEGORIES_LIST_ACTION_SUCCESSFUL",
	TAX_CATEGORIES_LIST_ACTION_FAILED = "TAX_CATEGORIES_LIST_ACTION_FAILED",
	TAX_REGIONS_LIST_ACTION_REQUESTED = "TAX_REGIONS_LIST_ACTION_REQUESTED",
	TAX_REGIONS_LIST_ACTION_SUCCESSFUL = "TAX_REGIONS_LIST_ACTION_SUCCESSFUL",
	TAX_REGIONS_LIST_ACTION_FAILED = "TAX_REGIONS_LIST_ACTION_FAILED",
	ADD_TAX_REGION_ACTION_REQUESTED = "ADD_TAX_REGION_ACTION_REQUESTED",
	ADD_TAX_REGION_ACTION_SUCCESSFUL = "ADD_TAX_REGION_ACTION_SUCCESSFUL",
	ADD_TAX_REGION_ACTION_FAILED = "ADD_TAX_REGION_ACTION_FAILED",
	EDIT_TAX_REGION_ACTION_REQUESTED = "EDIT_TAX_REGION_ACTION_REQUESTED",
	EDIT_TAX_REGION_ACTION_SUCCESSFUL = "EDIT_TAX_REGION_ACTION_SUCCESSFUL",
	EDIT_TAX_REGION_ACTION_FAILED = "EDIT_TAX_REGION_ACTION_FAILED",
	ADD_TAX_CATEGORY_ACTION_REQUESTED = "ADD_TAX_CATEGORY_ACTION_REQUESTED",
	ADD_TAX_CATEGORY_ACTION_SUCCESSFUL = "ADD_TAX_CATEGORY_ACTION_SUCCESSFUL",
	ADD_TAX_CATEGORY_ACTION_FAILED = "ADD_TAX_CATEGORY_ACTION_FAILED",
	EDIT_TAX_CATEGORY_ACTION_REQUESTED = "EDIT_TAX_CATEGORY_ACTION_REQUESTED",
	EDIT_TAX_CATEGORY_ACTION_SUCCESSFUL = "EDIT_TAX_CATEGORY_ACTION_SUCCESSFUL",
	EDIT_TAX_CATEGORY_ACTION_FAILED = "EDIT_TAX_CATEGORY_ACTION_FAILED",
	ENABLE_TAX_REGION_ACTION_REQUESTED = "ENABLE_TAX_REGION_ACTION_REQUESTED",
	ENABLE_TAX_REGION_ACTION_SUCCESSFUL = "ENABLE_TAX_REGION_ACTION_SUCCESSFUL",
	ENABLE_TAX_REGION_ACTION_FAILED = "ENABLE_TAX_REGION_ACTION_FAILED",
	DISABLE_TAX_REGION_ACTION_REQUESTED = "DISABLE_TAX_REGION_ACTION_REQUESTED",
	DISABLE_TAX_REGION_ACTION_SUCCESSFUL = "DISABLE_TAX_REGION_ACTION_SUCCESSFUL",
	DISABLE_TAX_REGION_ACTION_FAILED = "DISABLE_TAX_REGION_ACTION_FAILED",
	ADD_TAX_REGION_DRAWER_OPENED = "ADD_TAX_REGION_DRAWER_OPENED",
	ADD_TAX_REGION_DRAWER_CLOSED = "ADD_TAX_REGION_DRAWER_CLOSED",
	EDIT_TAX_REGION_DRAWER_OPENED = "EDIT_TAX_REGION_DRAWER_OPENED",
	EDIT_TAX_REGION_DRAWER_CLOSED = "EDIT_TAX_REGION_DRAWER_CLOSED",
	ADD_TAX_CATEGORY_DRAWER_OPENED = "ADD_TAX_CATEGORY_DRAWER_OPENED",
	ADD_TAX_CATEGORY_DRAWER_CLOSED = "ADD_TAX_CATEGORY_DRAWER_CLOSED",
	EDIT_TAX_CATEGORY_DRAWER_OPENED = "EDIT_TAX_CATEGORY_DRAWER_OPENED",
	EDIT_TAX_CATEGORY_DRAWER_CLOSED = "EDIT_TAX_CATEGORY_DRAWER_CLOSED",
	// Company Actions
	CHANGE_COMPANY_LOGO_REQUESTED = "CHANGE_COMPANY_LOGO_REQUESTED",
	CHANGE_COMPANY_LOGO_SUCCESSFUL = "CHANGE_COMPANY_LOGO_SUCCESSFUL",
	CHANGE_COMPANY_LOGO_FAILED = "CHANGE_COMPANY_LOGO_FAILED",
	COMPANY_USERS_REQUESTED = "COMPANY_USERS_REQUESTED",
	COMPANY_USERS_SUCCESSFUL = "COMPANY_USERS_SUCCESSFUL",
	COMPANY_USERS_FAILED = "COMPANY_USERS_FAILED",
	GET_DATE_PATTERNS_REQUESTED = "GET_DATE_PATTERNS_REQUESTED",
	GET_DATE_PATTERNS_SUCCESSFUL = "GET_DATE_PATTERNS_SUCCESSFUL",
	GET_DATE_PATTERNS_FAILED = "GET_DATE_PATTERNS_FAILED",
	DELETE_COMPANY_USER_REQUESTED = "DELETE_COMPANY_USER_REQUESTED",
	DELETE_COMPANY_USER_SUCCESSFUL = "DELETE_COMPANY_USER_SUCCESSFUL",
	DELETE_COMPANY_USER_FAILED = "DELETE_COMPANY_USER_FAILED",
	ADD_COMPANY_USER_REQUESTED = "ADD_COMPANY_USER_REQUESTED",
	ADD_COMPANY_USER_SUCCESSFUL = "ADD_COMPANY_USER_SUCCESSFUL",
	ADD_COMPANY_USER_FAILED = "ADD_COMPANY_USER_FAILED",
	EDIT_COMPANY_USER_REQUESTED = "EDIT_COMPANY_USER_REQUESTED",
	EDIT_COMPANY_USER_SUCCESSFUL = "EDIT_COMPANY_USER_SUCCESSFUL",
	EDIT_COMPANY_USER_FAILED = "EDIT_COMPANY_USER_FAILED",
	EDIT_CURRENT_COMPANY_USER_REQUESTED = "EDIT_CURRENT_COMPANY_USER_REQUESTED",
	EDIT_CURRENT_COMPANY_USER_SUCCESSFUL = "EDIT_CURRENT_COMPANY_USER_SUCCESSFUL",
	EDIT_CURRENT_COMPANY_USER_FAILED = "EDIT_CURRENT_COMPANY_USER_FAILED",
	UPDATE_COMPANY_USER = "UPDATE_COMPANY_USER",
	DELETE_COMPANY_EMAIL_INTEGRATION_REQUESTED = "DELETE_COMPANY_EMAIL_INTEGRATION_REQUESTED",
	DELETE_COMPANY_EMAIL_INTEGRATION_SUCCESSFUL = "DELETE_COMPANY_EMAIL_INTEGRATION_SUCCESSFUL",
	DELETE_COMPANY_EMAIL_INTEGRATION_FAILED = "DELETE_COMPANY_EMAIL_INTEGRATION_FAILED",
	ADD_COMPANY_EMAIL_INTEGRATION_REQUESTED = "ADD_COMPANY_EMAIL_INTEGRATION_REQUESTED",
	ADD_COMPANY_EMAIL_INTEGRATION_SUCCESSFUL = "ADD_COMPANY_EMAIL_INTEGRATION_SUCCESSFUL",
	ADD_COMPANY_EMAIL_INTEGRATION_FAILED = "ADD_COMPANY_EMAIL_INTEGRATION_FAILED",
	TOGGLE_CRM_VISIBILITY_REQUESTED = "TOGGLE_CRM_VISIBILITY_REQUESTED",
	TOGGLE_CRM_VISIBILITY_SUCCESSFUL = "TOGGLE_CRM_VISIBILITY_SUCCESSFUL",
	TOGGLE_CRM_VISIBILITY_FAILED = "TOGGLE_CRM_VISIBILITY_FAILED",
	EDIT_COMPANY_PREFERENCES_FAILED = "EDIT_COMPANY_PREFERENCES_FAILED",
	EDIT_COMPANY_PREFERENCES_SUCCESSFUL = "EDIT_COMPANY_PREFERENCES_SUCCESSFUL",
	EDIT_COMPANY_PREFERENCES_REQUESTED = "EDIT_COMPANY_PREFERENCES_REQUESTED",
	UDPATE_TRANSLATE_LANGUAGE_SUCCESSFUL = "UDPATE_TRANSLATE_LANGUAGE_SUCCESSFUL",
	// Connectwise Actions
	DELETE_COMPANY_CONNECTWISE_INTEGRATION_REQUESTED = "DELETE_COMPANY_CONNECTWISE_INTEGRATION_REQUESTED",
	DELETE_COMPANY_CONNECTWISE_INTEGRATION_SUCCESSFUL = "DELETE_COMPANY_CONNECTWISE_INTEGRATION_SUCCESSFUL",
	DELETE_COMPANY_CONNECTWISE_INTEGRATION_FAILED = "DELETE_COMPANY_CONNECTWISE_INTEGRATION_FAILED",
	ADD_COMPANY_CONNECTWISE_INTEGRATION_REQUESTED = "ADD_COMPANY_CONNECTWISE_INTEGRATION_REQUESTED",
	ADD_COMPANY_CONNECTWISE_INTEGRATION_SUCCESSFUL = "ADD_COMPANY_CONNECTWISE_INTEGRATION_SUCCESSFUL",
	ADD_COMPANY_CONNECTWISE_INTEGRATION_FAILED = "ADD_COMPANY_CONNECTWISE_INTEGRATION_FAILED",
	// Autotask Actions
	DELETE_COMPANY_AUTOTASK_INTEGRATION_REQUESTED = "DELETE_COMPANY_AUTOTASK_INTEGRATION_REQUESTED",
	DELETE_COMPANY_AUTOTASK_INTEGRATION_SUCCESSFUL = "DELETE_COMPANY_AUTOTASK_INTEGRATION_SUCCESSFUL",
	DELETE_COMPANY_AUTOTASK_INTEGRATION_FAILED = "DELETE_COMPANY_AUTOTASK_INTEGRATION_FAILED",
	ADD_COMPANY_AUTOTASK_INTEGRATION_REQUESTED = "ADD_COMPANY_AUTOTASK_INTEGRATION_REQUESTED",
	ADD_COMPANY_AUTOTASK_INTEGRATION_SUCCESSFUL = "ADD_COMPANY_AUTOTASK_INTEGRATION_SUCCESSFUL",
	ADD_COMPANY_AUTOTASK_INTEGRATION_FAILED = "ADD_COMPANY_AUTOTASK_INTEGRATION_FAILED",
	// Zapier Actions
	DELETE_COMPANY_ZAPIER_INTEGRATION_REQUESTED = "DELETE_COMPANY_ZAPIER_INTEGRATION_REQUESTED",
	DELETE_COMPANY_ZAPIER_INTEGRATION_SUCCESSFUL = "DELETE_COMPANY_ZAPIER_INTEGRATION_SUCCESSFUL",
	DELETE_COMPANY_ZAPIER_INTEGRATION_FAILED = "DELETE_COMPANY_ZAPIER_INTEGRATION_FAILED",
	// Kaseya Actions
	ADD_COMPANY_KASEYA_INTEGRATION_REQUESTED = "ADD_COMPANY_KASEYA_INTEGRATION_REQUESTED",
	ADD_COMPANY_KASEYA_INTEGRATION_SUCCESSFUL = "ADD_COMPANY_KASEYA_INTEGRATION_SUCCESSFUL",
	ADD_COMPANY_KASEYA_INTEGRATION_FAILED = "ADD_COMPANY_KASEYA_INTEGRATION_FAILED",
	DELETE_COMPANY_KASEYA_INTEGRATION_REQUESTED = "DELETE_COMPANY_KASEYA_INTEGRATION_REQUESTED",
	DELETE_COMPANY_KASEYA_INTEGRATION_SUCCESSFUL = "DELETE_COMPANY_KASEYA_INTEGRATION_SUCCESSFUL",
	DELETE_COMPANY_KASEYA_INTEGRATION_FAILED = "DELETE_COMPANY_KASEYA_INTEGRATION_FAILED",
	// Ingram Actions
	ADD_COMPANY_INGRAM_INTEGRATION_REQUESTED = "ADD_COMPANY_INGRAM_INTEGRATION_REQUESTED",
	ADD_COMPANY_INGRAM_INTEGRATION_SUCCESSFUL = "ADD_COMPANY_INGRAM_INTEGRATION_SUCCESSFUL",
	ADD_COMPANY_INGRAM_INTEGRATION_FAILED = "ADD_COMPANY_INGRAM_INTEGRATION_FAILED",
	DELETE_COMPANY_INGRAM_INTEGRATION_REQUESTED = "DELETE_COMPANY_INGRAM_INTEGRATION_REQUESTED",
	DELETE_COMPANY_INGRAM_INTEGRATION_SUCCESSFUL = "DELETE_COMPANY_INGRAM_INTEGRATION_SUCCESSFUL",
	DELETE_COMPANY_INGRAM_INTEGRATION_FAILED = "DELETE_COMPANY_INGRAM_INTEGRATION_FAILED",
	// Techdata Actions
	ADD_COMPANY_TECH_DATA_INTEGRATION_REQUESTED = "ADD_COMPANY_TECH_DATA_INTEGRATION_REQUESTED",
	ADD_COMPANY_TECH_DATA_INTEGRATION_SUCCESSFUL = "ADD_COMPANY_TECH_DATA_INTEGRATION_SUCCESSFUL",
	ADD_COMPANY_TECH_DATA_INTEGRATION_FAILED = "ADD_COMPANY_TECH_DATA_INTEGRATION_FAILED",
	DELETE_COMPANY_TECH_DATA_INTEGRATION_REQUESTED = "DELETE_COMPANY_TECH_DATA_INTEGRATION_REQUESTED",
	DELETE_COMPANY_TECH_DATA_INTEGRATION_SUCCESSFUL = "DELETE_COMPANY_TECH_DATA_INTEGRATION_SUCCESSFUL",
	DELETE_COMPANY_TECH_DATA_INTEGRATION_FAILED = "DELETE_COMPANY_TECH_DATA_INTEGRATION_FAILED",
	// D and H Actions
	ADD_D_AND_H_INTEGRATION_REQUESTED = "ADD_D_AND_H_INTEGRATION_REQUESTED",
	ADD_D_AND_H_INTEGRATION_SUCCESSFUL = "ADD_D_AND_H_INTEGRATION_SUCCESSFUL",
	ADD_D_AND_H_INTEGRATION_FAILED = "ADD_D_AND_H_INTEGRATION_FAILED",
	DELETE_D_AND_H_INTEGRATION_REQUESTED = "DELETE_D_AND_H_INTEGRATION_REQUESTED",
	DELETE_D_AND_H_INTEGRATION_SUCCESSFUL = "DELETE_D_AND_H_INTEGRATION_SUCCESSFUL",
	DELETE_D_AND_H_INTEGRATION_FAILED = "DELETE_D_AND_H_INTEGRATION_FAILED",
	// Synnex Actions
	ADD_SYNNEX_INTEGRATION_REQUESTED = "ADD_SYNNEX_INTEGRATION_REQUESTED",
	ADD_SYNNEX_INTEGRATION_SUCCESSFUL = "ADD_SYNNEX_INTEGRATION_SUCCESSFUL",
	ADD_SYNNEX_INTEGRATION_FAILED = "ADD_SYNNEX_INTEGRATION_FAILED",
	DELETE_SYNNEX_INTEGRATION_REQUESTED = "DELETE_SYNNEX_INTEGRATION_REQUESTED",
	DELETE_SYNNEX_INTEGRATION_SUCCESSFUL = "DELETE_SYNNEX_INTEGRATION_SUCCESSFUL",
	DELETE_SYNNEX_INTEGRATION_FAILED = "DELETE_SYNNEX_INTEGRATION_FAILED",
	// Connect Booster Actions
	ADD_CB_INTEGRATION_REQUESTED = "ADD_CB_INTEGRATION_REQUESTED",
	ADD_CB_INTEGRATION_SUCCESSFUL = "ADD_CB_INTEGRATION_SUCCESSFUL",
	ADD_CB_INTEGRATION_FAILED = "ADD_CB_INTEGRATION_FAILED",
	DELETE_CB_INTEGRATION_REQUESTED = "DELETE_CB_INTEGRATION_REQUESTED",
	DELETE_CB_INTEGRATION_SUCCESSFUL = "DELETE_CB_INTEGRATION_SUCCESSFUL",
	DELETE_CB_INTEGRATION_FAILED = "DELETE_CB_INTEGRATION_FAILED",
	// Syncro Actions
	ADD_COMPANY_SYNCRO_INTEGRATION_REQUESTED = "ADD_COMPANY_SYNCRO_INTEGRATION_REQUESTED",
	ADD_COMPANY_SYNCRO_INTEGRATION_SUCCESSFUL = "ADD_COMPANY_SYNCRO_INTEGRATION_SUCCESSFUL",
	ADD_COMPANY_SYNCRO_INTEGRATION_FAILED = "ADD_COMPANY_SYNCRO_INTEGRATION_FAILED",
	DELETE_COMPANY_SYNCRO_INTEGRATION_REQUESTED = "DELETE_COMPANY_SYNCRO_INTEGRATION_REQUESTED",
	DELETE_COMPANY_SYNCRO_INTEGRATION_SUCCESSFUL = "DELETE_COMPANY_SYNCRO_INTEGRATION_SUCCESSFUL",
	DELETE_COMPANY_SYNCRO_INTEGRATION_FAILED = "DELETE_COMPANY_SYNCRO_INTEGRATION_FAILED",
	// Infusionsoft Actions
	ADD_INFUSION_SOFT_INTEGRATION_REQUESTED = "ADD_INFUSION_SOFT_INTEGRATION_REQUESTED",
	ADD_INFUSION_SOFT_INTEGRATION_SUCCESFUL = "ADD_INFUSION_SOFT_INTEGRATION_SUCCESFUL",
	ADD_INFUSION_SOFT_INTEGRATION_FAILED = "ADD_INFUSION_SOFT_INTEGRATION_FAILED",
	DELETE_INFUSION_SOFT_INTEGRATION_REQUESTED = "DELETE_INFUSION_SOFT_INTEGRATION_REQUESTED",
	DELETE_INFUSION_SOFT_INTEGRATION_SUCCESFUL = "DELETE_INFUSION_SOFT_INTEGRATION_SUCCESFUL",
	DELETE_INFUSION_SOFT_INTEGRATION_FAILED = "DELETE_INFUSION_SOFT_INTEGRATION_FAILED",

	// Library
	FETCH_LIBRARY_LIST_REQUESTED = "FETCH_LIBRARY_LIST_REQUESTED",
	FETCH_LIBRARY_LIST_SUCCESSFUL = "FETCH_LIBRARY_LIST_SUCCESSFUL",
	FETCH_LIBRARY_LIST_FAILED = "FETCH_LIBRARY_LIST_FAILED",
	SAVE_BLOCK_TO_LIBRARY_LIST_REQUESTED = "SAVE_BLOCK_TO_LIBRARY_LIST_REQUESTED",
	SAVE_BLOCK_TO_LIBRARY_LIST_SUCCESSFUL = "SAVE_BLOCK_TO_LIBRARY_LIST_SUCCESSFUL",
	SAVE_BLOCK_TO_LIBRARY_LIST_FAILED = "SAVE_BLOCK_TO_LIBRARY_LIST_FAILED",
	DELETE_BLOCK_TO_LIBRARY_LIST_REQUESTED = "DELETE_BLOCK_TO_LIBRARY_LIST_REQUESTED",
	DELETE_BLOCK_TO_LIBRARY_LIST_SUCCESSFUL = "DELETE_BLOCK_TO_LIBRARY_LIST_SUCCESSFUL",
	DELETE_BLOCK_TO_LIBRARY_LIST_FAILED = "DELETE_BLOCK_TO_LIBRARY_LIST_FAILED",
	UPDATE_LIBRARY_ITEM_TAGS_SUCCESSFUL = "UPDATE_LIBRARY_ITEM_TAGS_SUCCESSFUL",
	// payments
	CONFIRM_CARD_ADDED_REQUESTED = "CONFIRM_CARD_ADDED_REQUESTED",
	CONFIRM_CARD_ADDED_FAILED = "CONFIRM_CARD_ADDED_FAILED",
	CONFIRM_CARD_ADDED_SUCCESSFUL = "CONFIRM_CARD_ADDED_SUCCESSFUL",
	CHARGEBEE_PORTAL_SESSION_REQUESTED = "CHARGEBEE_PORTAL_SESSION_REQUESTED",
	CHARGEBEE_PORTAL_SESSION_FAILED = "CHARGEBEE_PORTAL_SESSION_FAILED",
	CHARGEBEE_PORTAL_SESSION_SUCCESSFUL = "CHARGEBEE_PORTAL_SESSION_SUCCESSFUL",
	BILLING_INFO_REQUESTED = "BILLING_INFO_REQUESTED",
	BILLING_INFO_FAILED = "BILLING_INFO_FAILED",
	BILLING_INFO_SUCCESSFUL = "BILLING_INFO_SUCCESSFUL",
	SUBSCRIPTION_PLANS_REQUESTED = "SUBSCRIPTION_PLANS_REQUESTED",
	SUBSCRIPTION_PLANS_FAILED = "SUBSCRIPTION_PLANS_FAILED",
	SUBSCRIPTION_PLANS_SUCCESSFUL = "SUBSCRIPTION_PLANS_SUCCESSFUL",
	SUBSCRIPTION_DETAILS_REQUESTED = "SUBSCRIPTION_DETAILS_REQUESTED",
	SUBSCRIPTION_DETAILS_FAILED = "SUBSCRIPTION_DETAILS_FAILED",
	SUBSCRIPTION_DETAILS_SUCCESSFUL = "SUBSCRIPTION_DETAILS_SUCCESSFUL",
	SUBSCRIPTION_PLAN_ADD_USER_REQUESTED = "SUBSCRIPTION_PLAN_ADD_USER_REQUESTED",
	SUBSCRIPTION_PLAN_ADD_USER_FAILED = "SUBSCRIPTION_PLAN_ADD_USER_FAILED",
	SUBSCRIPTION_PLAN_ADD_USER_SUCCESSFUL = "SUBSCRIPTION_PLAN_ADD_USER_SUCCESSFUL",
	SUBSCRIPTION_PLAN_REMOVE_USER_REQUESTED = "SUBSCRIPTION_PLAN_REMOVE_USER_REQUESTED",
	SUBSCRIPTION_PLAN_REMOVE_USER_FAILED = "SUBSCRIPTION_PLAN_REMOVE_USER_FAILED",
	SUBSCRIPTION_PLAN_REMOVE_USER_SUCCESSFUL = "SUBSCRIPTION_PLAN_REMOVE_USER_SUCCESSFUL",
	SUBSCRIPTION_PLAN_UPGRADE_DOWNGRADE_REQUESTED = "SUBSCRIPTION_PLAN_UPGRADE_DOWNGRADE_REQUESTED",
	SUBSCRIPTION_PLAN_UPGRADE_DOWNGRADE_FAILED = "SUBSCRIPTION_PLAN_UPGRADE_DOWNGRADE_FAILED",
	SUBSCRIPTION_PLAN_UPGRADE_DOWNGRADE_SUCCESSFUL = "SUBSCRIPTION_PLAN_UPGRADE_DOWNGRADE_SUCCESSFUL",
	SUBSCRIPTION_PLAN_ESTIMATE_REQUESTED = "SUBSCRIPTION_PLAN_ESTIMATE_REQUESTED",
	SUBSCRIPTION_PLAN_ESTIMATE_FAILED = "SUBSCRIPTION_PLAN_ESTIMATE_FAILED",
	SUBSCRIPTION_PLAN_ESTIMATE_SUCCESSFUL = "SUBSCRIPTION_PLAN_ESTIMATE_SUCCESSFUL",
	SUBSCRIPTION_PLAN_ESTIMATE_RESET = "SUBSCRIPTION_PLAN_ESTIMATE_RESET",
	SHOW_SUBSCRIPTION_DETAILS_LOADER = "SHOW_SUBSCRIPTION_DETAILS_LOADER",
	HIDE_SUBSCRIPTION_DETAILS_LOADER = "HIDE_SUBSCRIPTION_DETAILS_LOADER",
	SHOW_UPDATE_CARD_MODAL = "SHOW_UPDATE_CARD_MODAL",
	HIDE_UPDATE_CARD_MODAL = "HIDE_UPDATE_CARD_MODAL",
	SHOW_BILLING_INFO_LOADER = "SHOW_BILLING_INFO_LOADER",
	HIDE_BILLING_INFO_LOADER = "HIDE_BILLING_INFO_LOADER",
	// Email Alias
	ADD_EMAIL_ALIAS_REQUESTED = "ADD_EMAIL_ALIAS_REQUESTED",
	ADD_EMAIL_ALIAS_SUCCESSFUL = "ADD_EMAIL_ALIAS_SUCCESSFUL",
	ADD_EMAIL_ALIAS_FAILED = "ADD_EMAIL_ALIAS_FAILED",
	DELETE_EMAIL_ALIAS_REQUESTED = "DELETE_EMAIL_ALIAS_REQUESTED",
	DELETE_EMAIL_ALIAS_SUCCESSFUL = "DELETE_EMAIL_ALIAS_SUCCESSFUL",
	DELETE_EMAIL_ALIAS_FAILED = "DELETE_EMAIL_ALIAS_FAILED",

	EDIT_QUOTE_DEFAULT_LABEL_COMPANY_REQUESTED = "EDIT_QUOTE_DEFAULT_LABEL_COMPANY_REQUESTED",
	EDIT_QUOTE_DEFAULT_LABEL_COMPANY_FAILED = "EDIT_QUOTE_DEFAULT_LABEL_COMPANY_FAILED",
	EDIT_QUOTE_DEFAULT_LABEL_COMPANY_SUCCESSFUL = "EDIT_QUOTE_DEFAULT_LABEL_COMPANY_SUCCESSFUL",

	DISABLE_USER_REQUESTED = "DISABLE_USER_REQUESTED",
	DISABLE_USER_SUCCESSFUL = "DISABLE_USER_SUCCESSFUL",
	DISABLE_USER_FAILED = "DISABLE_USER_FAILED",

	ENABLE_USER_REQUESTED = "ENABLE_USER_REQUESTED",
	ENABLE_USER_SUCCESSFUL = "ENABLE_USER_SUCCESSFUL",
	ENABLE_USER_FAILED = "ENABLE_USER_FAILED",

	FETCH_USER_CALENDARS_REQUESTED = "FETCH_USER_CALENDARS_REQUESTED",
	FETCH_USER_CALENDARS_SUCCESSFUL = "FETCH_USER_CALENDARS_SUCCESSFUL",
	FETCH_USER_CALENDARS_FAILED = "FETCH_USER_CALENDARS_FAILED",

	RESET_USER_CALENDARS = "RESET_USER_CALENDARS",

	CONNECT_CALENDAR_REQUESTED = "CONNECT_CALENDAR_REQUESTED",
	CONNECT_CALENDAR_SUCCESSFUL = "CONNECT_CALENDAR_SUCCESSFUL",
	CONNECT_CALENDAR_FAILED = "CONNECT_CALENDAR_FAILED",

	FETCH_COMPANY_INTEGRATIONS_REQUESTED = "FETCH_COMPANY_INTEGRATIONS_REQUESTED",
	FETCH_COMPANY_INTEGRATIONS_SUCCESSFUL = "FETCH_COMPANY_INTEGRATIONS_SUCCESSFUL",
	FETCH_COMPANY_INTEGRATIONS_FAILED = "FETCH_COMPANY_INTEGRATIONS_FAILED",
	// Chargebee Actions
	CHARGEBEE_INVOICES_LIST_REQUESTED = "CHARGEBEE_INVOICES_LIST_REQUESTED",
	CHARGEBEE_INVOICES_LIST_SUCCESSFUL = "CHARGEBEE_INVOICES_LIST_SUCCESSFUL",
	CHARGEBEE_INVOICES_LIST_FAILED = "CHARGEBEE_INVOICES_LIST_FAILED",
	CHARGEBEE_INVOICE_DETAILS_REQUESTED = "CHARGEBEE_INVOICE_DETAILS_REQUESTED",
	CHARGEBEE_INVOICE_DETAILS_SUCCESSFUL = "CHARGEBEE_INVOICE_DETAILS_SUCCESSFUL",
	CHARGEBEE_INVOICE_DETAILS_FAILED = "CHARGEBEE_INVOICE_DETAILS_FAILED",
	CHARGEBEE_CREDIT_NOTES_LIST_REQUESTED = "CHARGEBEE_CREDIT_NOTES_LIST_REQUESTED",
	CHARGEBEE_CREDIT_NOTES_LIST_SUCCESSFUL = "CHARGEBEE_CREDIT_NOTES_LIST_SUCCESSFUL",
	CHARGEBEE_CREDIT_NOTES_LIST_FAILED = "CHARGEBEE_CREDIT_NOTES_LIST_FAILED",
	CHARGEBEE_CREDIT_NOTE_DETAILS_REQUESTED = "CHARGEBEE_CREDIT_NOTES_DETAILS_REQUESTED",
	CHARGEBEE_CREDIT_NOTE_DETAILS_SUCCESSFUL = "CHARGEBEE_CREDIT_NOTE_DETAILS_SUCCESSFUL",
	CHARGEBEE_CREDIT_NOTE_DETAILS_FAILED = "CHARGEBEE_CREDIT_NOTE_DETAILS_FAILED",
	PAUSE_CHARGEBEE_SUBSCRIPTION_REQUESTED = "PAUSE_CHARGEBEE_SUBSCRIPTION_REQUESTED",
	PAUSE_CHARGEBEE_SUBSCRIPTION_SUCCESSFUL = "PAUSE_CHARGEBEE_SUBSCRIPTION_SUCCESSFUL",
	PAUSE_CHARGEBEE_SUBSCRIPTION_FAILED = "PAUSE_CHARGEBEE_SUBSCRIPTION_FAILED",
	// DKIM Actions
	VERIFY_USER_EMAIL = "VERIFY_USER_EMAIL",
	ADD_CUSTOM_DOMAIN = "ADD_CUSTOM_DOMAIN",
	DELETE_CUSTOM_EMAIL = "DELETE_CUSTOM_EMAIL",
	DELETE_CUSTOM_DOMAIN = "DELETE_CUSTOM_DOMAIN",

	RESEND_DOMAIN_VERIFICATION_REQUESTED = "RESEND_DOMAIN_VERIFICATION_REQUESTED",
	RESEND_DOMAIN_VERIFICATION_SUCCESSFUL = "RESEND_DOMAIN_VERIFICATION_SUCCESSFUL",
	RESEND_DOMAIN_VERIFICATION_FAILED = "RESEND_DOMAIN_VERIFICATION_FAILED",

	UPDATED_EMAIL_PREFERENCES_REQUESTED = "UPDATED_EMAIL_PREFERENCES_REQUESTED",
	UPDATED_EMAIL_PREFERENCES_SUCCESSFUL = "UPDATED_EMAIL_PREFERENCES_SUCCESSFUL",
	UPDATED_EMAIL_PREFERENCES_FAILED = "UPDATED_EMAIL_PREFERENCES_FAILED",

	FETCH_EMAIL_SIGNATURES_REQUESTED = "FETCH_EMAIL_SIGNATURES_REQUESTED",
	FETCH_EMAIL_SIGNATURES_SUCCESSFUL = "FETCH_EMAIL_SIGNATURES_SUCCESSFUL",
	FETCH_EMAIL_SIGNATURES_FAILED = "FETCH_EMAIL_SIGNATURES_FAILED",

	UPDATE_EMAIL_SIGNATURES_REQUESTED = "UPDATE_EMAIL_SIGNATURES_REQUESTED",
	UPDATE_EMAIL_SIGNATURES_SUCCESSFUL = "UPDATE_EMAIL_SIGNATURES_SUCCESSFUL",
	UPDATE_EMAIL_SIGNATURES_FAILED = "UPDATE_EMAIL_SIGNATURES_FAILED",

	RESET_USER_STATE = "RESET_USER_STATE",

	ONBOARDING_CALL_SCHEDULED_REQUESTED = "ONBOARDING_CALL_SCHEDULED_REQUESTED",
	ONBOARDING_CALL_SCHEDULED_SUCCESSFUL = "ONBOARDING_CALL_SCHEDULED_SUCCESSFUL",
	ONBOARDING_CALL_SCHEDULED_FAILED = "ONBOARDING_CALL_SCHEDULED_FAILED",

	ADD_DICKER_DATA_INTERGRATION_REQUESTED = "ADD_DICKER_DATA_INTERGRATION_REQUESTED",
	ADD_DICKER_DATA_INTERGRATION_SUCCESSFUL = "ADD_DICKER_DATA_INTERGRATION_SUCCESSFUL",
	ADD_DICKER_DATA_INTERGRATION_FAILED = "ADD_DICKER_DATA_INTERGRATION_FAILED",

	DELETE_DICKER_DATA_INTEGRATION_REQUESTED = "DELETE_DICKER_DATA_INTEGRATION_REQUESTED",
	DELETE_DICKER_DATA_INTEGRATION_SUCCESSFUL = "DELETE_DICKER_DATA_INTEGRATION_SUCCESSFUL",
	DELETE_DICKER_DATA_INTEGRATION_FAILED = "DELETE_DICKER_DATA_INTEGRATION_FAILED",

	ADD_WESTCOAST_INTERGRATION_REQUESTED = "ADD_WESTCOAST_INTERGRATION_REQUESTED",
	ADD_WESTCOAST_INTERGRATION_SUCCESSFUL = "ADD_WESTCOAST_INTERGRATION_SUCCESSFUL",
	ADD_WESTCOAST_INTERGRATION_FAILED = "ADD_WESTCOAST_INTERGRATION_FAILED",

	DELETE_WESTCOAST_INTEGRATION_REQUESTED = "DELETE_WESTCOAST_INTEGRATION_REQUESTED",
	DELETE_WESTCOAST_INTEGRATION_SUCCESSFUL = "DELETE_WESTCOAST_INTEGRATION_SUCCESSFUL",
	DELETE_WESTCOAST_INTEGRATION_FAILED = "DELETE_WESTCOAST_INTEGRATION_FAILED",

	DELETE_REPAIRSHOPR_INTEGRATION_REQUESTED = "DELETE_REPAIRSHOPR_INTEGRATION_REQUESTED",
	DELETE_REPAIRSHOPR_INTEGRATION_SUCCESSFUL = "DELETE_REPAIRSHOPR_INTEGRATION_SUCCESSFUL",
	DELETE_REPAIRSHOPR_INTEGRATION_FAILED = "DELETE_REPAIRSHOPR_INTEGRATION_FAILED",
	ADD_COMPANY_REPAIRSHOPR_INTEGRATION_REQUESTED = "ADD_COMPANY_REPAIRSHOPR_INTEGRATION_REQUESTED",
	ADD_COMPANY_REPAIRSHOPR_INTEGRATION_SUCCESSFUL = "ADD_COMPANY_REPAIRSHOPR_INTEGRATION_SUCCESSFUL",
	ADD_COMPANY_REPAIRSHOPR_INTEGRATION_FAILED = "ADD_COMPANY_REPAIRSHOPR_INTEGRATION_FAILED",

	GET_COMPANY_HUBSPOT_INTEGRATION_METADATA_REQUESTED = "GET_COMPANY_HUBSPOT_INTEGRATION_METADATA_REQUESTED",
	GET_COMPANY_HUBSPOT_INTEGRATION_METADATA_SUCCESSFUL = "GET_COMPANY_HUBSPOT_INTEGRATION_METADATA_SUCCESSFUL",
	GET_COMPANY_HUBSPOT_INTEGRATION_METADATA_FAILED = "GET_COMPANY_HUBSPOT_INTEGRATION_METADATA_FAILED",
	ADD_COMPANY_HUBSPOT_INTEGRATION_REQUESTED = "ADD_COMPANY_HUBSPOT_INTEGRATION_REQUESTED",
	ADD_COMPANY_HUBSPOT_INTEGRATION_SUCCESSFUL = "ADD_COMPANY_HUBSPOT_INTEGRATION_SUCCESSFUL",
	ADD_COMPANY_HUBSPOT_INTEGRATION_FAILED = "ADD_COMPANY_HUBSPOT_INTEGRATION_FAILED",
	DELETE_COMPANY_HUBSPOT_INTEGRATION_REQUESTED = "DELETE_COMPANY_HUBSPOT_INTEGRATION_REQUESTED",
	DELETE_COMPANY_HUBSPOT_INTEGRATION_SUCCESSFUL = "DELETE_COMPANY_HUBSPOT_INTEGRATION_SUCCESSFUL",
	DELETE_COMPANY_HUBSPOT_INTEGRATION_FAILED = "DELETE_COMPANY_HUBSPOT_INTEGRATION_FAILED",
	//Stripe
	ADD_STRIPE_CONNECTION_REQUESTED = "ADD_STRIPE_CONNECTION_REQUESTED",
	ADD_STRIPE_CONNECTION_SUCCESSFUL = "ADD_STRIPE_CONNECTION_SUCCESSFUL",
	ADD_STRIPE_CONNECTION_FAILED = "ADD_STRIPE_CONNECTION_FAILED",
	DELETE_STRIPE_CONNECTION_REQUESTED = "DELETE_STRIPE_CONNECTION_REQUESTED",
	DELETE_STRIPE_CONNECTION_SUCCESSFUL = "DELETE_STRIPE_CONNECTION_SUCCESSFUL",
	DELETE_STRIPE_CONNECTION_FAILED = "DELETE_STRIPE_CONNECTION_FAILED",

	DELETE_PAYMENTS_CONNECTION_SUCCESSFUL = "DELETE_PAYMENTS_CONNECTION_SUCCESSFUL",

	GET_QUICKBOOKS_ONLINE_INTEGRATION_METADATA_REQUESTED = "GET_QUICKBOOKS_ONLINE_INTEGRATION_METADATA_REQUESTED",
	GET_QUICKBOOKS_ONLINE_INTEGRATION_METADATA_SUCCESSFUL = "GET_QUICKBOOKS_ONLINE_INTEGRATION_METADATA_SUCCESSFUL",
	GET_QUICKBOOKS_ONLINE_INTEGRATION_METADATA_FAILED = "GET_QUICKBOOKS_ONLINE_INTEGRATION_METADATA_FAILED",
	ADD_QUICKBOOKS_ONLINE_INTEGRATION_REQUESTED = "ADD_QUICKBOOKS_ONLINE_INTEGRATION_REQUESTED",
	ADD_QUICKBOOKS_ONLINE_INTEGRATION_SUCCESSFUL = "ADD_QUICKBOOKS_ONLINE_INTEGRATION_SUCCESSFUL",
	ADD_QUICKBOOKS_ONLINE_INTEGRATION_FAILED = "ADD_QUICKBOOKS_ONLINE_INTEGRATION_FAILED",
	DELETE_QUICKBOOKS_ONLINE_INTEGRATION_REQUESTED = "DELETE_QUICKBOOKS_ONLINE_INTEGRATION_REQUESTED",
	DELETE_QUICKBOOKS_ONLINE_INTEGRATION_SUCCESSFUL = "DELETE_QUICKBOOKS_ONLINE_INTEGRATION_SUCCESSFUL",
	DELETE_QUICKBOOKS_ONLINE_INTEGRATION_FAILED = "DELETE_QUICKBOOKS_ONLINE_INTEGRATION_FAILED",

	// HaloPSA
	TEST_HALOPSA_CREDENTIALS_REQUESTED = "TEST_HALOPSA_CREDENTIALS_REQUESTED",
	TEST_HALOPSA_CREDENTIALS_SUCCESSFUL = "TEST_HALOPSA_CREDENTIALS_SUCCESSFUL",
	TEST_HALOPSA_CREDENTIALS_FAILED = "TEST_HALOPSA_CREDENTIALS_FAILED",
	FETCH_HALOPSA_METADATA_REQUESTED = "FETCH_HALOPSA_METADATA_REQUESTED",
	FETCH_HALOPSA_METADATA_SUCCESSFUL = "FETCH_HALOPSA_METADATA_SUCCESSFUL",
	FETCH_HALOPSA_METADATA_FAILED = "FETCH_HALOPSA_METADATA_FAILED",
	IMPORT_HALO_TAX_INFO_REQUESTED = "IMPORT_HALO_TAX_INFO_REQUESTED",
	IMPORT_HALO_TAX_INFO_SUCCESSFUL = "IMPORT_HALO_TAX_INFO_SUCCESSFUL",
	IMPORT_HALO_TAX_INFO_FAILED = "IMPORT_HALO_TAX_INFO_FAILED",
	ADD_HALO_INTEGRATION_REQUESTED = "ADD_HALO_INTEGRATION_REQUESTED",
	ADD_HALO_INTEGRATION_SUCCESSFUL = "ADD_HALO_INTEGRATION_SUCCESSFUL",
	ADD_HALO_INTEGRATION_FAILED = "ADD_HALO_INTEGRATION_FAILED",
	DELETE_HALO_INTEGRATION_REQUESTED = "DELETE_HALO_INTEGRATION_REQUESTED",
	DELETE_HALO_INTEGRATION_SUCCESSFUL = "DELETE_HALO_INTEGRATION_SUCCESSFUL",
	DELETE_HALO_INTEGRATION_FAILED = "DELETE_HALO_INTEGRATION_FAILED",
	FETCH_HALOPSA_AUTH_DETAILS_REQUESTED = "FETCH_HALOPSA_AUTH_DETAILS_REQUESTED",
	FETCH_HALOPSA_AUTH_DETAILS_FAILED = "FETCH_HALOPSA_AUTH_DETAILS_FAILED",
	FETCH_HALOPSA_AUTH_DETAILS_SUCCESSFUL = "FETCH_HALOPSA_AUTH_DETAILS_SUCCESSFUL",

	// Custom Distributor
	ADD_CUSTOM_DISTRIBUTOR_REQUESTED = "ADD_CUSTOM_DISTRIBUTOR_REQUESTED",
	ADD_CUSTOM_DISTRIBUTOR_SUCCESSFUL = "ADD_CUSTOM_DISTRIBUTOR_SUCCESSFUL",
	ADD_CUSTOM_DISTRIBUTOR_FAILED = "ADD_CUSTOM_DISTRIBUTOR_FAILED",

	DELETE_CUSTOM_DISTRIBUTOR_REQUESTED = "DELETE_CUSTOM_DISTRIBUTOR_REQUESTED",
	DELETE_CUSTOM_DISTRIBUTOR_SUCCESSFUL = "DELETE_CUSTOM_DISTRIBUTOR_SUCCESSFUL",
	DELETE_CUSTOM_DISTRIBUTOR_FAILED = "DELETE_CUSTOM_DISTRIBUTOR_FAILED",

	CONNECT_DATEK_DISTRIBUTOR_REQUESTED = "CONNECT_DATEK_DISTRIBUTOR_REQUESTED",
	CONNECT_DATEK_DISTRIBUTOR_SUCCESSFUL = "CONNECT_DATEK_DISTRIBUTOR_SUCCESSFUL",
	CONNECT_DATEK_DISTRIBUTOR_FAILED = "CONNECT_DATEK_DISTRIBUTOR_FAILED",

	// Favourite Integrations
	UPDATE_INITIAL_SYNC_STATUS = "UPDATE_INITIAL_SYNC_STATUS",

	UPDATE_INTEGRATIION_PAGE_DETAILS = "UPDATE_INTEGRATIION_PAGE_DETAILS",

	// AI Writer
	AI_WRITER_TRIAL_INFO_UPDATED = "AI_WRITER_TRIAL_INFO_UPDATED",

	// Amazon Distributor
	DELETE_AMAZON_DISTRIBUTOR_REQUESTED = "DELETE_AMAZON_DISTRIBUTOR_REQUESTED",
	DELETE_AMAZON_DISTRIBUTOR_SUCCESSFUL = "DELETE_AMAZON_DISTRIBUTOR_SUCCESSFUL",
	DELETE_AMAZON_DISTRIBUTOR_FAILED = "DELETE_AMAZON_DISTRIBUTOR_FAILED",

	GET_AMAZON_META_DATA_REQUESTED = "GET_AMAZON_META_DATA_REQUESTED",
	GET_AMAZON_META_DATA_SUCCESSFUL = "GET_AMAZON_META_DATA_SUCCESSFUL",
	GET_AMAZON_META_DATA_FAILED = "GET_AMAZON_META_DATA_FAILED",
	UPDATE_AMAZON_APPLIED_FILTER = "UPDATE_AMAZON_APPLIED_FILTER"
}

export type ShowUserLoaderAction = Action<UsersActionTypes.SHOW_USER_LOADER>;
export type HideUserLoaderAction = Action<UsersActionTypes.HIDE_USER_LOADER>;
export type ShowLibraryLoaderAction =
	Action<UsersActionTypes.SHOW_LIBRARY_LOADER>;
export type HideLibraryLoaderAction =
	Action<UsersActionTypes.HIDE_LIBRARY_LOADER>;
export type UserDataRequestedAction =
	Action<UsersActionTypes.USER_DATA_REQUESTED>;
export type ShowSubcriptionDetailsLoaderAction =
	Action<UsersActionTypes.SHOW_SUBSCRIPTION_DETAILS_LOADER>;
export type HideSubscriptionDetailsLoaderAction =
	Action<UsersActionTypes.HIDE_SUBSCRIPTION_DETAILS_LOADER>;

export interface UserDataSuccessfulAction
	extends Action<UsersActionTypes.USER_DATA_SUCCESSFUL> {
	readonly userData: UserData;
}

export type UserDataFailedAction = Action<UsersActionTypes.USER_DATA_FAILED>;

export interface UserDataUpdatedAction
	extends Action<UsersActionTypes.USER_DATA_UPDATED> {
	readonly userData: UserData;
}

export type EditUserDataRequestedAction =
	Action<UsersActionTypes.EDIT_USER_DATA_REQUESTED>;
export interface EditUserDataSuccessfulAction
	extends Action<UsersActionTypes.EDIT_USER_DATA_SUCCESSFUL> {
	readonly userData: UserData;
}
export type EditUserDataFailedAction =
	Action<UsersActionTypes.EDIT_USER_DATA_FAILED>;

export type SetTwoFactorAuthRequestedAction =
	Action<UsersActionTypes.SET_TWO_FACTOR_AUTH_REQUESTED>;

export interface SetTwoFactorAuthSuccessfulAction
	extends Action<UsersActionTypes.SET_TWO_FACTOR_AUTH_SUCCESSFUL> {
	readonly userCompany: UserCompany;
}

export type SetSsoMandatoryFailedAction =
	Action<UsersActionTypes.SET_SSO_MANDATORY_FAILED>;
export type SetSsoMandatoryRequestedAction =
	Action<UsersActionTypes.SET_SSO_MANDATORY_REQUESTED>;

export interface SetSsoMandatorySuccessfulAction
	extends Action<UsersActionTypes.SET_SSO_MANDATORY_SUCCESSFUL> {
	readonly userCompany: UserCompany;
}

export type SetSsoEnableFailedAction =
	Action<UsersActionTypes.SET_SSO_ENABLE_FAILED>;
export type SetSsoEnableRequestedAction =
	Action<UsersActionTypes.SET_SSO_ENABLE_REQUESTED>;

export interface SetSsoEnableSuccessfulAction
	extends Action<UsersActionTypes.SET_SSO_ENABLE_SUCCESSFUL> {
	readonly userCompany: UserCompany;
}

export type SetTwoFactorAuthFailedAction =
	Action<UsersActionTypes.SET_TWO_FACTOR_AUTH_FAILED>;
export type ConfigurationSettingsRequestedAction =
	Action<UsersActionTypes.CONFIGURATION_SETTINGS_REQUESTED>;

export interface ConfigurationSettingsSuccessfulAction
	extends Action<UsersActionTypes.CONFIGURATION_SETTINGS_SUCCESSFUL> {
	readonly configurationSettings: ConfigurationSettingsResponse;
}

export type ConfigurationSettingsFailedAction =
	Action<UsersActionTypes.CONFIGURATION_SETTINGS_FAILED>;

export type UserRequestJoinCompanyRequestedAction =
	Action<UsersActionTypes.USERREQUEST_JOINCOMPANY_REQUESTED>;
export interface UserRequestJoinCompanySuccessfulAction
	extends Action<UsersActionTypes.USERREQUEST_JOINCOMPANY_SUCCESSFUL> {
	readonly configurationSettings: ConfigurationSettingsResponse;
}

export type UserRequestJoinCompanyFailedAction =
	Action<UsersActionTypes.USERREQUEST_JOINCOMPANY_FAILED>;

export type UserRequestWithdrawCompanyRequestedAction =
	Action<UsersActionTypes.USERREQUEST_WITHDRAWCOMPANY_REQUESTED>;
export interface UserRequestWithdrawCompanySuccessfulAction
	extends Action<UsersActionTypes.USERREQUEST_WITHDRAWCOMPANY_SUCCESSFUL> {
	readonly configurationSettings: ConfigurationSettingsResponse;
}

export type UserRequestWithdrawCompanyFailedAction =
	Action<UsersActionTypes.USERREQUEST_WITHDRAWCOMPANY_FAILED>;

export type UserCompanyConfigUpdateRequestedAction =
	Action<UsersActionTypes.USER_COMPANY_CONFIG_UPDATE_REQUESTED>;
export type UserCompanyConfigUpdateFailedAction =
	Action<UsersActionTypes.USER_COMPANY_CONFIG_UPDATE_FAILED>;

export interface UserCompanyConfigUpdateSuccessfulAction
	extends Action<UsersActionTypes.USER_COMPANY_CONFIG_UPDATE_SUCCESSFUL> {
	readonly configurationSettings: ConfigurationSettingsResponse;
}

export interface CompanyDataUpdatedAction
	extends Action<UsersActionTypes.COMPANY_DATA_UPDATED> {
	readonly userCompany: UserCompany;
}

export type UserCompanyRequestedAction =
	Action<UsersActionTypes.USER_COMPANY_REQUESTED>;

export interface UserCompanySuccessfulAction
	extends Action<UsersActionTypes.USER_COMPANY_SUCCESSFUL> {
	readonly userCompany: UserCompany;
}

export type UserCompanyFailedAction =
	Action<UsersActionTypes.USER_COMPANY_FAILED>;

export type GetDatePatternsRequestedAction =
	Action<UsersActionTypes.GET_DATE_PATTERNS_REQUESTED>;

export interface GetDatePatternsSuccessfulAction
	extends Action<UsersActionTypes.GET_DATE_PATTERNS_SUCCESSFUL> {
	readonly datePatterns: DatePattern[];
}

export type GetDatePatternsFailedAction =
	Action<UsersActionTypes.GET_DATE_PATTERNS_FAILED>;

export type AddEmailAliasRequestedAction =
	Action<UsersActionTypes.ADD_EMAIL_ALIAS_REQUESTED>;

export interface AddEmailAliasSuccessfulAction
	extends Action<UsersActionTypes.ADD_EMAIL_ALIAS_SUCCESSFUL> {
	readonly addEmailAliasResponse: EmailAliasItem;
}

export type DeleteEmailAliasFailedAction =
	Action<UsersActionTypes.DELETE_EMAIL_ALIAS_FAILED>;
export type DeleteEmailAliasRequestedAction =
	Action<UsersActionTypes.DELETE_EMAIL_ALIAS_REQUESTED>;

export interface DeleteEmailAliasSuccessfulAction
	extends Action<UsersActionTypes.DELETE_EMAIL_ALIAS_SUCCESSFUL> {
	readonly deleteEmailAliasResponse: EmailAliasItem;
}

export type AddEmailAliasFailedAction =
	Action<UsersActionTypes.ADD_EMAIL_ALIAS_FAILED>;
export type EditUserCompanyRequestedAction =
	Action<UsersActionTypes.EDIT_USER_COMPANY_REQUESTED>;

export interface EditUserCompanySuccessfulAction
	extends Action<UsersActionTypes.EDIT_USER_COMPANY_SUCCESSFUL> {
	readonly editUserCompany: UserCompany;
}

export type EditUserCompanyFailedAction =
	Action<UsersActionTypes.EDIT_USER_COMPANY_FAILED>;
export type EditUserPasswordRequestedAction =
	Action<UsersActionTypes.EDIT_USER_PASSWORD_REQUESTED>;
export type EditUserPasswordSuccessfulAction =
	Action<UsersActionTypes.EDIT_USER_PASSWORD_SUCCESSFUL>;
export type EditUserPasswordFailedAction =
	Action<UsersActionTypes.EDIT_USER_PASSWORD_FAILED>;
export type TaxCategoriesListRequestedAction =
	Action<UsersActionTypes.TAX_CATEGORIES_LIST_ACTION_REQUESTED>;

export interface TaxCategoriesListSuccessfulAction
	extends Action<UsersActionTypes.TAX_CATEGORIES_LIST_ACTION_SUCCESSFUL> {
	readonly taxCategoriesList: TaxCategory[];
}

export type TaxCategoriesListFailedAction =
	Action<UsersActionTypes.TAX_CATEGORIES_LIST_ACTION_FAILED>;
export type TaxRegionsListRequestedAction =
	Action<UsersActionTypes.TAX_REGIONS_LIST_ACTION_REQUESTED>;

export interface TaxRegionsListSuccessfulAction
	extends Action<UsersActionTypes.TAX_REGIONS_LIST_ACTION_SUCCESSFUL> {
	readonly taxRegionsList: TaxRegion[];
}

export type TaxRegionsListFailedAction =
	Action<UsersActionTypes.TAX_REGIONS_LIST_ACTION_FAILED>;
export type AddTaxRegionRequestedAction =
	Action<UsersActionTypes.ADD_TAX_REGION_ACTION_REQUESTED>;

export interface AddTaxRegionSuccessfulAction
	extends Action<UsersActionTypes.ADD_TAX_REGION_ACTION_SUCCESSFUL> {
	readonly addTaxRegion: TaxRegion;
}

export type AddTaxRegionFailedAction =
	Action<UsersActionTypes.ADD_TAX_REGION_ACTION_FAILED>;
export type EditTaxRegionRequestedAction =
	Action<UsersActionTypes.EDIT_TAX_REGION_ACTION_REQUESTED>;

export interface EditTaxRegionSuccessfulAction
	extends Action<UsersActionTypes.EDIT_TAX_REGION_ACTION_SUCCESSFUL> {
	readonly editTaxRegion: TaxRegion;
}

export type EditTaxRegionFailedAction =
	Action<UsersActionTypes.EDIT_TAX_REGION_ACTION_FAILED>;
export type AddTaxCategoryRequestedAction =
	Action<UsersActionTypes.ADD_TAX_CATEGORY_ACTION_REQUESTED>;

export interface AddTaxCategorySuccessfulAction
	extends Action<UsersActionTypes.ADD_TAX_CATEGORY_ACTION_SUCCESSFUL> {
	readonly addTaxCategory: TaxCategory;
}

export type AddTaxCategoryFailedAction =
	Action<UsersActionTypes.ADD_TAX_CATEGORY_ACTION_FAILED>;
export type EditTaxCategoryRequestedAction =
	Action<UsersActionTypes.EDIT_TAX_CATEGORY_ACTION_REQUESTED>;

export interface EditTaxCategorySuccessfulAction
	extends Action<UsersActionTypes.EDIT_TAX_CATEGORY_ACTION_SUCCESSFUL> {
	readonly editTaxCategory: TaxCategory;
}

export type EditTaxCategoryFailedAction =
	Action<UsersActionTypes.EDIT_TAX_CATEGORY_ACTION_FAILED>;
export type AddTaxRegionDrawerOpenedAction =
	Action<UsersActionTypes.ADD_TAX_REGION_DRAWER_OPENED>;
export type AddTaxRegionDrawerClosedAction =
	Action<UsersActionTypes.ADD_TAX_REGION_DRAWER_CLOSED>;
export type EditTaxRegionDrawerOpenedAction =
	Action<UsersActionTypes.EDIT_TAX_REGION_DRAWER_OPENED>;
export type EditTaxRegionDrawerClosedAction =
	Action<UsersActionTypes.EDIT_TAX_REGION_DRAWER_CLOSED>;
export type AddTaxCategoryDrawerOpenedAction =
	Action<UsersActionTypes.ADD_TAX_CATEGORY_DRAWER_OPENED>;
export type AddTaxCategoryDrawerClosedAction =
	Action<UsersActionTypes.ADD_TAX_CATEGORY_DRAWER_CLOSED>;
export type EditTaxCategoryDrawerOpenedAction =
	Action<UsersActionTypes.EDIT_TAX_CATEGORY_DRAWER_OPENED>;
export type EditTaxCategoryDrawerClosedAction =
	Action<UsersActionTypes.EDIT_TAX_CATEGORY_DRAWER_CLOSED>;
export type ChangeCompanyLogoRequestedAction =
	Action<UsersActionTypes.CHANGE_COMPANY_LOGO_REQUESTED>;

export interface ChangeCompanyLogoSuccessfulAction
	extends Action<UsersActionTypes.CHANGE_COMPANY_LOGO_SUCCESSFUL> {
	changeLogoUserCompany: UserCompany;
}

export type ChangeCompanyLogoFailedAction =
	Action<UsersActionTypes.CHANGE_COMPANY_LOGO_FAILED>;
export type CompanyUsersRequestedAction =
	Action<UsersActionTypes.COMPANY_USERS_REQUESTED>;

export interface CompanyUsersSuccessfulAction
	extends Action<UsersActionTypes.COMPANY_USERS_SUCCESSFUL> {
	readonly companyUserList: UserData[];
	readonly companyUserCount: CompanyUserCount;
}

export type CompanyUsersFailedAction =
	Action<UsersActionTypes.COMPANY_USERS_FAILED>;
export type DeleteCompanyUserRequestedAction =
	Action<UsersActionTypes.DELETE_COMPANY_USER_REQUESTED>;

export interface DeleteCompanyUserSuccessfulAction
	extends Action<UsersActionTypes.DELETE_COMPANY_USER_SUCCESSFUL> {
	readonly deleteCompanyUser: UserData;
}

export type DeleteCompanyUserFailedAction =
	Action<UsersActionTypes.DELETE_COMPANY_USER_FAILED>;
export type AddCompanyUserRequestedAction =
	Action<UsersActionTypes.ADD_COMPANY_USER_REQUESTED>;

export interface AddCompanyUserSuccessfulAction
	extends Action<UsersActionTypes.ADD_COMPANY_USER_SUCCESSFUL> {
	readonly addCompanyUser: UserData;
}

export type AddCompanyUserFailedAction =
	Action<UsersActionTypes.ADD_COMPANY_USER_FAILED>;
export type EditCompanyUserRequestedAction =
	Action<UsersActionTypes.EDIT_COMPANY_USER_REQUESTED>;

export interface EditCompanyUserSuccessfulAction
	extends Action<UsersActionTypes.EDIT_COMPANY_USER_SUCCESSFUL> {
	readonly editCompanyUser: UserData;
}

export type EditCompanyUserFailedAction =
	Action<UsersActionTypes.EDIT_COMPANY_USER_FAILED>;

export interface UpdateCompanyUserAction
	extends Action<UsersActionTypes.UPDATE_COMPANY_USER> {
	readonly updateCompanyUser: UserData;
}

export type EditCurrentCompanyUserRequestedAction =
	Action<UsersActionTypes.EDIT_CURRENT_COMPANY_USER_REQUESTED>;

export interface EditCurrentCompanyUserSuccessfulAction
	extends Action<UsersActionTypes.EDIT_CURRENT_COMPANY_USER_SUCCESSFUL> {
	readonly editCompanyUser: UserData;
}

export type EditCurrentCompanyUserFailedAction =
	Action<UsersActionTypes.EDIT_CURRENT_COMPANY_USER_FAILED>;

export type DeleteCompanyEmailIntegrationRequestedAction =
	Action<UsersActionTypes.DELETE_COMPANY_EMAIL_INTEGRATION_REQUESTED>;
export type DeleteCompanyEmailIntegrationSuccessfulAction =
	Action<UsersActionTypes.DELETE_COMPANY_EMAIL_INTEGRATION_SUCCESSFUL>;
export type DeleteCompanyEmailIntegrationFailedAction =
	Action<UsersActionTypes.DELETE_COMPANY_EMAIL_INTEGRATION_FAILED>;
export type AddCompanyEmailIntegrationRequestedAction =
	Action<UsersActionTypes.ADD_COMPANY_EMAIL_INTEGRATION_REQUESTED>;

export interface AddCompanyEmailIntegrationSuccessfulAction
	extends Action<UsersActionTypes.ADD_COMPANY_EMAIL_INTEGRATION_SUCCESSFUL> {
	readonly integrations: Integrations;
}

export type AddCompanyEmailIntegrationFailedAction =
	Action<UsersActionTypes.ADD_COMPANY_EMAIL_INTEGRATION_FAILED>;
export type DeleteCompanyConnectWiseIntegrationRequestedAction =
	Action<UsersActionTypes.DELETE_COMPANY_CONNECTWISE_INTEGRATION_REQUESTED>;

export interface DeleteCompanyConnectWiseIntegrationSuccessfulAction
	extends Action<UsersActionTypes.DELETE_COMPANY_CONNECTWISE_INTEGRATION_SUCCESSFUL> {
	readonly integrations: Integrations;
}

export type DeleteCompanyConnectWiseIntegrationFailedAction =
	Action<UsersActionTypes.DELETE_COMPANY_CONNECTWISE_INTEGRATION_FAILED>;
export type AddCompanyConnectWiseIntegrationRequestedAction =
	Action<UsersActionTypes.ADD_COMPANY_CONNECTWISE_INTEGRATION_REQUESTED>;

export interface AddCompanyConnectWiseIntegrationSuccessfulAction
	extends Action<UsersActionTypes.ADD_COMPANY_CONNECTWISE_INTEGRATION_SUCCESSFUL> {
	readonly integrations: Integrations;
}

export type AddCompanyConnectWiseIntegrationFailedAction =
	Action<UsersActionTypes.ADD_COMPANY_CONNECTWISE_INTEGRATION_FAILED>;
export type DeleteCompanyAutotaskIntegrationRequestedAction =
	Action<UsersActionTypes.DELETE_COMPANY_AUTOTASK_INTEGRATION_REQUESTED>;

export interface DeleteCompanyAutotaskIntegrationSuccessfulAction
	extends Action<UsersActionTypes.DELETE_COMPANY_AUTOTASK_INTEGRATION_SUCCESSFUL> {
	readonly integrations: Integrations;
}

export type DeleteCompanyAutotaskIntegrationFailedAction =
	Action<UsersActionTypes.DELETE_COMPANY_AUTOTASK_INTEGRATION_FAILED>;
export type AddCompanyAutotaskIntegrationRequestedAction =
	Action<UsersActionTypes.ADD_COMPANY_AUTOTASK_INTEGRATION_REQUESTED>;

export interface AddCompanyAutotaskIntegrationSuccessfulAction
	extends Action<UsersActionTypes.ADD_COMPANY_AUTOTASK_INTEGRATION_SUCCESSFUL> {
	readonly integrations: Integrations;
}

export type AddCompanyAutotaskIntegrationFailedAction =
	Action<UsersActionTypes.ADD_COMPANY_AUTOTASK_INTEGRATION_FAILED>;
export type DeleteCompanyZapierIntegrationRequestedAction =
	Action<UsersActionTypes.DELETE_COMPANY_ZAPIER_INTEGRATION_REQUESTED>;

export interface DeleteCompanyZapierIntegrationSuccessfulAction
	extends Action<UsersActionTypes.DELETE_COMPANY_ZAPIER_INTEGRATION_SUCCESSFUL> {
	readonly integrations: Integrations;
}

export type DeleteCompanyZapierIntegrationFailedAction =
	Action<UsersActionTypes.DELETE_COMPANY_ZAPIER_INTEGRATION_FAILED>;

export type AddCompanyKaseyaIntegrationRequestedAction =
	Action<UsersActionTypes.ADD_COMPANY_KASEYA_INTEGRATION_REQUESTED>;

export interface AddCompanyKaseyaIntegrationSuccessfulAction
	extends Action<UsersActionTypes.ADD_COMPANY_KASEYA_INTEGRATION_SUCCESSFUL> {
	readonly integrations: Integrations;
}

export type AddCompanyKaseyaIntegrationFailedAction =
	Action<UsersActionTypes.ADD_COMPANY_KASEYA_INTEGRATION_FAILED>;
export type DeleteCompanyKaseyaIntegrationRequestedAction =
	Action<UsersActionTypes.DELETE_COMPANY_KASEYA_INTEGRATION_REQUESTED>;

export interface DeleteCompanyKaseyaIntegrationSuccessfulAction
	extends Action<UsersActionTypes.DELETE_COMPANY_KASEYA_INTEGRATION_SUCCESSFUL> {
	readonly integrations: Integrations;
}

export type DeleteCompanyKaseyaIntegrationFailedAction =
	Action<UsersActionTypes.DELETE_COMPANY_KASEYA_INTEGRATION_FAILED>;

export type AddCompanyIngramIntegrationRequestedAction =
	Action<UsersActionTypes.ADD_COMPANY_INGRAM_INTEGRATION_REQUESTED>;

export interface AddCompanyIngramIntegrationSuccessfulAction
	extends Action<UsersActionTypes.ADD_COMPANY_INGRAM_INTEGRATION_SUCCESSFUL> {
	readonly integrations: Integrations;
}

export type AddCompanyIngramIntegrationFailedAction =
	Action<UsersActionTypes.ADD_COMPANY_INGRAM_INTEGRATION_FAILED>;
export type DeleteCompanyIngramIntegrationRequestedAction =
	Action<UsersActionTypes.DELETE_COMPANY_INGRAM_INTEGRATION_REQUESTED>;

export interface DeleteCompanyIngramIntegrationSuccessfulAction
	extends Action<UsersActionTypes.DELETE_COMPANY_INGRAM_INTEGRATION_SUCCESSFUL> {
	readonly integrations: Integrations;
}

export type DeleteCompanyIngramIntegrationFailedAction =
	Action<UsersActionTypes.DELETE_COMPANY_INGRAM_INTEGRATION_FAILED>;

export type AddCompanyTechDataIntegrationRequestedAction =
	Action<UsersActionTypes.ADD_COMPANY_TECH_DATA_INTEGRATION_REQUESTED>;

export interface AddCompanyTechDataIntegrationSuccessfulAction
	extends Action<UsersActionTypes.ADD_COMPANY_TECH_DATA_INTEGRATION_SUCCESSFUL> {
	readonly integrations: Integrations;
}

export type AddCompanyTechDataIntegrationFailedAction =
	Action<UsersActionTypes.ADD_COMPANY_TECH_DATA_INTEGRATION_FAILED>;
export type DeleteCompanyTechDataIntegrationRequestedAction =
	Action<UsersActionTypes.DELETE_COMPANY_TECH_DATA_INTEGRATION_REQUESTED>;

export interface DeleteCompanyTechDataIntegrationSuccessfulAction
	extends Action<UsersActionTypes.DELETE_COMPANY_TECH_DATA_INTEGRATION_SUCCESSFUL> {
	readonly integrations: Integrations;
}

export type DeleteCompanyTechDataIntegrationFailedAction =
	Action<UsersActionTypes.DELETE_COMPANY_TECH_DATA_INTEGRATION_FAILED>;

export type AddDAndHIntegrationRequestedAction =
	Action<UsersActionTypes.ADD_D_AND_H_INTEGRATION_REQUESTED>;

export interface AddDAndHIntegrationSuccessfulAction
	extends Action<UsersActionTypes.ADD_D_AND_H_INTEGRATION_SUCCESSFUL> {
	readonly integrations: Integrations;
}

export type AddDAndHIntegrationFailedAction =
	Action<UsersActionTypes.ADD_D_AND_H_INTEGRATION_FAILED>;
export type DeleteDAndHIntegrationRequestedAction =
	Action<UsersActionTypes.DELETE_D_AND_H_INTEGRATION_REQUESTED>;

export interface DeleteDAndHIntegrationSuccessfulAction
	extends Action<UsersActionTypes.DELETE_D_AND_H_INTEGRATION_SUCCESSFUL> {
	readonly integrations: Integrations;
}

export type DeleteDAndHIntegrationFailedAction =
	Action<UsersActionTypes.DELETE_D_AND_H_INTEGRATION_FAILED>;

export type AddSynnexIntegrationRequestedAction =
	Action<UsersActionTypes.ADD_SYNNEX_INTEGRATION_REQUESTED>;

export interface AddSynnexIntegrationSuccessfulAction
	extends Action<UsersActionTypes.ADD_SYNNEX_INTEGRATION_SUCCESSFUL> {
	readonly integrations: Integrations;
}

export type AddSynnexIntegrationFailedAction =
	Action<UsersActionTypes.ADD_SYNNEX_INTEGRATION_FAILED>;
export type DeleteSynnexIntegrationRequestedAction =
	Action<UsersActionTypes.DELETE_SYNNEX_INTEGRATION_REQUESTED>;

export interface DeleteSynnexIntegrationSuccessfulAction
	extends Action<UsersActionTypes.DELETE_SYNNEX_INTEGRATION_SUCCESSFUL> {
	readonly integrations: Integrations;
}

export type DeleteSynnexIntegrationFailedAction =
	Action<UsersActionTypes.DELETE_SYNNEX_INTEGRATION_FAILED>;

export type AddCBIntegrationRequestedAction =
	Action<UsersActionTypes.ADD_CB_INTEGRATION_REQUESTED>;

export interface AddCBIntegrationSuccessfulAction
	extends Action<UsersActionTypes.ADD_CB_INTEGRATION_SUCCESSFUL> {
	readonly integrations: Integrations;
}

export type AddCBIntegrationFailedAction =
	Action<UsersActionTypes.ADD_CB_INTEGRATION_FAILED>;
export type DeleteCBIntegrationRequestedAction =
	Action<UsersActionTypes.DELETE_CB_INTEGRATION_REQUESTED>;

export interface DeleteCBIntegrationSuccessfulAction
	extends Action<UsersActionTypes.DELETE_CB_INTEGRATION_SUCCESSFUL> {
	readonly integrations: Integrations;
}

export type DeleteCBIntegrationFailedAction =
	Action<UsersActionTypes.DELETE_CB_INTEGRATION_FAILED>;

export type FetchLibraryListRequestedAction =
	Action<UsersActionTypes.FETCH_LIBRARY_LIST_REQUESTED>;

export interface FetchLibraryListSuccessfulAction
	extends Action<UsersActionTypes.FETCH_LIBRARY_LIST_SUCCESSFUL> {
	readonly libraryList: LibraryItem[];
}

export type FetchLibraryListFailedAction =
	Action<UsersActionTypes.FETCH_LIBRARY_LIST_FAILED>;
export type SaveBlockToLibraryListRequestedAction =
	Action<UsersActionTypes.SAVE_BLOCK_TO_LIBRARY_LIST_REQUESTED>;
export type SaveBlockToLibraryListSuccessfulAction =
	Action<UsersActionTypes.SAVE_BLOCK_TO_LIBRARY_LIST_SUCCESSFUL>;
export type SaveBlockToLibraryListFailedAction =
	Action<UsersActionTypes.SAVE_BLOCK_TO_LIBRARY_LIST_FAILED>;

export type DeleteBlockToLibraryListRequestedAction =
	Action<UsersActionTypes.DELETE_BLOCK_TO_LIBRARY_LIST_REQUESTED>;
export type DeleteBlockToLibraryListSuccessfulAction =
	Action<UsersActionTypes.DELETE_BLOCK_TO_LIBRARY_LIST_SUCCESSFUL>;
export type DeleteBlockToLibraryListFailedAction =
	Action<UsersActionTypes.DELETE_BLOCK_TO_LIBRARY_LIST_FAILED>;

export interface UpdateLibraryItemTagsSuccessfulAction
	extends Action<UsersActionTypes.UPDATE_LIBRARY_ITEM_TAGS_SUCCESSFUL> {
	readonly libraryItemId: string;
	readonly tagIds: string[];
}

export type ConfirmCardAddedRequestedAction =
	Action<UsersActionTypes.CONFIRM_CARD_ADDED_REQUESTED>;
export type ConfirmCardAddedFailedAction =
	Action<UsersActionTypes.CONFIRM_CARD_ADDED_FAILED>;

export interface ConfirmCardAddedSuccessfulAction
	extends Action<UsersActionTypes.CONFIRM_CARD_ADDED_SUCCESSFUL> {
	readonly billingInfo: BillingInfo;
}

export type ChargebeePortalSessionRequestedAction =
	Action<UsersActionTypes.CHARGEBEE_PORTAL_SESSION_REQUESTED>;

export interface ChargebeePortalSessionSuccessfulAction
	extends Action<UsersActionTypes.CHARGEBEE_PORTAL_SESSION_SUCCESSFUL> {
	readonly portalSession: ChargebeePortalSession;
}

export type ChargebeePortalSessionFailedAction =
	Action<UsersActionTypes.CHARGEBEE_PORTAL_SESSION_FAILED>;

export type BillingInfoRequestedAction =
	Action<UsersActionTypes.BILLING_INFO_REQUESTED>;
export interface BillingInfoFailedAction
	extends Action<UsersActionTypes.BILLING_INFO_FAILED> {
	readonly error: unknown;
}

export interface BillingInfoSuccessfulAction
	extends Action<UsersActionTypes.BILLING_INFO_SUCCESSFUL> {
	readonly billingInfo: BillingInfo;
}

export type SubscriptionPlansRequestedAction =
	Action<UsersActionTypes.SUBSCRIPTION_PLANS_REQUESTED>;
export interface SubscriptionPlansFailedAction
	extends Action<UsersActionTypes.SUBSCRIPTION_PLANS_FAILED> {
	readonly error: unknown;
}

export interface SubscriptionPlansSuccessfulAction
	extends Action<UsersActionTypes.SUBSCRIPTION_PLANS_SUCCESSFUL> {
	readonly subscriptions: PricingPlan[];
}

export type SubscriptionDetailsRequestedAction =
	Action<UsersActionTypes.SUBSCRIPTION_DETAILS_REQUESTED>;
export interface SubscriptionDetailsFailedAction
	extends Action<UsersActionTypes.SUBSCRIPTION_DETAILS_FAILED> {
	readonly error: unknown;
}

export interface SubscriptionDetailsSuccessfulAction
	extends Action<UsersActionTypes.SUBSCRIPTION_DETAILS_SUCCESSFUL> {
	readonly subscriptionDetails: PricingPlanSummary;
}

export type SubscriptionPlanAddUserRequestedAction =
	Action<UsersActionTypes.SUBSCRIPTION_PLAN_ADD_USER_REQUESTED>;
export type SubscriptionPlanAddUserFailedAction =
	Action<UsersActionTypes.SUBSCRIPTION_PLAN_ADD_USER_FAILED>;

export interface SubscriptionPlanAddUserSuccessfulAction
	extends Action<UsersActionTypes.SUBSCRIPTION_PLAN_ADD_USER_SUCCESSFUL> {
	readonly subscriptionDetails: PricingPlanSummary;
}

export type SubscriptionPlanRemoveUserRequestedAction =
	Action<UsersActionTypes.SUBSCRIPTION_PLAN_REMOVE_USER_REQUESTED>;
export type SubscriptionPlanRemoveUserFailedAction =
	Action<UsersActionTypes.SUBSCRIPTION_PLAN_REMOVE_USER_FAILED>;

export interface SubscriptionPlanRemoveUserSuccessfulAction
	extends Action<UsersActionTypes.SUBSCRIPTION_PLAN_REMOVE_USER_SUCCESSFUL> {
	readonly subscriptionDetails: PricingPlanSummary;
}

export type SubscriptionPlanUpgradeDowngradeRequestedAction =
	Action<UsersActionTypes.SUBSCRIPTION_PLAN_UPGRADE_DOWNGRADE_REQUESTED>;
export type SubscriptionPlanUpgradeDowngradeFailedAction =
	Action<UsersActionTypes.SUBSCRIPTION_PLAN_UPGRADE_DOWNGRADE_FAILED>;

export interface SubscriptionPlanUpgradeDowngradeSuccessfulAction
	extends Action<UsersActionTypes.SUBSCRIPTION_PLAN_UPGRADE_DOWNGRADE_SUCCESSFUL> {
	readonly subscriptionDetails: PricingPlanSummary;
}

export type SubscriptionPlanEstimateRequestedAction =
	Action<UsersActionTypes.SUBSCRIPTION_PLAN_ESTIMATE_REQUESTED>;
export type SubscriptionPlanEstimateFailedAction =
	Action<UsersActionTypes.SUBSCRIPTION_PLAN_ESTIMATE_FAILED>;

export interface SubscriptionPlanEstimateSuccessfulAction
	extends Action<UsersActionTypes.SUBSCRIPTION_PLAN_ESTIMATE_SUCCESSFUL> {
	readonly subscriptionEstimate: PricingPlanEstimateData;
}

export type ShowUpdateCardModalAction =
	Action<UsersActionTypes.SHOW_UPDATE_CARD_MODAL>;
export type HideUpdateCardModalAction =
	Action<UsersActionTypes.HIDE_UPDATE_CARD_MODAL>;
export type ShowBillingInfoLoaderAction =
	Action<UsersActionTypes.SHOW_BILLING_INFO_LOADER>;
export type HideBillingInfoLoaderAction =
	Action<UsersActionTypes.HIDE_BILLING_INFO_LOADER>;

export type EditQuoteDefaultLabelCompanyRequestedAction =
	Action<UsersActionTypes.EDIT_QUOTE_DEFAULT_LABEL_COMPANY_REQUESTED>;
export type EditQuoteDefaultLabelCompanyFailedAction =
	Action<UsersActionTypes.EDIT_QUOTE_DEFAULT_LABEL_COMPANY_FAILED>;

export interface EditQuoteDefaultLabelCompanySuccessfulAction
	extends Action<UsersActionTypes.EDIT_QUOTE_DEFAULT_LABEL_COMPANY_SUCCESSFUL> {
	readonly quoteLabels?: QuoteLabels;
}

export type DisableUserRequestedAction =
	Action<UsersActionTypes.DISABLE_USER_REQUESTED>;

export interface DisableUserSuccessfulAction
	extends Action<UsersActionTypes.DISABLE_USER_SUCCESSFUL> {
	readonly userData: UserData;
}

export type DisableUserFailedAction =
	Action<UsersActionTypes.DISABLE_USER_FAILED>;

export type EnableUserRequestedAction =
	Action<UsersActionTypes.ENABLE_USER_REQUESTED>;

export interface EnableUserSuccessfulAction
	extends Action<UsersActionTypes.ENABLE_USER_SUCCESSFUL> {
	readonly userData: UserData;
}

export type EnableUserFailedAction =
	Action<UsersActionTypes.ENABLE_USER_FAILED>;

export type FetchUserCalendarRequestedAction =
	Action<UsersActionTypes.FETCH_USER_CALENDARS_REQUESTED>;

export interface FetchUserCalendarSuccessfulAction
	extends Action<UsersActionTypes.FETCH_USER_CALENDARS_SUCCESSFUL> {
	readonly userCalendars: UserCalendar[];
}

export type FetchUserCalendarFailedAction =
	Action<UsersActionTypes.FETCH_USER_CALENDARS_FAILED>;

export type ResetUserCalendarsAction =
	Action<UsersActionTypes.RESET_USER_CALENDARS>;

export type ConnectCalendarRequestedAction =
	Action<UsersActionTypes.CONNECT_CALENDAR_REQUESTED>;
export type ConnectCalendarSuccessfulAction =
	Action<UsersActionTypes.CONNECT_CALENDAR_SUCCESSFUL>;
export type ConnectCalendarFailedAction =
	Action<UsersActionTypes.CONNECT_CALENDAR_FAILED>;

export type FetchCompanyIntegrationsRequestedAction =
	Action<UsersActionTypes.FETCH_COMPANY_INTEGRATIONS_REQUESTED>;

export interface FetchCompanyIntegrationsSuccessfulAction
	extends Action<UsersActionTypes.FETCH_COMPANY_INTEGRATIONS_SUCCESSFUL> {
	readonly integrations: Integrations;
}

export type FetchCompanyIntegrationsFailedAction =
	Action<UsersActionTypes.FETCH_COMPANY_INTEGRATIONS_FAILED>;

export type ChargebeeInvoiceListRequestedAction =
	Action<UsersActionTypes.CHARGEBEE_INVOICES_LIST_REQUESTED>;

export interface ChargebeeInvoiceListSuccessfulAction
	extends Action<UsersActionTypes.CHARGEBEE_INVOICES_LIST_SUCCESSFUL> {
	readonly chargebeeInvoicesList: ChargebeeInvoiceResponse;
}

export type ChargebeeInvoiceListFailedAction =
	Action<UsersActionTypes.CHARGEBEE_INVOICES_LIST_FAILED>;
export type ChargebeeInvoiceDetailsRequestedAction =
	Action<UsersActionTypes.CHARGEBEE_INVOICE_DETAILS_REQUESTED>;

export interface ChargebeeInvoiceDetailsSuccessfulAction
	extends Action<UsersActionTypes.CHARGEBEE_INVOICE_DETAILS_SUCCESSFUL> {
	readonly chargebeeInvoice: ChargebeeInvoice;
}

export type ChargebeeInvoiceDetailsFailedAction =
	Action<UsersActionTypes.CHARGEBEE_INVOICE_DETAILS_FAILED>;

export type ChargebeeCreditNotesListRequestedAction =
	Action<UsersActionTypes.CHARGEBEE_CREDIT_NOTES_LIST_REQUESTED>;

export interface ChargebeeCreditNotesListSuccessfulAction
	extends Action<UsersActionTypes.CHARGEBEE_CREDIT_NOTES_LIST_SUCCESSFUL> {
	readonly chargebeeCreditNotesList: ChargebeeCreditNoteResponse;
}

export type ChargebeeCreditNotesListFailedAction =
	Action<UsersActionTypes.CHARGEBEE_CREDIT_NOTES_LIST_FAILED>;

export type ChargebeeCreditNoteDetailsRequestedAction =
	Action<UsersActionTypes.CHARGEBEE_CREDIT_NOTE_DETAILS_REQUESTED>;

export interface ChargebeeCreditNoteDetailsSuccessfulAction
	extends Action<UsersActionTypes.CHARGEBEE_CREDIT_NOTE_DETAILS_SUCCESSFUL> {
	readonly chargebeeCreditNote: ChargebeeCreditNoteDetail;
}

export type ChargebeeCreditNoteDetailsFailedAction =
	Action<UsersActionTypes.CHARGEBEE_CREDIT_NOTE_DETAILS_FAILED>;

export type PauseChargebeeSubscriptionRequestedAction =
	Action<UsersActionTypes.PAUSE_CHARGEBEE_SUBSCRIPTION_REQUESTED>;
export type PauseChargebeeSubscriptionSuccessfulAction =
	Action<UsersActionTypes.PAUSE_CHARGEBEE_SUBSCRIPTION_SUCCESSFUL>;
export type PauseChargebeeSubscriptionFailedAction =
	Action<UsersActionTypes.PAUSE_CHARGEBEE_SUBSCRIPTION_FAILED>;
export type VerifyUserEmail = Action<UsersActionTypes.VERIFY_USER_EMAIL>;
export type DeleteCustomEmail = Action<UsersActionTypes.DELETE_CUSTOM_EMAIL>;

export interface AddCustomDomain
	extends Action<UsersActionTypes.ADD_CUSTOM_DOMAIN> {
	readonly customDomainItem: CustomDomainItem;
}

export type DeleteCustomDomain = Action<UsersActionTypes.DELETE_CUSTOM_DOMAIN>;
export type AddCompanySyncroIntegrationRequestedAction =
	Action<UsersActionTypes.ADD_COMPANY_SYNCRO_INTEGRATION_REQUESTED>;

export interface AddCompanySyncroIntegrationSuccessfulAction
	extends Action<UsersActionTypes.ADD_COMPANY_SYNCRO_INTEGRATION_SUCCESSFUL> {
	readonly integrations: Integrations;
}

export type AddCompanySyncroIntegrationFailedAction =
	Action<UsersActionTypes.ADD_COMPANY_SYNCRO_INTEGRATION_FAILED>;
export type DeleteCompanySyncroIntegrationRequestedAction =
	Action<UsersActionTypes.DELETE_COMPANY_SYNCRO_INTEGRATION_REQUESTED>;

export interface DeleteCompanySyncroIntegrationSuccessfulAction
	extends Action<UsersActionTypes.DELETE_COMPANY_SYNCRO_INTEGRATION_SUCCESSFUL> {
	readonly integrations: Integrations;
}

export type DeleteCompanySyncroIntegrationFailedAction =
	Action<UsersActionTypes.DELETE_COMPANY_SYNCRO_INTEGRATION_FAILED>;
export type AddInfusionSoftIntegrationRequestedAction =
	Action<UsersActionTypes.ADD_INFUSION_SOFT_INTEGRATION_REQUESTED>;

export interface AddInfusionSoftIntegrationSuccessfulAction
	extends Action<UsersActionTypes.ADD_INFUSION_SOFT_INTEGRATION_SUCCESFUL> {
	readonly integrations: Integrations;
}

export type AddInfusionSoftIntegrationFailedAction =
	Action<UsersActionTypes.ADD_INFUSION_SOFT_INTEGRATION_FAILED>;
export type DeleteInfusionSoftIntegrationRequestedAction =
	Action<UsersActionTypes.DELETE_INFUSION_SOFT_INTEGRATION_REQUESTED>;

export interface DeleteInfusionSoftIntegrationSuccessfulAction
	extends Action<UsersActionTypes.DELETE_INFUSION_SOFT_INTEGRATION_SUCCESFUL> {
	readonly integrations: Integrations;
}

export type DeleteInfusionSoftIntegrationFailedAction =
	Action<UsersActionTypes.DELETE_INFUSION_SOFT_INTEGRATION_FAILED>;

export type UpdateEmailPreferencesRequested =
	Action<UsersActionTypes.UPDATED_EMAIL_PREFERENCES_REQUESTED>;
export type UpdateEmailPreferencesSuccessful =
	Action<UsersActionTypes.UPDATED_EMAIL_PREFERENCES_SUCCESSFUL>;
export type UpdateEmailPreferencesFailed =
	Action<UsersActionTypes.UPDATED_EMAIL_PREFERENCES_FAILED>;

export type ResendDomainVerificationRequested =
	Action<UsersActionTypes.RESEND_DOMAIN_VERIFICATION_REQUESTED>;
export type ResendDomainVerificationSuccessful =
	Action<UsersActionTypes.RESEND_DOMAIN_VERIFICATION_SUCCESSFUL>;
export type ResendDomainVerificationFailed =
	Action<UsersActionTypes.RESEND_DOMAIN_VERIFICATION_FAILED>;

export type UpdateEmailSignaturesRequested =
	Action<UsersActionTypes.UPDATE_EMAIL_SIGNATURES_REQUESTED>;

export interface UpdateEmailSignaturesSuccessful
	extends Action<UsersActionTypes.UPDATE_EMAIL_SIGNATURES_SUCCESSFUL> {
	readonly emailSignature: UserEmailSignature;
}

export type UpdateEmailSignaturesFailed =
	Action<UsersActionTypes.UPDATE_EMAIL_SIGNATURES_FAILED>;

export type FetchAllEmailSignaturesRequested =
	Action<UsersActionTypes.FETCH_EMAIL_SIGNATURES_REQUESTED>;

export interface FetchAllEmailSignaturesSuccessful
	extends Action<UsersActionTypes.FETCH_EMAIL_SIGNATURES_SUCCESSFUL> {
	readonly emailSignature: UserEmailSignature;
}

export type FetchAllEmailSignaturesFailed =
	Action<UsersActionTypes.FETCH_EMAIL_SIGNATURES_FAILED>;

export type ResetUserStateAction = Action<UsersActionTypes.RESET_USER_STATE>;
export type OnboardingCallScheduledRequestedAction =
	Action<UsersActionTypes.ONBOARDING_CALL_SCHEDULED_REQUESTED>;

export interface OnboardingCallScheduledSuccessfulAction
	extends Action<UsersActionTypes.ONBOARDING_CALL_SCHEDULED_SUCCESSFUL> {
	readonly billingStatus: BillingStatusEnum;
}

export type OnboardingCallScheduledFailedAction =
	Action<UsersActionTypes.ONBOARDING_CALL_SCHEDULED_FAILED>;

export type AddDickerDataIntegrationRequestedAction =
	Action<UsersActionTypes.ADD_DICKER_DATA_INTERGRATION_REQUESTED>;

export interface AddDickerDataIntegrationSuccessfulAction
	extends Action<UsersActionTypes.ADD_DICKER_DATA_INTERGRATION_SUCCESSFUL> {
	readonly integrations: Integrations;
}

export type AddDickerDataIntegrationFailedAction =
	Action<UsersActionTypes.ADD_DICKER_DATA_INTERGRATION_FAILED>;

export type DeleteDickerDataIntegrationRequestedAction =
	Action<UsersActionTypes.DELETE_DICKER_DATA_INTEGRATION_REQUESTED>;

export interface DeleteDickerDataIntegrationSuccessfulAction
	extends Action<UsersActionTypes.DELETE_DICKER_DATA_INTEGRATION_SUCCESSFUL> {
	readonly integrations: Integrations;
}

export type DeleteDickerDataIntegrationFailedAction =
	Action<UsersActionTypes.DELETE_DICKER_DATA_INTEGRATION_FAILED>;

export type AddWestcoastIntegrationRequestedAction =
	Action<UsersActionTypes.ADD_WESTCOAST_INTERGRATION_REQUESTED>;

export interface AddWestcoastIntegrationSuccessfulAction
	extends Action<UsersActionTypes.ADD_WESTCOAST_INTERGRATION_SUCCESSFUL> {
	readonly integrations: Integrations;
}

export type AddWestcoastIntegrationFailedAction =
	Action<UsersActionTypes.ADD_WESTCOAST_INTERGRATION_FAILED>;

export type DeleteWestcoastIntegrationRequestedAction =
	Action<UsersActionTypes.DELETE_WESTCOAST_INTEGRATION_REQUESTED>;

export interface DeleteWestcoastIntegrationSuccessfulAction
	extends Action<UsersActionTypes.DELETE_WESTCOAST_INTEGRATION_SUCCESSFUL> {
	readonly integrations: Integrations;
}

export type DeleteWestcoastIntegrationFailedAction =
	Action<UsersActionTypes.DELETE_WESTCOAST_INTEGRATION_FAILED>;

export type DeleteCompanyRepairShoprIntegrationRequestedAction =
	Action<UsersActionTypes.DELETE_REPAIRSHOPR_INTEGRATION_REQUESTED>;

export interface DeleteCompanyRepairShoprIntegrationSuccessfulAction
	extends Action<UsersActionTypes.DELETE_REPAIRSHOPR_INTEGRATION_SUCCESSFUL> {
	readonly integrations: Integrations;
}

export type DeleteCompanyRepairShoprIntegrationFailedAction =
	Action<UsersActionTypes.DELETE_REPAIRSHOPR_INTEGRATION_FAILED>;
export type AddCompanyRepairShoprIntegrationRequestedAction =
	Action<UsersActionTypes.ADD_COMPANY_REPAIRSHOPR_INTEGRATION_REQUESTED>;

export interface AddCompanyRepairShoprIntegrationSuccessfulAction
	extends Action<UsersActionTypes.ADD_COMPANY_REPAIRSHOPR_INTEGRATION_SUCCESSFUL> {
	readonly integrations: Integrations;
}

export type AddCompanyRepairShoprIntegrationFailedAction =
	Action<UsersActionTypes.ADD_COMPANY_REPAIRSHOPR_INTEGRATION_FAILED>;
export type AddCompanyHubspotIntegrationRequestedAction =
	Action<UsersActionTypes.ADD_COMPANY_HUBSPOT_INTEGRATION_REQUESTED>;

export interface AddCompanyHubspotIntegrationSuccessfulAction
	extends Action<UsersActionTypes.ADD_COMPANY_HUBSPOT_INTEGRATION_SUCCESSFUL> {
	readonly integrations: Integrations;
}

export type AddCompanyHubspotIntegrationFailedAction =
	Action<UsersActionTypes.ADD_COMPANY_HUBSPOT_INTEGRATION_FAILED>;
export type DeleteCompanyHubspotIntegrationRequestedAction =
	Action<UsersActionTypes.DELETE_COMPANY_HUBSPOT_INTEGRATION_REQUESTED>;

export interface DeleteCompanyHubspotIntegrationSuccessfulAction
	extends Action<UsersActionTypes.DELETE_COMPANY_HUBSPOT_INTEGRATION_SUCCESSFUL> {
	readonly integrations: Integrations;
}

export type DeleteCompanyHubspotIntegrationFailedAction =
	Action<UsersActionTypes.DELETE_COMPANY_HUBSPOT_INTEGRATION_FAILED>;
export type GetCompanyHubspotIntegrationMetadataRequestedAction =
	Action<UsersActionTypes.GET_COMPANY_HUBSPOT_INTEGRATION_METADATA_REQUESTED>;

export interface GetCompanyHubspotIntegrationMetadataSuccessfulAction
	extends Action<UsersActionTypes.GET_COMPANY_HUBSPOT_INTEGRATION_METADATA_SUCCESSFUL> {
	readonly hubspotMetadata: HubspotMetadataResponse;
}

export type GetCompanyHubspotIntegrationMetadataFailedAction =
	Action<UsersActionTypes.GET_COMPANY_HUBSPOT_INTEGRATION_METADATA_FAILED>;

export type AddStripeConnectionRequestedAction =
	Action<UsersActionTypes.ADD_STRIPE_CONNECTION_REQUESTED>;
export type AddStripeConnectionSuccessfulAction =
	Action<UsersActionTypes.ADD_STRIPE_CONNECTION_SUCCESSFUL>;
export type AddStripeConnectionFailedAction =
	Action<UsersActionTypes.ADD_STRIPE_CONNECTION_FAILED>;

export type DeleteStripeConnectionRequestedAction =
	Action<UsersActionTypes.DELETE_STRIPE_CONNECTION_REQUESTED>;

export interface DeleteStripeConnectionSuccessfulAction
	extends Action<UsersActionTypes.DELETE_STRIPE_CONNECTION_SUCCESSFUL> {
	readonly company: UserCompany;
}

export type DeleteStripeConnectionFailedAction =
	Action<UsersActionTypes.DELETE_STRIPE_CONNECTION_FAILED>;
export interface DeletePaymentsConnectionSuccessfulAction
	extends Action<UsersActionTypes.DELETE_PAYMENTS_CONNECTION_SUCCESSFUL> {
	readonly company: UserCompany;
}
export type GetQuickbooksIntegrationMetadataRequestedAction =
	Action<UsersActionTypes.GET_QUICKBOOKS_ONLINE_INTEGRATION_METADATA_REQUESTED>;

export interface GetQuickbooksIntegrationMetadataSuccessfulAction
	extends Action<UsersActionTypes.GET_QUICKBOOKS_ONLINE_INTEGRATION_METADATA_SUCCESSFUL> {
	readonly quickbooksMetadata: QuickbooksMetadataResponse;
}

export type GetQuickbooksIntegrationMetadataFailedAction =
	Action<UsersActionTypes.GET_QUICKBOOKS_ONLINE_INTEGRATION_METADATA_FAILED>;

export type AddQuickbooksIntegrationRequestedAction =
	Action<UsersActionTypes.ADD_QUICKBOOKS_ONLINE_INTEGRATION_REQUESTED>;

export interface AddQuickbooksIntegrationSuccessfulAction
	extends Action<UsersActionTypes.ADD_QUICKBOOKS_ONLINE_INTEGRATION_SUCCESSFUL> {
	readonly integrations: Integrations;
}

export type AddQuickbooksIntegrationFailedAction =
	Action<UsersActionTypes.ADD_QUICKBOOKS_ONLINE_INTEGRATION_FAILED>;
export type DeleteQuickbooksIntegrationRequestedAction =
	Action<UsersActionTypes.DELETE_QUICKBOOKS_ONLINE_INTEGRATION_REQUESTED>;

export interface DeleteQuickbooksIntegrationSuccessfulAction
	extends Action<UsersActionTypes.DELETE_QUICKBOOKS_ONLINE_INTEGRATION_SUCCESSFUL> {
	readonly integrations: Integrations;
}

export type DeleteQuickbooksIntegrationFailedAction =
	Action<UsersActionTypes.DELETE_QUICKBOOKS_ONLINE_INTEGRATION_FAILED>;

export type TestHaloPSACredentialsRequestedAction =
	Action<UsersActionTypes.TEST_HALOPSA_CREDENTIALS_REQUESTED>;

export interface TestHaloPSACredentialsSuccessfulAction
	extends Action<UsersActionTypes.TEST_HALOPSA_CREDENTIALS_SUCCESSFUL>,
		TestHaloPSACredentialsResponse {}

export type TestHaloPSACredentialsFailedAction =
	Action<UsersActionTypes.TEST_HALOPSA_CREDENTIALS_FAILED>;

export type FetchHaloPSAMetadataRequestedAction =
	Action<UsersActionTypes.FETCH_HALOPSA_METADATA_REQUESTED>;

export interface FetchHaloPSAMetadataSuccessfulAction
	extends Action<UsersActionTypes.FETCH_HALOPSA_METADATA_SUCCESSFUL> {
	readonly metadata: HaloPSAMetadata;
}

export type FetchHaloPSAMetadataFailedAction =
	Action<UsersActionTypes.FETCH_HALOPSA_METADATA_FAILED>;

export type ImportHaloTaxInfoRequestedAction =
	Action<UsersActionTypes.IMPORT_HALO_TAX_INFO_REQUESTED>;

export interface ImportHaloTaxInfoSuccessfulAction
	extends Action<UsersActionTypes.IMPORT_HALO_TAX_INFO_SUCCESSFUL> {
	readonly taxInfo: TaxInfo;
}

export type ImportHaloTaxInfoFailedlAction =
	Action<UsersActionTypes.IMPORT_HALO_TAX_INFO_FAILED>;

export type AddHaloIntegrationRequestedAction =
	Action<UsersActionTypes.ADD_HALO_INTEGRATION_REQUESTED>;

export interface AddHaloIntegrationSuccessfulAction
	extends Action<UsersActionTypes.ADD_HALO_INTEGRATION_SUCCESSFUL> {
	readonly newIntegrations: Integrations;
}

export type AddHaloIntegrationFailedlAction =
	Action<UsersActionTypes.ADD_HALO_INTEGRATION_FAILED>;

export type DeleteHaloPSAIntegrationRequestedAction =
	Action<UsersActionTypes.DELETE_HALO_INTEGRATION_REQUESTED>;

export interface DeleteHaloPSAIntegrationSuccessfulAction
	extends Action<UsersActionTypes.DELETE_HALO_INTEGRATION_SUCCESSFUL> {
	readonly integrations: Integrations;
}

export type DeleteHaloPSAIntegrationFailedAction =
	Action<UsersActionTypes.DELETE_HALO_INTEGRATION_FAILED>;

export type FetchHaloPSAAuthDetailsRequestedAction =
	Action<UsersActionTypes.FETCH_HALOPSA_AUTH_DETAILS_REQUESTED>;

export interface FetchHaloPSAAuthDetailsSuccessfulAction
	extends Action<UsersActionTypes.FETCH_HALOPSA_AUTH_DETAILS_SUCCESSFUL> {
	authDetails: HaloPSAAuthDetails;
}

export type FetchHaloPSAAuthDetailsFailedAction =
	Action<UsersActionTypes.FETCH_HALOPSA_AUTH_DETAILS_FAILED>;

export interface AddCustomDistributorSuccessfulAction
	extends Action<UsersActionTypes.ADD_CUSTOM_DISTRIBUTOR_SUCCESSFUL> {
	readonly integrations: Integrations;
}

export type AddCustomDistributorRequestedAction =
	Action<UsersActionTypes.ADD_CUSTOM_DISTRIBUTOR_REQUESTED>;
export type AddCustomDistributorFailedAction =
	Action<UsersActionTypes.ADD_CUSTOM_DISTRIBUTOR_FAILED>;

export interface DeleteCustomDistributorSuccessfulAction
	extends Action<UsersActionTypes.DELETE_CUSTOM_DISTRIBUTOR_SUCCESSFUL> {
	readonly integrations: Integrations;
}

export type DeleteCustomDistributorRequestedAction =
	Action<UsersActionTypes.DELETE_CUSTOM_DISTRIBUTOR_REQUESTED>;
export type DeleteCustomDistributorFailedAction =
	Action<UsersActionTypes.DELETE_CUSTOM_DISTRIBUTOR_FAILED>;

export interface ConnectDatekDistributorSuccessfulAction
	extends Action<UsersActionTypes.CONNECT_DATEK_DISTRIBUTOR_SUCCESSFUL> {
	readonly integrations: Integrations;
}

export type ConnectDatekDistributorRequestedAction =
	Action<UsersActionTypes.CONNECT_DATEK_DISTRIBUTOR_REQUESTED>;
export type ConnectDatekDistributorFailedAction =
	Action<UsersActionTypes.CONNECT_DATEK_DISTRIBUTOR_FAILED>;

export interface EditCompanyPreferencesSuccessfulAction
	extends Action<UsersActionTypes.EDIT_COMPANY_PREFERENCES_SUCCESSFUL> {
	readonly userCompany: UserCompany;
}

export type EditCompanyPreferencesRequestedAction =
	Action<UsersActionTypes.EDIT_COMPANY_PREFERENCES_REQUESTED>;
export type EditCompanyPreferencesFailedAction =
	Action<UsersActionTypes.EDIT_COMPANY_PREFERENCES_FAILED>;
export interface UpdateTranslateLanguageSuccessfulAction
	extends Action<UsersActionTypes.UDPATE_TRANSLATE_LANGUAGE_SUCCESSFUL> {
	readonly editUserCompany: UserCompany;
}

export type SubscriptionPlanEstimateResetAction =
	Action<UsersActionTypes.SUBSCRIPTION_PLAN_ESTIMATE_RESET>;

export interface UpdateInitialSyncStatusAction
	extends Action<UsersActionTypes.UPDATE_INITIAL_SYNC_STATUS> {
	readonly integrationToUpdate: string;
}

export interface UpdateIntegrationPageDetailsAction
	extends Action<UsersActionTypes.UPDATE_INTEGRATIION_PAGE_DETAILS> {
	readonly companyId: string;
	readonly integrations: Integrations;
}
export interface AIWriterInfoUpdateToCompanyDataAction
	extends Action<UsersActionTypes.AI_WRITER_TRIAL_INFO_UPDATED> {
	readonly aiWriterTrialInfo: TrialInfoAIWriter;
}

export type DeleteAmazonDistributorRequestedAction =
	Action<UsersActionTypes.DELETE_AMAZON_DISTRIBUTOR_REQUESTED>;

export interface DeleteAmazonDistributorSuccessfulAction
	extends Action<UsersActionTypes.DELETE_AMAZON_DISTRIBUTOR_SUCCESSFUL> {
	readonly integrations: Integrations;
}

export type DeleteAmazonDistributorFailedAction =
	Action<UsersActionTypes.DELETE_AMAZON_DISTRIBUTOR_FAILED>;

export type GetAmazonMetadataRequestedAction =
	Action<UsersActionTypes.GET_AMAZON_META_DATA_REQUESTED>;

export interface GetAmazonMetadataSuccessfulAction
	extends Action<UsersActionTypes.GET_AMAZON_META_DATA_SUCCESSFUL> {
	readonly amazonMetaData: any;
}

export type GetAmazonMetadataFailedAction =
	Action<UsersActionTypes.GET_AMAZON_META_DATA_FAILED>;
export interface UpdateAmazonProductAppliedFilterAction
	extends Action<UsersActionTypes.UPDATE_AMAZON_APPLIED_FILTER> {
	readonly amazonAppliedFilters: AmazonProductAppliedFilters;
}

export type UsersActions =
	| ShowUserLoaderAction
	| HideUserLoaderAction
	| UserDataRequestedAction
	| UserDataSuccessfulAction
	| UserDataFailedAction
	| EditUserDataRequestedAction
	| EditUserDataSuccessfulAction
	| EditUserDataFailedAction
	| UserDataUpdatedAction
	| SetTwoFactorAuthRequestedAction
	| SetTwoFactorAuthSuccessfulAction
	| SetTwoFactorAuthFailedAction
	| SetSsoMandatoryRequestedAction
	| SetSsoMandatorySuccessfulAction
	| SetSsoMandatoryFailedAction
	| SetSsoEnableRequestedAction
	| SetSsoEnableSuccessfulAction
	| SetSsoEnableFailedAction
	| ConfigurationSettingsRequestedAction
	| ConfigurationSettingsSuccessfulAction
	| ConfigurationSettingsFailedAction
	| UserCompanyConfigUpdateFailedAction
	| UserCompanyConfigUpdateRequestedAction
	| UserCompanyConfigUpdateSuccessfulAction
	| CompanyDataUpdatedAction
	| UserCompanyRequestedAction
	| UserCompanySuccessfulAction
	| UserCompanyFailedAction
	| GetDatePatternsRequestedAction
	| GetDatePatternsSuccessfulAction
	| GetDatePatternsFailedAction
	| EditUserCompanyRequestedAction
	| EditUserCompanySuccessfulAction
	| EditUserCompanyFailedAction
	| EditUserPasswordRequestedAction
	| EditUserPasswordSuccessfulAction
	| EditUserPasswordFailedAction
	| TaxCategoriesListRequestedAction
	| TaxCategoriesListSuccessfulAction
	| TaxCategoriesListFailedAction
	| TaxRegionsListRequestedAction
	| TaxRegionsListSuccessfulAction
	| TaxRegionsListFailedAction
	| AddTaxRegionRequestedAction
	| AddTaxRegionSuccessfulAction
	| AddTaxRegionFailedAction
	| EditTaxRegionRequestedAction
	| EditTaxRegionSuccessfulAction
	| EditTaxRegionFailedAction
	| AddTaxCategoryRequestedAction
	| AddTaxCategorySuccessfulAction
	| AddTaxCategoryFailedAction
	| EditTaxCategoryRequestedAction
	| EditTaxCategorySuccessfulAction
	| EditTaxCategoryFailedAction
	| AddTaxRegionDrawerOpenedAction
	| AddTaxRegionDrawerClosedAction
	| EditTaxRegionDrawerOpenedAction
	| EditTaxRegionDrawerClosedAction
	| AddTaxCategoryDrawerOpenedAction
	| AddTaxCategoryDrawerClosedAction
	| EditTaxCategoryDrawerOpenedAction
	| EditTaxCategoryDrawerClosedAction
	| ChangeCompanyLogoRequestedAction
	| ChangeCompanyLogoSuccessfulAction
	| ChangeCompanyLogoFailedAction
	| CompanyUsersRequestedAction
	| CompanyUsersSuccessfulAction
	| CompanyUsersFailedAction
	| DeleteCompanyUserRequestedAction
	| DeleteCompanyUserSuccessfulAction
	| DeleteCompanyUserFailedAction
	| AddCompanyUserRequestedAction
	| AddCompanyUserSuccessfulAction
	| AddCompanyUserFailedAction
	| EditCompanyUserRequestedAction
	| EditCompanyUserSuccessfulAction
	| EditCompanyUserFailedAction
	| EditCurrentCompanyUserRequestedAction
	| EditCurrentCompanyUserSuccessfulAction
	| EditCurrentCompanyUserFailedAction
	| UpdateCompanyUserAction
	| DeleteCompanyEmailIntegrationRequestedAction
	| DeleteCompanyEmailIntegrationSuccessfulAction
	| DeleteCompanyEmailIntegrationFailedAction
	| AddCompanyEmailIntegrationRequestedAction
	| AddCompanyEmailIntegrationSuccessfulAction
	| AddCompanyEmailIntegrationFailedAction
	| DeleteCompanyConnectWiseIntegrationRequestedAction
	| DeleteCompanyConnectWiseIntegrationSuccessfulAction
	| DeleteCompanyConnectWiseIntegrationFailedAction
	| AddCompanyConnectWiseIntegrationRequestedAction
	| AddCompanyConnectWiseIntegrationSuccessfulAction
	| AddCompanyConnectWiseIntegrationFailedAction
	| DeleteCompanyAutotaskIntegrationRequestedAction
	| DeleteCompanyAutotaskIntegrationSuccessfulAction
	| DeleteCompanyAutotaskIntegrationFailedAction
	| AddCompanyAutotaskIntegrationRequestedAction
	| AddCompanyAutotaskIntegrationSuccessfulAction
	| AddCompanyAutotaskIntegrationFailedAction
	| DeleteCompanyZapierIntegrationRequestedAction
	| DeleteCompanyZapierIntegrationSuccessfulAction
	| DeleteCompanyZapierIntegrationFailedAction
	| AddCompanyKaseyaIntegrationRequestedAction
	| AddCompanyKaseyaIntegrationSuccessfulAction
	| AddCompanyKaseyaIntegrationFailedAction
	| DeleteCompanyKaseyaIntegrationRequestedAction
	| DeleteCompanyKaseyaIntegrationSuccessfulAction
	| DeleteCompanyKaseyaIntegrationFailedAction
	| AddCompanyIngramIntegrationRequestedAction
	| AddCompanyIngramIntegrationSuccessfulAction
	| AddCompanyIngramIntegrationFailedAction
	| DeleteCompanyIngramIntegrationRequestedAction
	| DeleteCompanyIngramIntegrationSuccessfulAction
	| DeleteCompanyIngramIntegrationFailedAction
	| AddCompanyTechDataIntegrationRequestedAction
	| AddCompanyTechDataIntegrationSuccessfulAction
	| AddCompanyTechDataIntegrationFailedAction
	| DeleteCompanyTechDataIntegrationRequestedAction
	| DeleteCompanyTechDataIntegrationSuccessfulAction
	| DeleteCompanyTechDataIntegrationFailedAction
	| AddDAndHIntegrationRequestedAction
	| AddDAndHIntegrationSuccessfulAction
	| AddDAndHIntegrationFailedAction
	| DeleteDAndHIntegrationRequestedAction
	| DeleteDAndHIntegrationSuccessfulAction
	| DeleteDAndHIntegrationFailedAction
	| AddSynnexIntegrationRequestedAction
	| AddSynnexIntegrationSuccessfulAction
	| AddSynnexIntegrationFailedAction
	| DeleteSynnexIntegrationRequestedAction
	| DeleteSynnexIntegrationSuccessfulAction
	| DeleteSynnexIntegrationFailedAction
	| AddCBIntegrationRequestedAction
	| AddCBIntegrationSuccessfulAction
	| AddCBIntegrationFailedAction
	| DeleteCBIntegrationRequestedAction
	| DeleteCBIntegrationSuccessfulAction
	| DeleteCBIntegrationFailedAction
	| AddCompanySyncroIntegrationRequestedAction
	| AddCompanySyncroIntegrationSuccessfulAction
	| AddCompanySyncroIntegrationFailedAction
	| DeleteCompanySyncroIntegrationRequestedAction
	| DeleteCompanySyncroIntegrationSuccessfulAction
	| DeleteCompanySyncroIntegrationFailedAction
	| AddInfusionSoftIntegrationRequestedAction
	| AddInfusionSoftIntegrationSuccessfulAction
	| AddInfusionSoftIntegrationFailedAction
	| DeleteInfusionSoftIntegrationRequestedAction
	| DeleteInfusionSoftIntegrationSuccessfulAction
	| DeleteInfusionSoftIntegrationFailedAction
	| GetCompanyHubspotIntegrationMetadataRequestedAction
	| GetCompanyHubspotIntegrationMetadataSuccessfulAction
	| GetCompanyHubspotIntegrationMetadataFailedAction
	| AddCompanyHubspotIntegrationRequestedAction
	| AddCompanyHubspotIntegrationSuccessfulAction
	| AddCompanyHubspotIntegrationFailedAction
	| DeleteCompanyHubspotIntegrationRequestedAction
	| DeleteCompanyHubspotIntegrationSuccessfulAction
	| DeleteCompanyHubspotIntegrationFailedAction
	| EditCompanyPreferencesSuccessfulAction
	| EditCompanyPreferencesFailedAction
	| EditCompanyPreferencesRequestedAction
	| UpdateTranslateLanguageSuccessfulAction
	| UserRequestJoinCompanyRequestedAction
	| UserRequestJoinCompanySuccessfulAction
	| UserRequestJoinCompanyFailedAction
	| UserRequestWithdrawCompanyRequestedAction
	| UserRequestWithdrawCompanySuccessfulAction
	| UserRequestWithdrawCompanyFailedAction
	// library
	| ShowLibraryLoaderAction
	| HideLibraryLoaderAction
	| FetchLibraryListRequestedAction
	| FetchLibraryListSuccessfulAction
	| FetchLibraryListFailedAction
	| SaveBlockToLibraryListRequestedAction
	| SaveBlockToLibraryListSuccessfulAction
	| SaveBlockToLibraryListFailedAction
	| DeleteBlockToLibraryListRequestedAction
	| DeleteBlockToLibraryListSuccessfulAction
	| DeleteBlockToLibraryListFailedAction
	| UpdateLibraryItemTagsSuccessfulAction
	// payment
	| ConfirmCardAddedRequestedAction
	| ConfirmCardAddedFailedAction
	| ConfirmCardAddedSuccessfulAction
	| ChargebeePortalSessionRequestedAction
	| ChargebeePortalSessionSuccessfulAction
	| ChargebeePortalSessionFailedAction
	| BillingInfoRequestedAction
	| BillingInfoFailedAction
	| BillingInfoSuccessfulAction
	| SubscriptionPlansRequestedAction
	| SubscriptionPlansFailedAction
	| SubscriptionPlansSuccessfulAction
	| SubscriptionDetailsRequestedAction
	| SubscriptionDetailsFailedAction
	| SubscriptionDetailsSuccessfulAction
	| SubscriptionPlanAddUserFailedAction
	| SubscriptionPlanAddUserRequestedAction
	| SubscriptionPlanAddUserSuccessfulAction
	| SubscriptionPlanRemoveUserFailedAction
	| SubscriptionPlanRemoveUserRequestedAction
	| SubscriptionPlanRemoveUserSuccessfulAction
	| SubscriptionPlanUpgradeDowngradeFailedAction
	| SubscriptionPlanUpgradeDowngradeRequestedAction
	| SubscriptionPlanUpgradeDowngradeSuccessfulAction
	| SubscriptionPlanEstimateRequestedAction
	| SubscriptionPlanEstimateSuccessfulAction
	| SubscriptionPlanEstimateFailedAction
	| ShowSubcriptionDetailsLoaderAction
	| HideSubscriptionDetailsLoaderAction
	| SubscriptionPlanEstimateResetAction
	| ShowUpdateCardModalAction
	| HideUpdateCardModalAction
	| ShowBillingInfoLoaderAction
	| AddEmailAliasSuccessfulAction
	| AddEmailAliasRequestedAction
	| AddEmailAliasFailedAction
	| DeleteEmailAliasSuccessfulAction
	| DeleteEmailAliasRequestedAction
	| DeleteEmailAliasFailedAction
	| HideBillingInfoLoaderAction
	| EditQuoteDefaultLabelCompanyRequestedAction
	| EditQuoteDefaultLabelCompanyFailedAction
	| EditQuoteDefaultLabelCompanySuccessfulAction
	| DisableUserRequestedAction
	| DisableUserSuccessfulAction
	| DisableUserFailedAction
	| EnableUserRequestedAction
	| EnableUserSuccessfulAction
	| EnableUserFailedAction
	| FetchUserCalendarRequestedAction
	| FetchUserCalendarSuccessfulAction
	| FetchUserCalendarFailedAction
	| ResetUserCalendarsAction
	| ConnectCalendarRequestedAction
	| ConnectCalendarSuccessfulAction
	| ConnectCalendarFailedAction
	| FetchCompanyIntegrationsRequestedAction
	| FetchCompanyIntegrationsSuccessfulAction
	| FetchCompanyIntegrationsFailedAction
	// chargebee
	| ChargebeeInvoiceListRequestedAction
	| ChargebeeInvoiceListSuccessfulAction
	| ChargebeeInvoiceListFailedAction
	| ChargebeeInvoiceDetailsRequestedAction
	| ChargebeeInvoiceDetailsSuccessfulAction
	| ChargebeeInvoiceDetailsFailedAction
	| ChargebeeCreditNotesListFailedAction
	| ChargebeeCreditNotesListRequestedAction
	| ChargebeeCreditNotesListSuccessfulAction
	| ChargebeeCreditNoteDetailsFailedAction
	| ChargebeeCreditNoteDetailsRequestedAction
	| ChargebeeCreditNoteDetailsSuccessfulAction
	| PauseChargebeeSubscriptionRequestedAction
	| PauseChargebeeSubscriptionSuccessfulAction
	| PauseChargebeeSubscriptionFailedAction
	// DKIM domain settings
	| VerifyUserEmail
	| AddCustomDomain
	| DeleteCustomEmail
	| DeleteCustomDomain
	| UpdateEmailPreferencesRequested
	| UpdateEmailPreferencesSuccessful
	| UpdateEmailPreferencesFailed
	| ResendDomainVerificationRequested
	| ResendDomainVerificationSuccessful
	| ResendDomainVerificationFailed
	| UpdateEmailSignaturesRequested
	| UpdateEmailSignaturesSuccessful
	| UpdateEmailSignaturesFailed
	| FetchAllEmailSignaturesRequested
	| FetchAllEmailSignaturesSuccessful
	| FetchAllEmailSignaturesFailed
	| ResetUserStateAction
	| OnboardingCallScheduledRequestedAction
	| OnboardingCallScheduledSuccessfulAction
	| OnboardingCallScheduledFailedAction
	| AddDickerDataIntegrationRequestedAction
	| AddDickerDataIntegrationSuccessfulAction
	| AddDickerDataIntegrationFailedAction
	| DeleteDickerDataIntegrationRequestedAction
	| DeleteDickerDataIntegrationSuccessfulAction
	| DeleteDickerDataIntegrationFailedAction
	| AddWestcoastIntegrationRequestedAction
	| AddWestcoastIntegrationSuccessfulAction
	| AddWestcoastIntegrationFailedAction
	| DeleteWestcoastIntegrationRequestedAction
	| DeleteWestcoastIntegrationSuccessfulAction
	| DeleteWestcoastIntegrationFailedAction
	| AddStripeConnectionRequestedAction
	| AddStripeConnectionFailedAction
	| AddStripeConnectionSuccessfulAction
	| DeleteStripeConnectionRequestedAction
	| DeleteStripeConnectionFailedAction
	| DeleteStripeConnectionSuccessfulAction
	| DeletePaymentsConnectionSuccessfulAction
	| GetQuickbooksIntegrationMetadataRequestedAction
	| GetQuickbooksIntegrationMetadataSuccessfulAction
	| GetQuickbooksIntegrationMetadataFailedAction
	| AddQuickbooksIntegrationRequestedAction
	| AddQuickbooksIntegrationSuccessfulAction
	| AddQuickbooksIntegrationFailedAction
	| DeleteQuickbooksIntegrationRequestedAction
	| DeleteQuickbooksIntegrationSuccessfulAction
	| DeleteQuickbooksIntegrationFailedAction
	//HaloPSA
	| TestHaloPSACredentialsRequestedAction
	| TestHaloPSACredentialsSuccessfulAction
	| TestHaloPSACredentialsFailedAction
	| FetchHaloPSAMetadataRequestedAction
	| FetchHaloPSAMetadataSuccessfulAction
	| FetchHaloPSAMetadataFailedAction
	| ImportHaloTaxInfoRequestedAction
	| ImportHaloTaxInfoSuccessfulAction
	| ImportHaloTaxInfoFailedlAction
	| AddHaloIntegrationRequestedAction
	| AddHaloIntegrationSuccessfulAction
	| AddHaloIntegrationFailedlAction
	| DeleteHaloPSAIntegrationRequestedAction
	| DeleteHaloPSAIntegrationFailedAction
	| DeleteHaloPSAIntegrationSuccessfulAction
	| FetchHaloPSAAuthDetailsRequestedAction
	| FetchHaloPSAAuthDetailsSuccessfulAction
	| FetchHaloPSAAuthDetailsFailedAction
	| AddCustomDistributorSuccessfulAction
	| DeleteCustomDistributorSuccessfulAction
	| ConnectDatekDistributorRequestedAction
	| ConnectDatekDistributorSuccessfulAction
	| ConnectDatekDistributorFailedAction
	// Favourite Integrations
	| UpdateInitialSyncStatusAction
	| UpdateIntegrationPageDetailsAction
	| AIWriterInfoUpdateToCompanyDataAction
	// Amazon Distributor
	| DeleteAmazonDistributorRequestedAction
	| DeleteAmazonDistributorSuccessfulAction
	| DeleteAmazonDistributorFailedAction
	| GetAmazonMetadataRequestedAction
	| GetAmazonMetadataSuccessfulAction
	| GetAmazonMetadataFailedAction
	| UpdateAmazonProductAppliedFilterAction;

export interface UserDispatch {
	showUserLoader: ActionCreator<
		ThunkAction<
			ShowUserLoaderAction,
			GlobalState,
			null,
			ShowUserLoaderAction
		>
	>;
	hideUserLoader: ActionCreator<
		ThunkAction<
			HideUserLoaderAction,
			GlobalState,
			null,
			HideUserLoaderAction
		>
	>;
	showLibraryLoader: ActionCreator<
		ThunkAction<
			ShowLibraryLoaderAction,
			GlobalState,
			null,
			ShowLibraryLoaderAction
		>
	>;
	hideLibraryLoader: ActionCreator<
		ThunkAction<
			HideLibraryLoaderAction,
			GlobalState,
			null,
			HideLibraryLoaderAction
		>
	>;
	showSubscriptionDetailsLoader: ActionCreator<
		ThunkAction<
			ShowSubcriptionDetailsLoaderAction,
			GlobalState,
			null,
			ShowSubcriptionDetailsLoaderAction
		>
	>;
	hideSubscriptionDetailsLoader: ActionCreator<
		ThunkAction<
			HideSubscriptionDetailsLoaderAction,
			GlobalState,
			null,
			HideSubscriptionDetailsLoaderAction
		>
	>;
	getUserData: ActionCreator<
		ThunkAction<
			Promise<UserDataSuccessfulAction>,
			GlobalState,
			null,
			UserDataSuccessfulAction
		>
	>;
	updateUserData: ActionCreator<
		ThunkAction<
			UserDataUpdatedAction,
			GlobalState,
			UserData,
			UserDataUpdatedAction
		>
	>;
	editUserData: ActionCreator<
		ThunkAction<
			Promise<EditUserDataSuccessfulAction>,
			GlobalState,
			UserData,
			EditUserDataSuccessfulAction
		>
	>;
	getConfigurationSettings: ActionCreator<
		ThunkAction<
			Promise<ConfigurationSettingsSuccessfulAction>,
			GlobalState,
			null,
			ConfigurationSettingsSuccessfulAction
		>
	>;
	getInitialConfig: ActionCreator<
		ThunkAction<
			Promise<ConfigurationSettingsSuccessfulAction>,
			GlobalState,
			null,
			ConfigurationSettingsSuccessfulAction
		>
	>;
	bootstrapAuthenticatedSession: ActionCreator<
		ThunkAction<
			Promise<ConfigurationSettingsSuccessfulAction>,
			GlobalState,
			null,
			ConfigurationSettingsSuccessfulAction
		>
	>;
	userRequestJoinCompany: ActionCreator<
		ThunkAction<
			Promise<UserRequestJoinCompanySuccessfulAction>,
			GlobalState,
			null,
			UserRequestJoinCompanySuccessfulAction
		>
	>;
	userRequestWithdrawCompany: ActionCreator<
		ThunkAction<
			Promise<UserRequestWithdrawCompanySuccessfulAction>,
			GlobalState,
			null,
			UserRequestWithdrawCompanySuccessfulAction
		>
	>;
	userCompanyConfigUpdate: ActionCreator<
		ThunkAction<
			Promise<UserCompanyConfigUpdateSuccessfulAction>,
			GlobalState,
			null,
			UserCompanyConfigUpdateSuccessfulAction
		>
	>;
	updateCompanyData: ActionCreator<
		ThunkAction<
			CompanyDataUpdatedAction,
			GlobalState,
			UserData,
			CompanyDataUpdatedAction
		>
	>;
	setTwoFactorAuth: ActionCreator<
		ThunkAction<
			Promise<SetTwoFactorAuthSuccessfulAction>,
			GlobalState,
			boolean,
			SetTwoFactorAuthSuccessfulAction
		>
	>;
	setSsoMandatory: ActionCreator<
		ThunkAction<
			Promise<SetSsoMandatorySuccessfulAction>,
			GlobalState,
			boolean,
			SetTwoFactorAuthSuccessfulAction
		>
	>;
	setSsoEnable: ActionCreator<
		ThunkAction<
			Promise<SetSsoEnableSuccessfulAction>,
			GlobalState,
			boolean,
			SetSsoEnableSuccessfulAction
		>
	>;
	getUserCompany: ActionCreator<
		ThunkAction<
			Promise<UserCompanySuccessfulAction>,
			GlobalState,
			null,
			UserCompanySuccessfulAction
		>
	>;
	editUserCompany: ActionCreator<
		ThunkAction<
			Promise<EditUserCompanySuccessfulAction>,
			GlobalState,
			UserCompany,
			EditUserCompanySuccessfulAction
		>
	>;
	editUserPassword: ActionCreator<
		ThunkAction<
			Promise<EditUserPasswordSuccessfulAction>,
			GlobalState,
			string,
			EditUserPasswordSuccessfulAction
		>
	>;
	changeCompanyLogo: ActionCreator<
		ThunkAction<
			Promise<ChangeCompanyLogoSuccessfulAction>,
			GlobalState,
			ChangeCompanyLogoRequest,
			ChangeCompanyLogoSuccessfulAction
		>
	>;
	getDatePatterns: ActionCreator<
		ThunkAction<
			Promise<GetDatePatternsSuccessfulAction>,
			GlobalState,
			null,
			GetDatePatternsSuccessfulAction
		>
	>;
	getCompanyUsers: ActionCreator<
		ThunkAction<
			Promise<CompanyUsersSuccessfulAction>,
			GlobalState,
			CompanyUsersRequest,
			CompanyUsersSuccessfulAction
		>
	>;
	deleteCompanyUser: ActionCreator<
		ThunkAction<
			Promise<DeleteCompanyUserSuccessfulAction>,
			GlobalState,
			UserCompany,
			DeleteCompanyUserSuccessfulAction
		>
	>;
	addCompanyUser: ActionCreator<
		ThunkAction<
			Promise<AddCompanyUserSuccessfulAction>,
			GlobalState,
			AddCompanyUserRequest,
			AddCompanyUserSuccessfulAction
		>
	>;
	editCompanyUser: ActionCreator<
		ThunkAction<
			Promise<EditCompanyUserSuccessfulAction>,
			GlobalState,
			UserCompany,
			EditCompanyUserSuccessfulAction
		>
	>;
	editCurrentCompanyUser: ActionCreator<
		ThunkAction<
			Promise<EditCurrentCompanyUserSuccessfulAction>,
			GlobalState,
			UserCompany,
			EditCurrentCompanyUserSuccessfulAction
		>
	>;
	updateCompanyUser: ActionCreator<
		ThunkAction<
			UpdateCompanyUserAction,
			GlobalState,
			UserCompany,
			UpdateCompanyUserAction
		>
	>;
	getTaxCategoriesList: ActionCreator<
		ThunkAction<
			Promise<TaxCategoriesListSuccessfulAction>,
			GlobalState,
			UserCompany,
			TaxCategoriesListSuccessfulAction
		>
	>;
	getTaxRegionsList: ActionCreator<
		ThunkAction<
			Promise<TaxRegionsListSuccessfulAction>,
			GlobalState,
			UserCompany,
			TaxRegionsListSuccessfulAction
		>
	>;
	addTaxRegion: ActionCreator<
		ThunkAction<
			Promise<AddTaxRegionSuccessfulAction>,
			GlobalState,
			UserCompany,
			AddTaxRegionSuccessfulAction
		>
	>;
	editTaxRegion: ActionCreator<
		ThunkAction<
			Promise<EditTaxRegionSuccessfulAction>,
			GlobalState,
			UserCompany,
			EditTaxRegionSuccessfulAction
		>
	>;
	addTaxCategory: ActionCreator<
		ThunkAction<
			Promise<AddTaxCategorySuccessfulAction>,
			GlobalState,
			UserCompany,
			AddTaxCategorySuccessfulAction
		>
	>;
	editTaxCategory: ActionCreator<
		ThunkAction<
			Promise<EditTaxCategorySuccessfulAction>,
			GlobalState,
			UserCompany,
			EditTaxCategorySuccessfulAction
		>
	>;
	addTaxRegionDrawerOpened: ActionCreator<
		ThunkAction<
			AddTaxRegionDrawerOpenedAction,
			GlobalState,
			UserCompany,
			AddTaxRegionDrawerOpenedAction
		>
	>;
	addTaxRegionDrawerClosed: ActionCreator<
		ThunkAction<
			AddTaxRegionDrawerClosedAction,
			GlobalState,
			UserCompany,
			AddTaxRegionDrawerClosedAction
		>
	>;
	editTaxRegionDrawerOpened: ActionCreator<
		ThunkAction<
			EditTaxRegionDrawerOpenedAction,
			GlobalState,
			UserCompany,
			EditTaxRegionDrawerOpenedAction
		>
	>;
	editTaxRegionDrawerClosed: ActionCreator<
		ThunkAction<
			EditTaxRegionDrawerClosedAction,
			GlobalState,
			UserCompany,
			EditTaxRegionDrawerClosedAction
		>
	>;
	addTaxCategoryDrawerOpened: ActionCreator<
		ThunkAction<
			AddTaxCategoryDrawerOpenedAction,
			GlobalState,
			UserCompany,
			AddTaxCategoryDrawerOpenedAction
		>
	>;
	addTaxCategoryDrawerClosed: ActionCreator<
		ThunkAction<
			AddTaxCategoryDrawerClosedAction,
			GlobalState,
			UserCompany,
			AddTaxCategoryDrawerClosedAction
		>
	>;
	editTaxCategoryDrawerOpened: ActionCreator<
		ThunkAction<
			EditTaxCategoryDrawerOpenedAction,
			GlobalState,
			UserCompany,
			EditTaxCategoryDrawerOpenedAction
		>
	>;
	editTaxCategoryDrawerClosed: ActionCreator<
		ThunkAction<
			EditTaxCategoryDrawerClosedAction,
			GlobalState,
			UserCompany,
			EditTaxCategoryDrawerClosedAction
		>
	>;
	deleteCompanyEmailIntegration: ActionCreator<
		ThunkAction<
			Promise<DeleteCompanyEmailIntegrationSuccessfulAction>,
			GlobalState,
			null,
			DeleteCompanyEmailIntegrationSuccessfulAction
		>
	>;
	addCompanyEmailIntegration: ActionCreator<
		ThunkAction<
			Promise<AddCompanyEmailIntegrationSuccessfulAction>,
			GlobalState,
			AddEmailIntegrationRequest,
			AddCompanyEmailIntegrationSuccessfulAction
		>
	>;
	deleteCompanyConnectWiseIntegration: ActionCreator<
		ThunkAction<
			Promise<DeleteCompanyConnectWiseIntegrationSuccessfulAction>,
			GlobalState,
			null,
			DeleteCompanyConnectWiseIntegrationSuccessfulAction
		>
	>;
	addCompanyConnectWiseIntegration: ActionCreator<
		ThunkAction<
			Promise<AddCompanyConnectWiseIntegrationSuccessfulAction>,
			GlobalState,
			AddConnectWiseIntegrationRequest,
			AddCompanyConnectWiseIntegrationSuccessfulAction
		>
	>;
	deleteCompanyAutotaskIntegration: ActionCreator<
		ThunkAction<
			Promise<DeleteCompanyAutotaskIntegrationSuccessfulAction>,
			GlobalState,
			null,
			DeleteCompanyAutotaskIntegrationSuccessfulAction
		>
	>;
	addCompanyAutotaskIntegration: ActionCreator<
		ThunkAction<
			Promise<AddCompanyAutotaskIntegrationSuccessfulAction>,
			GlobalState,
			AutotaskIntegrationResponse,
			AddCompanyAutotaskIntegrationSuccessfulAction
		>
	>;
	deleteCompanyKaseyaIntegration: ActionCreator<
		ThunkAction<
			Promise<DeleteCompanyKaseyaIntegrationSuccessfulAction>,
			GlobalState,
			null,
			DeleteCompanyKaseyaIntegrationSuccessfulAction
		>
	>;
	addCompanyKaseyaIntegration: ActionCreator<
		ThunkAction<
			Promise<AddCompanyKaseyaIntegrationSuccessfulAction>,
			GlobalState,
			AddKaseyaIntegrationRequest,
			AddCompanyKaseyaIntegrationSuccessfulAction
		>
	>;
	deleteCompanyZapierIntegration: ActionCreator<
		ThunkAction<
			Promise<DeleteCompanyZapierIntegrationSuccessfulAction>,
			GlobalState,
			DeleteZapierIntegrationRequest,
			DeleteCompanyZapierIntegrationSuccessfulAction
		>
	>;
	addCompanyIngramIntegration: ActionCreator<
		ThunkAction<
			Promise<AddCompanyIngramIntegrationSuccessfulAction>,
			GlobalState,
			AddIngramIntegrationRequest,
			AddCompanyIngramIntegrationSuccessfulAction
		>
	>;
	deleteCompanyIngramIntegration: ActionCreator<
		ThunkAction<
			Promise<DeleteCompanyIngramIntegrationSuccessfulAction>,
			GlobalState,
			null,
			DeleteCompanyIngramIntegrationSuccessfulAction
		>
	>;
	addCompanyTechDataIntegration: ActionCreator<
		ThunkAction<
			Promise<AddCompanyTechDataIntegrationSuccessfulAction>,
			GlobalState,
			AddTechDataIntegrationRequest,
			AddCompanyTechDataIntegrationSuccessfulAction
		>
	>;
	deleteCompanyTechDataIntegration: ActionCreator<
		ThunkAction<
			Promise<DeleteCompanyTechDataIntegrationSuccessfulAction>,
			GlobalState,
			null,
			DeleteCompanyTechDataIntegrationSuccessfulAction
		>
	>;
	addDAndHIntegration: ActionCreator<
		ThunkAction<
			Promise<AddDAndHIntegrationSuccessfulAction>,
			GlobalState,
			AddDAndHIntegrationRequest,
			AddDAndHIntegrationSuccessfulAction
		>
	>;
	deleteDAndHIntegration: ActionCreator<
		ThunkAction<
			Promise<DeleteDAndHIntegrationSuccessfulAction>,
			GlobalState,
			null,
			DeleteDAndHIntegrationSuccessfulAction
		>
	>;
	addSynnexIntegration: ActionCreator<
		ThunkAction<
			Promise<AddSynnexIntegrationSuccessfulAction>,
			GlobalState,
			AddSynnexIntegrationRequest,
			AddSynnexIntegrationSuccessfulAction
		>
	>;
	deleteSynnexIntegration: ActionCreator<
		ThunkAction<
			Promise<DeleteSynnexIntegrationSuccessfulAction>,
			GlobalState,
			null,
			DeleteSynnexIntegrationSuccessfulAction
		>
	>;
	addCBIntegration: ActionCreator<
		ThunkAction<
			Promise<AddCBIntegrationSuccessfulAction>,
			GlobalState,
			AddCBIntegrationRequest,
			AddCBIntegrationSuccessfulAction
		>
	>;
	deleteCBIntegration: ActionCreator<
		ThunkAction<
			Promise<DeleteCBIntegrationSuccessfulAction>,
			GlobalState,
			null,
			DeleteCBIntegrationSuccessfulAction
		>
	>;
	addCompanySyncroIntegration: ActionCreator<
		ThunkAction<
			Promise<AddCompanySyncroIntegrationSuccessfulAction>,
			GlobalState,
			AddSyncroIntegrationRequest,
			AddCompanySyncroIntegrationSuccessfulAction
		>
	>;
	deleteCompanySyncroIntegration: ActionCreator<
		ThunkAction<
			Promise<DeleteCompanySyncroIntegrationSuccessfulAction>,
			GlobalState,
			null,
			DeleteCompanySyncroIntegrationSuccessfulAction
		>
	>;
	addInfusionSoftIntegration: ActionCreator<
		ThunkAction<
			Promise<AddInfusionSoftIntegrationSuccessfulAction>,
			GlobalState,
			null,
			AddInfusionSoftIntegrationSuccessfulAction
		>
	>;
	deleteInfusionSoftIntegration: ActionCreator<
		ThunkAction<
			Promise<DeleteInfusionSoftIntegrationSuccessfulAction>,
			GlobalState,
			null,
			DeleteInfusionSoftIntegrationSuccessfulAction
		>
	>;
	fetchLibrary: ActionCreator<
		ThunkAction<
			Promise<FetchLibraryListSuccessfulAction>,
			GlobalState,
			GetLibraryRequest,
			FetchLibraryListSuccessfulAction
		>
	>;
	saveBlockToLibrary: ActionCreator<
		ThunkAction<
			Promise<SaveBlockToLibraryListSuccessfulAction>,
			GlobalState,
			CreateLibraryItemRequest,
			SaveBlockToLibraryListSuccessfulAction
		>
	>;
	deleteBlockFromLibrary: ActionCreator<
		ThunkAction<
			Promise<DeleteBlockToLibraryListSuccessfulAction>,
			GlobalState,
			{ libraryItemId: string },
			DeleteBlockToLibraryListSuccessfulAction
		>
	>;
	updateLibraryResourceTags: ActionCreator<
		ThunkAction<
			Promise<UpdateLibraryItemTagsSuccessfulAction>,
			GlobalState,
			{ libraryItemId: string; tagIds: string[] },
			UpdateLibraryItemTagsSuccessfulAction
		>
	>;
	confirmCardAdded: ActionCreator<
		ThunkAction<
			Promise<ConfirmCardAddedSuccessfulAction>,
			GlobalState,
			string,
			ConfirmCardAddedSuccessfulAction
		>
	>;
	getBillingInfo: ActionCreator<
		ThunkAction<
			Promise<BillingInfoSuccessfulAction>,
			GlobalState,
			null,
			BillingInfoSuccessfulAction
		>
	>;
	getChargebeePortalSession: ActionCreator<
		ThunkAction<
			Promise<ChargebeePortalSessionSuccessfulAction>,
			GlobalState,
			null,
			ChargebeePortalSessionSuccessfulAction
		>
	>;
	updateBillingInfo: ActionCreator<
		ThunkAction<
			Promise<BillingInfoSuccessfulAction>,
			GlobalState,
			UpdateBillingInfoRequest,
			BillingInfoSuccessfulAction
		>
	>;
	getSubscriptionPlans: ActionCreator<
		ThunkAction<
			Promise<SubscriptionPlansSuccessfulAction>,
			GlobalState,
			null,
			SubscriptionPlansSuccessfulAction
		>
	>;
	getSubscriptionDetails: ActionCreator<
		ThunkAction<
			Promise<SubscriptionDetailsSuccessfulAction>,
			GlobalState,
			null,
			SubscriptionDetailsSuccessfulAction
		>
	>;
	getSubscriptionOverview: ActionCreator<
		ThunkAction<
			Promise<SubscriptionDetailsSuccessfulAction>,
			GlobalState,
			null,
			SubscriptionDetailsSuccessfulAction
		>
	>;
	addUserSubscription: ActionCreator<
		ThunkAction<
			Promise<SubscriptionPlanAddUserSuccessfulAction>,
			GlobalState,
			PricingPlanUserRequest,
			SubscriptionPlanAddUserSuccessfulAction
		>
	>;
	removeUserSubscription: ActionCreator<
		ThunkAction<
			Promise<SubscriptionPlanRemoveUserSuccessfulAction>,
			GlobalState,
			PricingPlanUserRequest,
			SubscriptionPlanRemoveUserSuccessfulAction
		>
	>;
	updatePricingPlan: ActionCreator<
		ThunkAction<
			Promise<SubscriptionPlanUpgradeDowngradeSuccessfulAction>,
			GlobalState,
			UpdatePricingPlanRequest,
			SubscriptionPlanUpgradeDowngradeSuccessfulAction
		>
	>;
	planEstimate: ActionCreator<
		ThunkAction<
			Promise<SubscriptionPlanEstimateSuccessfulAction>,
			GlobalState,
			PricingPlanEstimateRequest,
			SubscriptionPlanEstimateSuccessfulAction
		>
	>;
	showUpdateCardModal: ActionCreator<
		ThunkAction<
			ShowUpdateCardModalAction,
			GlobalState,
			null,
			ShowUpdateCardModalAction
		>
	>;
	hideUpdateCardModal: ActionCreator<
		ThunkAction<
			HideUpdateCardModalAction,
			GlobalState,
			null,
			HideUpdateCardModalAction
		>
	>;
	showBillingInfoLoader: ActionCreator<
		ThunkAction<
			ShowBillingInfoLoaderAction,
			GlobalState,
			null,
			ShowBillingInfoLoaderAction
		>
	>;
	hideBillingInfoLoader: ActionCreator<
		ThunkAction<
			HideBillingInfoLoaderAction,
			GlobalState,
			null,
			HideBillingInfoLoaderAction
		>
	>;
	addEmailAlias: ActionCreator<
		ThunkAction<
			Promise<AddEmailAliasSuccessfulAction>,
			GlobalState,
			{ addEmailAlias: EmailAliasRequest },
			AddEmailAliasSuccessfulAction
		>
	>;
	deleteEmailAlias: ActionCreator<
		ThunkAction<
			Promise<DeleteEmailAliasSuccessfulAction>,
			GlobalState,
			{ deleteEmailAlias: EmailAliasRequest },
			DeleteEmailAliasSuccessfulAction
		>
	>;
	editQuoteLabelGlobal: ActionCreator<
		ThunkAction<
			Promise<EditQuoteDefaultLabelCompanySuccessfulAction>,
			GlobalState,
			{ key: string; value: string },
			EditQuoteDefaultLabelCompanySuccessfulAction
		>
	>;
	disableUser: ActionCreator<
		ThunkAction<
			Promise<DisableUserSuccessfulAction>,
			GlobalState,
			{ userId: string },
			DisableUserSuccessfulAction
		>
	>;
	enableUser: ActionCreator<
		ThunkAction<
			Promise<EnableUserSuccessfulAction>,
			GlobalState,
			{ userId: string },
			EnableUserSuccessfulAction
		>
	>;
	fetchUserCalendars: ActionCreator<
		ThunkAction<
			Promise<FetchUserCalendarSuccessfulAction>,
			GlobalState,
			null,
			FetchUserCalendarSuccessfulAction
		>
	>;
	resetUserCalendar: ActionCreator<
		ThunkAction<
			ResetUserCalendarsAction,
			GlobalState,
			null,
			ResetUserCalendarsAction
		>
	>;
	connectCalendar: ActionCreator<
		ThunkAction<
			Promise<ConnectCalendarSuccessfulAction>,
			GlobalState,
			ConnectCalendarPostRequest,
			ConnectCalendarSuccessfulAction
		>
	>;
	refreshIntegrations: ActionCreator<
		ThunkAction<
			Promise<FetchCompanyIntegrationsSuccessfulAction>,
			GlobalState,
			null,
			FetchCompanyIntegrationsSuccessfulAction
		>
	>;
	getCharbeeInvoicesList: ActionCreator<
		ThunkAction<
			Promise<ChargebeeInvoiceListSuccessfulAction>,
			GlobalState,
			null,
			ChargebeeInvoiceListSuccessfulAction
		>
	>;
	getCharbeeInvoiceDetails: ActionCreator<
		ThunkAction<
			Promise<ChargebeeInvoiceDetailsSuccessfulAction>,
			GlobalState,
			string,
			ChargebeeInvoiceDetailsSuccessfulAction
		>
	>;
	getChargebeeCreditNotesList: ActionCreator<
		ThunkAction<
			Promise<ChargebeeCreditNotesListSuccessfulAction>,
			GlobalState,
			null,
			ChargebeeCreditNotesListSuccessfulAction
		>
	>;
	getChargebeeCreditNoteDetails: ActionCreator<
		ThunkAction<
			Promise<ChargebeeCreditNoteDetailsSuccessfulAction>,
			GlobalState,
			string,
			ChargebeeCreditNoteDetailsSuccessfulAction
		>
	>;
	pauseCharbeeSubscription: ActionCreator<
		ThunkAction<
			Promise<PauseChargebeeSubscriptionSuccessfulAction>,
			GlobalState,
			PauseChargebeeSubscriptionRequest,
			PauseChargebeeSubscriptionSuccessfulAction
		>
	>;
	verifyUserEmail: ActionCreator<
		ThunkAction<
			Promise<VerifyUserEmail>,
			GlobalState,
			unknown,
			VerifyUserEmail
		>
	>;
	deleteCustomEmail: ActionCreator<
		ThunkAction<
			Promise<DeleteCustomEmail>,
			GlobalState,
			string,
			DeleteCustomEmail
		>
	>;
	addCustomDomain: ActionCreator<
		ThunkAction<
			Promise<AddCustomDomain>,
			GlobalState,
			string,
			AddCustomDomain
		>
	>;
	resendDomainVerification: ActionCreator<
		ThunkAction<
			Promise<ResendDomainVerificationSuccessful>,
			GlobalState,
			string,
			ResendDomainVerificationSuccessful
		>
	>;
	deleteCustomDomain: ActionCreator<
		ThunkAction<
			Promise<DeleteCustomDomain>,
			GlobalState,
			string,
			DeleteCustomDomain
		>
	>;
	updateEmailPreferences: ActionCreator<
		ThunkAction<
			Promise<UpdateEmailPreferencesSuccessful>,
			GlobalState,
			EmailIntegration,
			UpdateEmailPreferencesSuccessful
		>
	>;
	fetchAllEmailSignatures: ActionCreator<
		ThunkAction<
			Promise<FetchAllEmailSignaturesSuccessful>,
			GlobalState,
			null,
			FetchAllEmailSignaturesSuccessful
		>
	>;
	updateEmailSignatures: ActionCreator<
		ThunkAction<
			Promise<UpdateEmailSignaturesSuccessful>,
			GlobalState,
			UserEmailSignature,
			UpdateEmailSignaturesSuccessful
		>
	>;
	resetUserState: ActionCreator<
		ThunkAction<
			ResetUserStateAction,
			GlobalState,
			null,
			ResetUserStateAction
		>
	>;
	onboardingCallScheduled: ActionCreator<
		ThunkAction<
			Promise<OnboardingCallScheduledSuccessfulAction>,
			GlobalState,
			null,
			OnboardingCallScheduledSuccessfulAction
		>
	>;
	addDickerDataIntegration: ActionCreator<
		ThunkAction<
			Promise<AddDickerDataIntegrationSuccessfulAction>,
			GlobalState,
			DickerDataCreateOrUpdateRequest,
			AddDickerDataIntegrationSuccessfulAction
		>
	>;
	deleteDickerDataIntegration: ActionCreator<
		ThunkAction<
			Promise<DeleteDickerDataIntegrationSuccessfulAction>,
			GlobalState,
			null,
			DeleteDickerDataIntegrationSuccessfulAction
		>
	>;
	addWestcoastIntegration: ActionCreator<
		ThunkAction<
			Promise<AddWestcoastIntegrationSuccessfulAction>,
			GlobalState,
			AddWestcoastIntegrationRequest,
			AddWestcoastIntegrationSuccessfulAction
		>
	>;
	deleteWestcoastIntegration: ActionCreator<
		ThunkAction<
			Promise<DeleteWestcoastIntegrationSuccessfulAction>,
			GlobalState,
			null,
			DeleteWestcoastIntegrationSuccessfulAction
		>
	>;
	deleteCompanyRepairShoprIntegration: ActionCreator<
		ThunkAction<
			Promise<DeleteCompanyRepairShoprIntegrationSuccessfulAction>,
			GlobalState,
			null,
			DeleteCompanyRepairShoprIntegrationSuccessfulAction
		>
	>;
	addCompanyRepairShoprIntegration: ActionCreator<
		ThunkAction<
			Promise<AddCompanyRepairShoprIntegrationSuccessfulAction>,
			GlobalState,
			AddRepairShoprIntegrationRequest,
			AddCompanyRepairShoprIntegrationSuccessfulAction
		>
	>;
	getCompanyHubspotIntegrationMetadata: ActionCreator<
		ThunkAction<
			Promise<GetCompanyHubspotIntegrationMetadataSuccessfulAction>,
			GlobalState,
			null,
			GetCompanyHubspotIntegrationMetadataSuccessfulAction
		>
	>;
	addCompanyHubspotIntegration: ActionCreator<
		ThunkAction<
			Promise<AddCompanyHubspotIntegrationSuccessfulAction>,
			GlobalState,
			AddHubspotIntegrationRequest,
			AddCompanyHubspotIntegrationSuccessfulAction
		>
	>;
	deleteCompanyHubspotIntegration: ActionCreator<
		ThunkAction<
			Promise<DeleteCompanyHubspotIntegrationSuccessfulAction>,
			GlobalState,
			null,
			DeleteCompanyHubspotIntegrationSuccessfulAction
		>
	>;
	addStripeConnection: ActionCreator<
		ThunkAction<
			Promise<string>,
			GlobalState,
			null,
			AddStripeConnectionSuccessfulAction
		>
	>;
	deleteStripeConnection: ActionCreator<
		ThunkAction<
			Promise<DeleteStripeConnectionSuccessfulAction>,
			GlobalState,
			null,
			DeleteStripeConnectionSuccessfulAction
		>
	>;
	deletePaymentsConnection: ActionCreator<
		ThunkAction<
			Promise<void>,
			GlobalState,
			null,
			DeletePaymentsConnectionSuccessfulAction
		>
	>;
	getQuickbooksIntegrationMetadata: ActionCreator<
		ThunkAction<
			Promise<GetQuickbooksIntegrationMetadataSuccessfulAction>,
			GlobalState,
			null,
			GetQuickbooksIntegrationMetadataSuccessfulAction
		>
	>;
	addQuickbooksIntegration: ActionCreator<
		ThunkAction<
			Promise<AddQuickbooksIntegrationSuccessfulAction>,
			GlobalState,
			AddQuickbooksIntegrationRequest,
			AddQuickbooksIntegrationSuccessfulAction
		>
	>;
	deleteQuickbooksIntegration: ActionCreator<
		ThunkAction<
			Promise<DeleteQuickbooksIntegrationSuccessfulAction>,
			GlobalState,
			null,
			DeleteQuickbooksIntegrationSuccessfulAction
		>
	>;
	testHaloPSACredentials: ActionCreator<
		ThunkAction<
			Promise<TestHaloPSACredentialsSuccessfulAction>,
			GlobalState,
			HaloPSAConnectFormValue,
			TestHaloPSACredentialsSuccessfulAction
		>
	>;
	fetchHaloPSAMetadata: ActionCreator<
		ThunkAction<
			Promise<FetchHaloPSAMetadataSuccessfulAction>,
			GlobalState,
			HaloPSAAuthDetails,
			FetchHaloPSAMetadataSuccessfulAction
		>
	>;
	importHaloPSATaxInfo: ActionCreator<
		ThunkAction<
			Promise<ImportHaloTaxInfoSuccessfulAction>,
			GlobalState,
			{
				taxRegionId: string;
				deactivateExisting: boolean;
				authDetails: HaloPSAAuthDetails;
			},
			ImportHaloTaxInfoSuccessfulAction
		>
	>;
	addHaloIntegration: ActionCreator<
		ThunkAction<
			Promise<AddHaloIntegrationSuccessfulAction>,
			GlobalState,
			{
				payload: HaloIntegrationRequest;
			},
			AddHaloIntegrationSuccessfulAction
		>
	>;
	deleteHaloPSAIntegration: ActionCreator<
		ThunkAction<
			Promise<DeleteHaloPSAIntegrationSuccessfulAction>,
			GlobalState,
			null,
			DeleteHaloPSAIntegrationSuccessfulAction
		>
	>;
	fetchHaloPSAAuthDetails: ActionCreator<
		ThunkAction<
			Promise<FetchHaloPSAAuthDetailsSuccessfulAction>,
			GlobalState,
			null,
			FetchHaloPSAAuthDetailsSuccessfulAction
		>
	>;
	addCustomDistributor: ActionCreator<
		ThunkAction<
			Promise<AddCustomDistributorSuccessfulAction>,
			GlobalState,
			null,
			AddCustomDistributorSuccessfulAction
		>
	>;
	deleteCustomDistributor: ActionCreator<
		ThunkAction<
			Promise<DeleteCustomDistributorSuccessfulAction>,
			GlobalState,
			null,
			DeleteCustomDistributorSuccessfulAction
		>
	>;
	connectDatekDistributor: ActionCreator<
		ThunkAction<
			Promise<ConnectDatekDistributorSuccessfulAction>,
			GlobalState,
			{ supplier_id: string },
			ConnectDatekDistributorSuccessfulAction
		>
	>;
	editCompanyPreferences: ActionCreator<
		ThunkAction<
			Promise<EditCompanyPreferencesSuccessfulAction>,
			GlobalState,
			null,
			EditCompanyPreferencesSuccessfulAction
		>
	>;
	subscriptionPlanEstimateReset: ActionCreator<
		ThunkAction<
			SubscriptionPlanEstimateResetAction,
			GlobalState,
			null,
			SubscriptionPlanEstimateResetAction
		>
	>;
	updateTranslateLanguage: ActionCreator<
		ThunkAction<
			Promise<UpdateTranslateLanguageSuccessfulAction>,
			GlobalState,
			string,
			UpdateTranslateLanguageSuccessfulAction
		>
	>;

	updateInitialSyncStatus: ActionCreator<
		ThunkAction<
			Promise<UpdateInitialSyncStatusAction>,
			GlobalState,
			{
				verb: NotificationVerb;
			},
			UpdateInitialSyncStatusAction
		>
	>;

	//Favourite Integrations
	updateIntegrationPageDetails: ActionCreator<
		ThunkAction<
			Promise<UpdateIntegrationPageDetailsAction>,
			GlobalState,
			{
				integrationPageDetailsUpdateRequest: IntegrationPageDetailsUpdateRequest;
			},
			UpdateIntegrationPageDetailsAction
		>
	>;

	// AI Writer
	updateAIWriterTrialInfo: ActionCreator<
		ThunkAction<
			AIWriterInfoUpdateToCompanyDataAction,
			GlobalState,
			TrialInfoAIWriter,
			AIWriterInfoUpdateToCompanyDataAction
		>
	>;

	//Amazon Distributor
	deleteAmazonDistributorIntegration: ActionCreator<
		ThunkAction<
			Promise<DeleteAmazonDistributorSuccessfulAction>,
			GlobalState,
			null,
			DeleteAmazonDistributorSuccessfulAction
		>
	>;
	getAmazonMetadata: ActionCreator<
		ThunkAction<
			Promise<GetAmazonMetadataSuccessfulAction>,
			GlobalState,
			null,
			GetAmazonMetadataSuccessfulAction
		>
	>;
	updateAmazonProductAppliedFilter: ActionCreator<
		ThunkAction<
			Promise<UpdateAmazonProductAppliedFilterAction>,
			GlobalState,
			{
				amazonAppliedFilters: AmazonProductAppliedFilters;
			},
			UpdateAmazonProductAppliedFilterAction
		>
	>;
}

export const UsersActionCreators: UserDispatch = {
	showUserLoader: () => {
		return (dispatch: Dispatch): ShowUserLoaderAction => {
			const showClientsLoaderAction: ShowUserLoaderAction = {
				type: UsersActionTypes.SHOW_USER_LOADER
			};
			return dispatch(showClientsLoaderAction);
		};
	},
	hideUserLoader: () => {
		return (dispatch: Dispatch): HideUserLoaderAction => {
			const hideClientsLoaderAction: HideUserLoaderAction = {
				type: UsersActionTypes.HIDE_USER_LOADER
			};
			return dispatch(hideClientsLoaderAction);
		};
	},
	showLibraryLoader: () => {
		return (dispatch: Dispatch): ShowLibraryLoaderAction => {
			const showLibraryLoaderAction: ShowLibraryLoaderAction = {
				type: UsersActionTypes.SHOW_LIBRARY_LOADER
			};
			return dispatch(showLibraryLoaderAction);
		};
	},
	hideLibraryLoader: () => {
		return (dispatch: Dispatch): HideLibraryLoaderAction => {
			const hideLibraryLoaderAction: HideLibraryLoaderAction = {
				type: UsersActionTypes.HIDE_LIBRARY_LOADER
			};
			return dispatch(hideLibraryLoaderAction);
		};
	},
	showSubscriptionDetailsLoader: () => {
		return (dispatch: Dispatch): ShowSubcriptionDetailsLoaderAction => {
			const showLibraryLoaderAction: ShowSubcriptionDetailsLoaderAction =
				{
					type: UsersActionTypes.SHOW_SUBSCRIPTION_DETAILS_LOADER
				};
			return dispatch(showLibraryLoaderAction);
		};
	},
	hideSubscriptionDetailsLoader: () => {
		return (dispatch: Dispatch): HideSubscriptionDetailsLoaderAction => {
			const hideLibraryLoaderAction: HideSubscriptionDetailsLoaderAction =
				{
					type: UsersActionTypes.HIDE_SUBSCRIPTION_DETAILS_LOADER
				};
			return dispatch(hideLibraryLoaderAction);
		};
	},
	getUserData: () => {
		return async (
			dispatch: Dispatch
		): Promise<UserDataSuccessfulAction> => {
			const userDataRequestedAction: UserDataRequestedAction = {
				type: UsersActionTypes.USER_DATA_REQUESTED
			};
			dispatch(userDataRequestedAction);
			try {
				const userDataResponse = await getUserData();
				const userDataSuccessfulAction: UserDataSuccessfulAction = {
					type: UsersActionTypes.USER_DATA_SUCCESSFUL,
					userData: userDataResponse
				};
				return dispatch(userDataSuccessfulAction);
			} catch (error) {
				const userDataFailedAction: UserDataFailedAction = {
					type: UsersActionTypes.USER_DATA_FAILED
				};
				dispatch(userDataFailedAction);

				return Promise.reject(error);
			}
		};
	},
	updateUserData: (updateUserDataRequest: UserData) => {
		return (dispatch: Dispatch): UserDataUpdatedAction => {
			const userDataUpdatedAction: UserDataUpdatedAction = {
				type: UsersActionTypes.USER_DATA_UPDATED,
				userData: updateUserDataRequest
			};
			return dispatch(userDataUpdatedAction);
		};
	},
	editUserData: (editUserDataRequest: UserData) => {
		return async (
			dispatch: Dispatch
		): Promise<EditUserDataSuccessfulAction> => {
			const editUserDataRequestedAction: EditUserDataRequestedAction = {
				type: UsersActionTypes.EDIT_USER_DATA_REQUESTED
			};
			dispatch(editUserDataRequestedAction);

			try {
				const editUserDataResponse = await editUserData(
					editUserDataRequest
				);
				const editUserDataSuccessfulAction: EditUserDataSuccessfulAction =
					{
						type: UsersActionTypes.EDIT_USER_DATA_SUCCESSFUL,
						userData: editUserDataResponse
					};
				return dispatch(editUserDataSuccessfulAction);
			} catch (error) {
				const editUserDataFailedAction: EditUserDataFailedAction = {
					type: UsersActionTypes.EDIT_USER_DATA_FAILED
				};
				dispatch(editUserDataFailedAction);

				return Promise.reject(error);
			}
		};
	},
	getConfigurationSettings: (
		getConfigurationRequest?: GetConfigurationRequest
	) => {
		return async (
			dispatch: Dispatch
		): Promise<ConfigurationSettingsSuccessfulAction> => {
			const configurationSettingsRequestedAction: ConfigurationSettingsRequestedAction =
				{
					type: UsersActionTypes.CONFIGURATION_SETTINGS_REQUESTED
				};
			dispatch(configurationSettingsRequestedAction);
			try {
				const configurationSettingsResponse =
					await getConfigurationSettings(getConfigurationRequest);
				const configurationSettingsSuccessfulAction: ConfigurationSettingsSuccessfulAction =
					{
						type: UsersActionTypes.CONFIGURATION_SETTINGS_SUCCESSFUL,
						configurationSettings: configurationSettingsResponse
					};
				return dispatch(configurationSettingsSuccessfulAction);
			} catch (error) {
				const configurationSettingsFailedAction: ConfigurationSettingsFailedAction =
					{
						type: UsersActionTypes.CONFIGURATION_SETTINGS_FAILED
					};
				dispatch(configurationSettingsFailedAction);

				return Promise.reject(error);
			}
		};
	},
	getInitialConfig: () => {
		return async (
			dispatch: Dispatch
		): Promise<ConfigurationSettingsSuccessfulAction> => {
			const configurationSettingsRequestedAction: ConfigurationSettingsRequestedAction =
				{
					type: UsersActionTypes.CONFIGURATION_SETTINGS_REQUESTED
				};
			dispatch(configurationSettingsRequestedAction);
			try {
				const configurationSettingsResponse =
					await getConfigurationSettings({
						included_child_entities: [
							ChildEntityHydrationParams.USERS_ALL,
							ChildEntityHydrationParams.COMPANY_FULL,
							ChildEntityHydrationParams.CURRENT_USER
						]
					});

				const configurationSettingsSuccessfulAction: ConfigurationSettingsSuccessfulAction =
					{
						type: UsersActionTypes.CONFIGURATION_SETTINGS_SUCCESSFUL,
						configurationSettings: configurationSettingsResponse
					};
				dispatch(configurationSettingsSuccessfulAction);

				try {
					if (configurationSettingsResponse.current_user) {
						const userDataSuccessfulAction: UserDataSuccessfulAction =
							{
								type: UsersActionTypes.USER_DATA_SUCCESSFUL,
								userData:
									configurationSettingsResponse.current_user
							};
						dispatch(userDataSuccessfulAction);
					}
				} catch (hydrationError) {
					console.error(
						"Failed to hydrate current_user from config response",
						hydrationError
					);
				}

				try {
					if (configurationSettingsResponse.company) {
						const { currency, locale } =
							configurationSettingsResponse.company.settings ??
							{};
						try {
							if (currency) {
								cache.save(
									ZomentumStorageKeys.Currency,
									currency
								);
							}
							if (locale) {
								cache.save(ZomentumStorageKeys.Locale, locale);
							}
						} catch (cacheError) {
							console.error(
								"Failed to save currency/locale to cache",
								cacheError
							);
						}

						const userCompanySuccessfulAction: UserCompanySuccessfulAction =
							{
								type: UsersActionTypes.USER_COMPANY_SUCCESSFUL,
								userCompany:
									configurationSettingsResponse.company
							};
						dispatch(userCompanySuccessfulAction);
					}
				} catch (hydrationError) {
					console.error(
						"Failed to hydrate company from config response",
						hydrationError
					);
				}

				try {
					if (configurationSettingsResponse.users) {
						const companyUsersSuccessfulAction: CompanyUsersSuccessfulAction =
							{
								type: UsersActionTypes.COMPANY_USERS_SUCCESSFUL,
								companyUserList:
									configurationSettingsResponse.users?.data ??
									[],
								companyUserCount: {
									all:
										configurationSettingsResponse.users
											?.data?.length ?? 0
								}
							};
						dispatch(companyUsersSuccessfulAction);
					}
				} catch (hydrationError) {
					console.error(
						"Failed to hydrate users from config response",
						hydrationError
					);
				}

				return configurationSettingsSuccessfulAction;
			} catch (error) {
				const configurationSettingsFailedAction: ConfigurationSettingsFailedAction =
					{
						type: UsersActionTypes.CONFIGURATION_SETTINGS_FAILED
					};
				dispatch(configurationSettingsFailedAction);
				return Promise.reject(error);
			}
		};
	},
	bootstrapAuthenticatedSession: () => {
		return async (
			dispatch: ThunkDispatch<GlobalState, any, Action>,
			getState: () => GlobalState
		): Promise<ConfigurationSettingsSuccessfulAction> => {
			const configAction = await dispatch(
				UsersActionCreators.getInitialConfig()
			);
			const { current_user, company, users } =
				configAction.configurationSettings ?? {};
			const state = getState();
			const missingSlices: string[] = [];
			const hasStateUserList = Array.isArray(state.user.companyUserList);
			const stateUserIdSet = hasStateUserList
				? new Set(state.user.companyUserList.map(user => user.id))
				: new Set<string>();
			const responseUsersData =
				users && Array.isArray(users.data) ? users.data : null;
			const responseUserIds =
				responseUsersData?.map(user => user.id) ?? [];

			if (
				!current_user?.id ||
				state.user.userData?.id !== current_user.id
			) {
				missingSlices.push("current_user");
			}
			const stateCompany = state.user.userCompany;
			const companyMatches =
				!!company?.id &&
				stateCompany?.id === company.id &&
				stateCompany?.name === company.name &&
				stateCompany?.domain === company.domain &&
				stateCompany?.favicon === company.favicon &&
				stateCompany?.logo === company.logo;
			if (!companyMatches) {
				missingSlices.push("company");
			}
			if (
				!responseUsersData ||
				!hasStateUserList ||
				state.user.companyUserList.length !== responseUserIds.length ||
				responseUserIds.some(id => !stateUserIdSet.has(id))
			) {
				missingSlices.push("users");
			}

			if (missingSlices.length > 0) {
				return Promise.reject(
					new Error(
						`Authenticated session bootstrap incomplete: missing ${missingSlices.join(
							", "
						)}`
					)
				);
			}

			// Flip the readiness flag AFTER all required slices are populated.
			// Dashboard reads this to skip the duplicate getInitialConfig fetch
			// on routes downstream of /authorize. Must NOT fire on the
			// rejection branch above — a failed bootstrap leaves the flag
			// false so Dashboard's own fallback getInitialConfig runs.
			dispatch(LoginActionCreators.sessionInitialized());

			return configAction;
		};
	},
	userCompanyConfigUpdate: (
		userCompanyConfigUpdateRequest: UserCompanyConfigUpdateRequest
	) => {
		return async (
			dispatch: Dispatch
		): Promise<UserCompanyConfigUpdateSuccessfulAction> => {
			const userCompanyConfigUpdateRequestedAction: UserCompanyConfigUpdateRequestedAction =
				{
					type: UsersActionTypes.USER_COMPANY_CONFIG_UPDATE_REQUESTED
				};
			dispatch(userCompanyConfigUpdateRequestedAction);
			try {
				const response = await userCompanyConfigUpdate(
					userCompanyConfigUpdateRequest
				);
				const userCompanyConfigUpdateSuccessfulAction: UserCompanyConfigUpdateSuccessfulAction =
					{
						type: UsersActionTypes.USER_COMPANY_CONFIG_UPDATE_SUCCESSFUL,
						configurationSettings: response
					};
				return dispatch(userCompanyConfigUpdateSuccessfulAction);
			} catch (error) {
				const userCompanyConfigUpdateFailedAction: UserCompanyConfigUpdateFailedAction =
					{
						type: UsersActionTypes.USER_COMPANY_CONFIG_UPDATE_FAILED
					};
				dispatch(userCompanyConfigUpdateFailedAction);
				return Promise.reject(error);
			}
		};
	},
	updateCompanyData: (updateCompanyDataRequest: UserCompany) => {
		return (dispatch: Dispatch): CompanyDataUpdatedAction => {
			const companyDataUpdatedAction: CompanyDataUpdatedAction = {
				type: UsersActionTypes.COMPANY_DATA_UPDATED,
				userCompany: updateCompanyDataRequest
			};
			return dispatch(companyDataUpdatedAction);
		};
	},
	userRequestJoinCompany: (
		companyId: string,
		joinCompanyRequest: GetUserCompanyRequest
	) => {
		return async (
			dispatch: Dispatch
		): Promise<UserRequestJoinCompanySuccessfulAction> => {
			const userRequestJoinCompanyRequestedAction: UserRequestJoinCompanyRequestedAction =
				{
					type: UsersActionTypes.USERREQUEST_JOINCOMPANY_REQUESTED
				};
			dispatch(userRequestJoinCompanyRequestedAction);
			try {
				const configurationSettingsResponse =
					await userRequestJoinCompany(companyId, joinCompanyRequest);
				const userRequestJoinCompanySuccessfulAction: UserRequestJoinCompanySuccessfulAction =
					{
						type: UsersActionTypes.USERREQUEST_JOINCOMPANY_SUCCESSFUL,
						configurationSettings: configurationSettingsResponse
					};
				return dispatch(userRequestJoinCompanySuccessfulAction);
			} catch (error) {
				const userRequestJoinCompanyFailedAction: UserRequestJoinCompanyFailedAction =
					{
						type: UsersActionTypes.USERREQUEST_JOINCOMPANY_FAILED
					};
				dispatch(userRequestJoinCompanyFailedAction);

				return Promise.reject(error);
			}
		};
	},
	userRequestWithdrawCompany: (
		companyId: string,
		withdrawCompanyRequest: GetUserCompanyRequest
	) => {
		return async (
			dispatch: Dispatch
		): Promise<UserRequestWithdrawCompanySuccessfulAction> => {
			const userRequestWithdrawCompanyRequestedAction: UserRequestWithdrawCompanyRequestedAction =
				{
					type: UsersActionTypes.USERREQUEST_WITHDRAWCOMPANY_REQUESTED
				};
			dispatch(userRequestWithdrawCompanyRequestedAction);
			try {
				const configurationSettingsResponse =
					await userRequestWithdrawCompany(
						companyId,
						withdrawCompanyRequest
					);
				const userRequestWithdrawCompanySuccessfulAction: UserRequestWithdrawCompanySuccessfulAction =
					{
						type: UsersActionTypes.USERREQUEST_WITHDRAWCOMPANY_SUCCESSFUL,
						configurationSettings: configurationSettingsResponse
					};
				return dispatch(userRequestWithdrawCompanySuccessfulAction);
			} catch (error) {
				const userRequestWithdrawCompanyFailedAction: UserRequestWithdrawCompanyFailedAction =
					{
						type: UsersActionTypes.USERREQUEST_WITHDRAWCOMPANY_FAILED
					};
				dispatch(userRequestWithdrawCompanyFailedAction);

				return Promise.reject(error);
			}
		};
	},
	setTwoFactorAuth:
		(is2fa_enabled: boolean) =>
		async (
			dispatch: Dispatch
		): Promise<SetTwoFactorAuthSuccessfulAction> => {
			try {
				const setTwoFactorAuthRequestedAction: SetTwoFactorAuthRequestedAction =
					{
						type: UsersActionTypes.SET_TWO_FACTOR_AUTH_REQUESTED
					};
				dispatch(setTwoFactorAuthRequestedAction);

				const setTwoFactorAuthResponse = await setTwoFactorAuth(
					is2fa_enabled
				);
				const setTwoFactorAuthSuccessfulAction: SetTwoFactorAuthSuccessfulAction =
					{
						type: UsersActionTypes.SET_TWO_FACTOR_AUTH_SUCCESSFUL,
						userCompany: setTwoFactorAuthResponse
					};
				return dispatch(setTwoFactorAuthSuccessfulAction);
			} catch (error) {
				console.error(error);
				const setTwoFactorAuthFailedAction: SetTwoFactorAuthFailedAction =
					{
						type: UsersActionTypes.SET_TWO_FACTOR_AUTH_FAILED
					};
				dispatch(setTwoFactorAuthFailedAction);
				throw error;
			}
		},
	setSsoMandatory:
		(isSsoMandatory: boolean) =>
		async (
			dispatch: Dispatch
		): Promise<SetSsoMandatorySuccessfulAction> => {
			try {
				const setSsoMandatoryRequestedAction: SetSsoMandatoryRequestedAction =
					{
						type: UsersActionTypes.SET_SSO_MANDATORY_REQUESTED
					};
				dispatch(setSsoMandatoryRequestedAction);
				const setSsoMandatoryResponse = await setSsoMandatory(
					isSsoMandatory
				);
				const ssoSsoMandatorySuccessfulAction: SetSsoMandatorySuccessfulAction =
					{
						type: UsersActionTypes.SET_SSO_MANDATORY_SUCCESSFUL,
						userCompany: setSsoMandatoryResponse
					};
				return dispatch(ssoSsoMandatorySuccessfulAction);
			} catch (error) {
				console.error(error);
				const setSsoMandatoryFailedAction: SetSsoMandatoryFailedAction =
					{
						type: UsersActionTypes.SET_SSO_MANDATORY_FAILED
					};
				dispatch(setSsoMandatoryFailedAction);
				throw error;
			}
		},
	setSsoEnable:
		(isSSOEnabled: boolean) =>
		async (dispatch: Dispatch): Promise<SetSsoEnableSuccessfulAction> => {
			try {
				const setSsoEnableRequestedAction: SetSsoEnableRequestedAction =
					{
						type: UsersActionTypes.SET_SSO_ENABLE_REQUESTED
					};
				dispatch(setSsoEnableRequestedAction);
				const setSsoEnableResponse = await setSsoEnable(isSSOEnabled);
				const ssoSsoEnableSuccessfulAction: SetSsoEnableSuccessfulAction =
					{
						type: UsersActionTypes.SET_SSO_ENABLE_SUCCESSFUL,
						userCompany: setSsoEnableResponse
					};
				return dispatch(ssoSsoEnableSuccessfulAction);
			} catch (error) {
				console.error(error);
				const setSsoEnableFailedAction: SetSsoEnableFailedAction = {
					type: UsersActionTypes.SET_SSO_ENABLE_FAILED
				};
				dispatch(setSsoEnableFailedAction);
				throw error;
			}
		},
	getUserCompany: (includeUsers = true) => {
		return async (
			dispatch: Dispatch
		): Promise<UserCompanySuccessfulAction> => {
			const userCompanyRequestedAction: UserCompanyRequestedAction = {
				type: UsersActionTypes.USER_COMPANY_REQUESTED
			};
			dispatch(userCompanyRequestedAction);
			try {
				let getUserCompanyRequest: GetUserCompanyRequest | null = null;
				if (includeUsers) {
					getUserCompanyRequest = {
						included_child_entities:
							new Array<ChildEntityHydrationParams>(
								ChildEntityHydrationParams.USERS_ALL
							)
					};
				}
				const userCompanyResponse = await getUserCompany(
					getUserCompanyRequest
				);

				// saving currency in the localStorage
				const { currency, locale } = userCompanyResponse?.settings;
				if (currency) {
					cache.save(ZomentumStorageKeys.Currency, currency);
				}
				if (locale) {
					cache.save(ZomentumStorageKeys.Locale, locale);
				}

				const userCompanySuccessfulAction: UserCompanySuccessfulAction =
					{
						type: UsersActionTypes.USER_COMPANY_SUCCESSFUL,
						userCompany: userCompanyResponse
					};

				if (includeUsers) {
					const companyUsersSuccessfulAction: CompanyUsersSuccessfulAction =
						{
							type: UsersActionTypes.COMPANY_USERS_SUCCESSFUL,
							companyUserList:
								userCompanyResponse.users?.data ?? [],
							companyUserCount: {
								all: userCompanyResponse.users?.data.length ?? 0
							}
						};
					dispatch(companyUsersSuccessfulAction);
				}
				return dispatch(userCompanySuccessfulAction);
			} catch (error) {
				const userCompanyFailedAction: UserCompanyFailedAction = {
					type: UsersActionTypes.USER_COMPANY_FAILED
				};
				dispatch(userCompanyFailedAction);

				return Promise.reject(error);
			}
		};
	},
	editUserCompany: (editUserCompanyRequest: UserCompany) => {
		return async (
			dispatch: Dispatch
		): Promise<EditUserCompanySuccessfulAction> => {
			const editUserCompanyRequestedAction: EditUserCompanyRequestedAction =
				{
					type: UsersActionTypes.EDIT_USER_COMPANY_REQUESTED
				};
			dispatch(editUserCompanyRequestedAction);
			try {
				const editUserCompanyResponse = await editUserCompany(
					editUserCompanyRequest
				);
				const editUserCompanySuccessfulAction: EditUserCompanySuccessfulAction =
					{
						type: UsersActionTypes.EDIT_USER_COMPANY_SUCCESSFUL,
						editUserCompany: editUserCompanyResponse
					};
				return dispatch(editUserCompanySuccessfulAction);
			} catch (error) {
				const editUserCompanyFailedAction: EditUserCompanyFailedAction =
					{
						type: UsersActionTypes.EDIT_USER_COMPANY_FAILED
					};
				dispatch(editUserCompanyFailedAction);

				return Promise.reject(error);
			}
		};
	},
	editUserPassword: (password: string) => {
		return async (
			dispatch: Dispatch
		): Promise<EditUserPasswordSuccessfulAction> => {
			try {
				const editUserPasswordRequestedAction: EditUserPasswordRequestedAction =
					{
						type: UsersActionTypes.EDIT_USER_PASSWORD_REQUESTED
					};
				dispatch(editUserPasswordRequestedAction);
				await editUserPassword(password);
				const editUserPasswordSuccessfulAction: EditUserPasswordSuccessfulAction =
					{
						type: UsersActionTypes.EDIT_USER_PASSWORD_SUCCESSFUL
					};
				return dispatch(editUserPasswordSuccessfulAction);
			} catch (error) {
				const editPasswordFailedAction: EditUserPasswordFailedAction = {
					type: UsersActionTypes.EDIT_USER_PASSWORD_FAILED
				};
				dispatch(editPasswordFailedAction);
				return Promise.reject(error);
			}
		};
	},
	changeCompanyLogo: (changeCompanyLogoRequest: ChangeCompanyLogoRequest) => {
		return async (
			dispatch: Dispatch
		): Promise<ChangeCompanyLogoSuccessfulAction> => {
			const changeCompanyLogoRequestedAction: ChangeCompanyLogoRequestedAction =
				{
					type: UsersActionTypes.CHANGE_COMPANY_LOGO_REQUESTED
				};
			dispatch(changeCompanyLogoRequestedAction);
			try {
				const changeCompanyLogoResponse = await changeCompanyLogo(
					changeCompanyLogoRequest
				);
				const changeCompanyLogoSuccessfulAction: ChangeCompanyLogoSuccessfulAction =
					{
						type: UsersActionTypes.CHANGE_COMPANY_LOGO_SUCCESSFUL,
						changeLogoUserCompany: changeCompanyLogoResponse
					};
				return dispatch(changeCompanyLogoSuccessfulAction);
			} catch (error) {
				const changeCompanyLogoFailedAction: ChangeCompanyLogoFailedAction =
					{
						type: UsersActionTypes.CHANGE_COMPANY_LOGO_FAILED
					};
				dispatch(changeCompanyLogoFailedAction);

				return Promise.reject(error);
			}
		};
	},
	getDatePatterns: () => {
		return async (
			dispatch: Dispatch
		): Promise<GetDatePatternsSuccessfulAction> => {
			const getDatePatternsRequestedAction: GetDatePatternsRequestedAction =
				{
					type: UsersActionTypes.GET_DATE_PATTERNS_REQUESTED
				};
			dispatch(getDatePatternsRequestedAction);
			try {
				const getDatePatternsResponse = await getDatePatterns();
				const getDatePatternsSuccessFulAction: GetDatePatternsSuccessfulAction =
					{
						type: UsersActionTypes.GET_DATE_PATTERNS_SUCCESSFUL,
						datePatterns:
							getDatePatternsResponse.data_with_display_value
					};
				return dispatch(getDatePatternsSuccessFulAction);
			} catch (error) {
				const getDatePatternsFailedAction: GetDatePatternsFailedAction =
					{
						type: UsersActionTypes.GET_DATE_PATTERNS_FAILED
					};
				dispatch(getDatePatternsFailedAction);

				return Promise.reject(error);
			}
		};
	},
	getCompanyUsers: (companyUsersRequest: CompanyUsersRequest) => {
		return async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<CompanyUsersSuccessfulAction> => {
			const companyUsersRequestedAction: CompanyUsersRequestedAction = {
				type: UsersActionTypes.COMPANY_USERS_REQUESTED
			};
			dispatch(companyUsersRequestedAction);
			try {
				const globalState = getState();
				if (
					globalState.user &&
					globalState.user.userCompany &&
					globalState.user.userCompany.id &&
					globalState.user.userCompany.id.length > 0
				) {
					const getCompanyUsersResponse = await getCompanyUsers(
						globalState.user.userCompany.id,
						companyUsersRequest
					);
					const companyUsersSuccessfulAction: CompanyUsersSuccessfulAction =
						{
							type: UsersActionTypes.COMPANY_USERS_SUCCESSFUL,
							companyUserList: getCompanyUsersResponse.data,
							companyUserCount:
								getCompanyUsersResponse.filtered_count_map
						};
					return dispatch(companyUsersSuccessfulAction);
				} else {
					const companyUsersFailedAction: CompanyUsersFailedAction = {
						type: UsersActionTypes.COMPANY_USERS_FAILED
					};
					dispatch(companyUsersFailedAction);
					return Promise.reject(DefaultErrorMessage);
				}
			} catch (error) {
				const companyUsersFailedAction: CompanyUsersFailedAction = {
					type: UsersActionTypes.COMPANY_USERS_FAILED
				};
				dispatch(companyUsersFailedAction);

				return Promise.reject(error);
			}
		};
	},
	deleteCompanyUser: (deleteCompanyUserRequest: UserData) => {
		return async (
			dispatch: Dispatch
		): Promise<DeleteCompanyUserSuccessfulAction> => {
			const deleteCompanyUserRequestedAction: DeleteCompanyUserRequestedAction =
				{
					type: UsersActionTypes.DELETE_COMPANY_USER_REQUESTED
				};
			dispatch(deleteCompanyUserRequestedAction);
			try {
				const deleteCompanyUserResponse = await deleteCompanyUser(
					deleteCompanyUserRequest
				);
				if (deleteCompanyUserResponse.deleted) {
					const deleteCompanyUserSuccessfulAction: DeleteCompanyUserSuccessfulAction =
						{
							type: UsersActionTypes.DELETE_COMPANY_USER_SUCCESSFUL,
							deleteCompanyUser: deleteCompanyUserRequest
						};
					return dispatch(deleteCompanyUserSuccessfulAction);
				} else {
					const deleteCompanyUserFailedAction: DeleteCompanyUserFailedAction =
						{
							type: UsersActionTypes.DELETE_COMPANY_USER_FAILED
						};
					dispatch(deleteCompanyUserFailedAction);
					return Promise.reject(DefaultErrorMessage);
				}
			} catch (error) {
				const deleteCompanyUserFailedAction: DeleteCompanyUserFailedAction =
					{
						type: UsersActionTypes.DELETE_COMPANY_USER_FAILED
					};
				dispatch(deleteCompanyUserFailedAction);

				return Promise.reject(error);
			}
		};
	},
	addCompanyUser: (addCompanyUserRequest: AddCompanyUserRequest) => {
		return async (
			dispatch: Dispatch
		): Promise<AddCompanyUserSuccessfulAction> => {
			const addCompanyUserRequestedAction: AddCompanyUserRequestedAction =
				{
					type: UsersActionTypes.ADD_COMPANY_USER_REQUESTED
				};
			dispatch(addCompanyUserRequestedAction);
			try {
				let addUserCompanyResponse: UserData;
				if (addCompanyUserRequest.role === UserRole.ApiUserRole) {
					addUserCompanyResponse = await addApiUser(
						addCompanyUserRequest
					);
				} else {
					addUserCompanyResponse = await addCompanyUser(
						addCompanyUserRequest
					);
				}
				const addCompanyUserSuccessfulAction: AddCompanyUserSuccessfulAction =
					{
						type: UsersActionTypes.ADD_COMPANY_USER_SUCCESSFUL,
						addCompanyUser: addUserCompanyResponse
					};
				return dispatch(addCompanyUserSuccessfulAction);
			} catch (error) {
				const addCompanyUserFailedAction: AddCompanyUserFailedAction = {
					type: UsersActionTypes.ADD_COMPANY_USER_FAILED
				};
				dispatch(addCompanyUserFailedAction);

				return Promise.reject(error);
			}
		};
	},
	editCompanyUser: (editCompanyUserRequest: UserData) => {
		return async (
			dispatch: Dispatch
		): Promise<EditCompanyUserSuccessfulAction> => {
			const editCompanyUserRequestedAction: EditCompanyUserRequestedAction =
				{
					type: UsersActionTypes.EDIT_COMPANY_USER_REQUESTED
				};
			dispatch(editCompanyUserRequestedAction);
			try {
				const editUserCompanyResponse = await editCompanyUser(
					editCompanyUserRequest
				);
				const editCompanyUserSuccessfulAction: EditCompanyUserSuccessfulAction =
					{
						type: UsersActionTypes.EDIT_COMPANY_USER_SUCCESSFUL,
						editCompanyUser: editUserCompanyResponse
					};
				return dispatch(editCompanyUserSuccessfulAction);
			} catch (error) {
				const editCompanyUserFailedAction: EditCompanyUserFailedAction =
					{
						type: UsersActionTypes.EDIT_COMPANY_USER_FAILED
					};
				dispatch(editCompanyUserFailedAction);

				return Promise.reject(error);
			}
		};
	},
	editCurrentCompanyUser: (editCompanyUserRequest: UserData) => {
		return async (
			dispatch: Dispatch
		): Promise<EditCurrentCompanyUserSuccessfulAction> => {
			const editCurrentCompanyUserRequestedAction: EditCurrentCompanyUserRequestedAction =
				{
					type: UsersActionTypes.EDIT_CURRENT_COMPANY_USER_REQUESTED
				};
			dispatch(editCurrentCompanyUserRequestedAction);
			try {
				const editUserCompanyResponse = await editCompanyUser(
					editCompanyUserRequest
				);
				const editCurrentCompanyUserSuccessfulAction: EditCurrentCompanyUserSuccessfulAction =
					{
						type: UsersActionTypes.EDIT_CURRENT_COMPANY_USER_SUCCESSFUL,
						editCompanyUser: editUserCompanyResponse
					};
				return dispatch(editCurrentCompanyUserSuccessfulAction);
			} catch (error) {
				const editCurrentCompanyUserFailedAction: EditCurrentCompanyUserFailedAction =
					{
						type: UsersActionTypes.EDIT_CURRENT_COMPANY_USER_FAILED
					};
				dispatch(editCurrentCompanyUserFailedAction);
				return Promise.reject(error);
			}
		};
	},
	updateCompanyUser: (updateCompanyUserRequest: UserData) => {
		return (dispatch: Dispatch): UpdateCompanyUserAction => {
			const editCompanyUserSuccessfulAction: UpdateCompanyUserAction = {
				type: UsersActionTypes.UPDATE_COMPANY_USER,
				updateCompanyUser: updateCompanyUserRequest
			};
			return dispatch(editCompanyUserSuccessfulAction);
		};
	},
	getTaxCategoriesList: () => {
		return async (
			dispatch: Dispatch
		): Promise<TaxCategoriesListSuccessfulAction> => {
			const taxCategoriesListRequestedAction: TaxCategoriesListRequestedAction =
				{
					type: UsersActionTypes.TAX_CATEGORIES_LIST_ACTION_REQUESTED
				};
			dispatch(taxCategoriesListRequestedAction);
			try {
				const taxCategoriesListResponse = await getTaxCategoriesList();
				const taxCategoriesListSuccessfulAction: TaxCategoriesListSuccessfulAction =
					{
						type: UsersActionTypes.TAX_CATEGORIES_LIST_ACTION_SUCCESSFUL,
						taxCategoriesList: taxCategoriesListResponse.data
					};
				return dispatch(taxCategoriesListSuccessfulAction);
			} catch (error) {
				const taxCategoriesListFailedAction: TaxCategoriesListFailedAction =
					{
						type: UsersActionTypes.TAX_CATEGORIES_LIST_ACTION_FAILED
					};
				dispatch(taxCategoriesListFailedAction);

				return Promise.reject(error);
			}
		};
	},
	getTaxRegionsList: () => {
		return async (
			dispatch: Dispatch
		): Promise<TaxRegionsListSuccessfulAction> => {
			const taxRegionsListRequestedAction: TaxRegionsListRequestedAction =
				{
					type: UsersActionTypes.TAX_REGIONS_LIST_ACTION_REQUESTED
				};
			dispatch(taxRegionsListRequestedAction);
			try {
				const taxRegionsListResponse = await getTaxRegionsList();
				const taxRegionsListSuccessfulAction: TaxRegionsListSuccessfulAction =
					{
						type: UsersActionTypes.TAX_REGIONS_LIST_ACTION_SUCCESSFUL,
						taxRegionsList: taxRegionsListResponse.data
					};
				return dispatch(taxRegionsListSuccessfulAction);
			} catch (error) {
				const taxRegionsListFailedAction: TaxRegionsListFailedAction = {
					type: UsersActionTypes.TAX_REGIONS_LIST_ACTION_FAILED
				};
				dispatch(taxRegionsListFailedAction);

				return Promise.reject(error);
			}
		};
	},
	addTaxRegion: (addTaxRegionRequest: AddTaxRegionRequest) => {
		return async (
			dispatch: Dispatch
		): Promise<AddTaxRegionSuccessfulAction> => {
			const addTaxRegionRequestedAction: AddTaxRegionRequestedAction = {
				type: UsersActionTypes.ADD_TAX_REGION_ACTION_REQUESTED
			};
			dispatch(addTaxRegionRequestedAction);
			try {
				const addTaxRegionResponse = await addTaxRegion(
					addTaxRegionRequest
				);
				const addTaxRegionSuccessfulAction: AddTaxRegionSuccessfulAction =
					{
						type: UsersActionTypes.ADD_TAX_REGION_ACTION_SUCCESSFUL,
						addTaxRegion: addTaxRegionResponse
					};
				return dispatch(addTaxRegionSuccessfulAction);
			} catch (error) {
				const addTaxRegionFailedAction: AddTaxRegionFailedAction = {
					type: UsersActionTypes.ADD_TAX_REGION_ACTION_FAILED
				};
				dispatch(addTaxRegionFailedAction);

				return Promise.reject(error);
			}
		};
	},
	editTaxRegion: (editTaxRegionRequest: TaxRegion) => {
		return async (
			dispatch: Dispatch
		): Promise<EditTaxRegionSuccessfulAction> => {
			const editTaxRegionRequestedAction: EditTaxRegionRequestedAction = {
				type: UsersActionTypes.EDIT_TAX_REGION_ACTION_REQUESTED
			};
			dispatch(editTaxRegionRequestedAction);
			try {
				const editTaxRegionResponse = await editTaxRegion(
					editTaxRegionRequest
				);
				const editTaxRegionSuccessfulAction: EditTaxRegionSuccessfulAction =
					{
						type: UsersActionTypes.EDIT_TAX_REGION_ACTION_SUCCESSFUL,
						editTaxRegion: editTaxRegionResponse
					};
				return dispatch(editTaxRegionSuccessfulAction);
			} catch (error) {
				const editTaxRegionFailedAction: EditTaxRegionFailedAction = {
					type: UsersActionTypes.EDIT_TAX_REGION_ACTION_FAILED
				};
				dispatch(editTaxRegionFailedAction);

				return Promise.reject(error);
			}
		};
	},
	addTaxCategory: (addTaxCategoryRequest: AddTaxCategoryRequest) => {
		return async (
			dispatch: Dispatch
		): Promise<AddTaxCategorySuccessfulAction> => {
			const addTaxCategoryRequestedAction: AddTaxCategoryRequestedAction =
				{
					type: UsersActionTypes.ADD_TAX_CATEGORY_ACTION_REQUESTED
				};
			dispatch(addTaxCategoryRequestedAction);
			try {
				const addTaxCategoryResponse = await addTaxCategory(
					addTaxCategoryRequest
				);
				const addTaxCategorySuccessfulAction: AddTaxCategorySuccessfulAction =
					{
						type: UsersActionTypes.ADD_TAX_CATEGORY_ACTION_SUCCESSFUL,
						addTaxCategory: addTaxCategoryResponse
					};
				return dispatch(addTaxCategorySuccessfulAction);
			} catch (error) {
				const addTaxCategoryFailedAction: AddTaxCategoryFailedAction = {
					type: UsersActionTypes.ADD_TAX_CATEGORY_ACTION_FAILED
				};
				dispatch(addTaxCategoryFailedAction);

				return Promise.reject(error);
			}
		};
	},
	editTaxCategory: (editTaxCategoryRequest: TaxCategory) => {
		return async (
			dispatch: Dispatch
		): Promise<EditTaxCategorySuccessfulAction> => {
			const editTaxCategoryRequestedAction: EditTaxCategoryRequestedAction =
				{
					type: UsersActionTypes.EDIT_TAX_CATEGORY_ACTION_REQUESTED
				};
			dispatch(editTaxCategoryRequestedAction);
			try {
				const editTaxCategoryResponse = await editTaxCategory(
					editTaxCategoryRequest
				);
				const editTaxCategorySuccessfulAction: EditTaxCategorySuccessfulAction =
					{
						type: UsersActionTypes.EDIT_TAX_CATEGORY_ACTION_SUCCESSFUL,
						editTaxCategory: editTaxCategoryResponse
					};
				return dispatch(editTaxCategorySuccessfulAction);
			} catch (error) {
				const editTaxCategoryFailedAction: EditTaxCategoryFailedAction =
					{
						type: UsersActionTypes.EDIT_TAX_CATEGORY_ACTION_FAILED
					};
				dispatch(editTaxCategoryFailedAction);

				return Promise.reject(error);
			}
		};
	},
	addTaxRegionDrawerOpened: () => {
		return (dispatch: Dispatch): AddTaxRegionDrawerOpenedAction => {
			const addTaxRegionDrawerOpenedAction: AddTaxRegionDrawerOpenedAction =
				{
					type: UsersActionTypes.ADD_TAX_REGION_DRAWER_OPENED
				};
			return dispatch(addTaxRegionDrawerOpenedAction);
		};
	},
	addTaxRegionDrawerClosed: () => {
		return (dispatch: Dispatch): AddTaxRegionDrawerClosedAction => {
			const addTaxRegionDrawerClosedAction: AddTaxRegionDrawerClosedAction =
				{
					type: UsersActionTypes.ADD_TAX_REGION_DRAWER_CLOSED
				};
			return dispatch(addTaxRegionDrawerClosedAction);
		};
	},
	editTaxRegionDrawerOpened: () => {
		return (dispatch: Dispatch): EditTaxRegionDrawerOpenedAction => {
			const editTaxRegionDrawerOpenedAction: EditTaxRegionDrawerOpenedAction =
				{
					type: UsersActionTypes.EDIT_TAX_REGION_DRAWER_OPENED
				};
			return dispatch(editTaxRegionDrawerOpenedAction);
		};
	},
	editTaxRegionDrawerClosed: () => {
		return (dispatch: Dispatch): EditTaxRegionDrawerClosedAction => {
			const editTaxRegionDrawerClosedAction: EditTaxRegionDrawerClosedAction =
				{
					type: UsersActionTypes.EDIT_TAX_REGION_DRAWER_CLOSED
				};
			return dispatch(editTaxRegionDrawerClosedAction);
		};
	},
	addTaxCategoryDrawerOpened: () => {
		return (dispatch: Dispatch): AddTaxCategoryDrawerOpenedAction => {
			const addTaxCategoryDrawerOpenedAction: AddTaxCategoryDrawerOpenedAction =
				{
					type: UsersActionTypes.ADD_TAX_CATEGORY_DRAWER_OPENED
				};
			return dispatch(addTaxCategoryDrawerOpenedAction);
		};
	},
	addTaxCategoryDrawerClosed: () => {
		return (dispatch: Dispatch): AddTaxCategoryDrawerClosedAction => {
			const addTaxCategoryDrawerClosedAction: AddTaxCategoryDrawerClosedAction =
				{
					type: UsersActionTypes.ADD_TAX_CATEGORY_DRAWER_CLOSED
				};
			return dispatch(addTaxCategoryDrawerClosedAction);
		};
	},
	editTaxCategoryDrawerOpened: () => {
		return (dispatch: Dispatch): EditTaxCategoryDrawerOpenedAction => {
			const editTaxCategoryDrawerOpenedAction: EditTaxCategoryDrawerOpenedAction =
				{
					type: UsersActionTypes.EDIT_TAX_CATEGORY_DRAWER_OPENED
				};
			return dispatch(editTaxCategoryDrawerOpenedAction);
		};
	},
	editTaxCategoryDrawerClosed: () => {
		return (dispatch: Dispatch): EditTaxCategoryDrawerClosedAction => {
			const editTaxCategoryDrawerClosedAction: EditTaxCategoryDrawerClosedAction =
				{
					type: UsersActionTypes.EDIT_TAX_CATEGORY_DRAWER_CLOSED
				};
			return dispatch(editTaxCategoryDrawerClosedAction);
		};
	},
	deleteCompanyEmailIntegration: () => {
		return async (
			dispatch: Dispatch
		): Promise<DeleteCompanyEmailIntegrationSuccessfulAction> => {
			const deleteCompanyEmailIntegrationRequestedAction: DeleteCompanyEmailIntegrationRequestedAction =
				{
					type: UsersActionTypes.DELETE_COMPANY_EMAIL_INTEGRATION_REQUESTED
				};
			dispatch(deleteCompanyEmailIntegrationRequestedAction);
			try {
				await deleteCompanyEmailIntegration();
				const deleteCompanyEmailIntegrationSuccessfulAction: DeleteCompanyEmailIntegrationSuccessfulAction =
					{
						type: UsersActionTypes.DELETE_COMPANY_EMAIL_INTEGRATION_SUCCESSFUL
					};
				return dispatch(deleteCompanyEmailIntegrationSuccessfulAction);
			} catch (error) {
				const deleteCompanyEmailIntegrationFailedAction: DeleteCompanyEmailIntegrationFailedAction =
					{
						type: UsersActionTypes.DELETE_COMPANY_EMAIL_INTEGRATION_FAILED
					};
				dispatch(deleteCompanyEmailIntegrationFailedAction);

				return Promise.reject(error);
			}
		};
	},
	addCompanyEmailIntegration: (
		addEmailIntegrationRequest: AddEmailIntegrationRequest
	) => {
		return async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<AddCompanyEmailIntegrationSuccessfulAction> => {
			const addCompanyEmailIntegrationRequestedAction: AddCompanyEmailIntegrationRequestedAction =
				{
					type: UsersActionTypes.ADD_COMPANY_EMAIL_INTEGRATION_REQUESTED
				};
			dispatch(addCompanyEmailIntegrationRequestedAction);
			try {
				const globalState = getState();
				if (
					globalState.user &&
					globalState.user.userData &&
					globalState.user.userData.id &&
					globalState.user.userData.id.length > 0
				) {
					addEmailIntegrationRequest.state =
						globalState.user.userData.id;
					const addCompanyEmailResponse =
						await addCompanyEmailIntegration(
							addEmailIntegrationRequest
						);
					const addCompanyEmailIntegrationSuccessfulAction: AddCompanyEmailIntegrationSuccessfulAction =
						{
							type: UsersActionTypes.ADD_COMPANY_EMAIL_INTEGRATION_SUCCESSFUL,
							integrations: addCompanyEmailResponse.integrations
						};
					return dispatch(addCompanyEmailIntegrationSuccessfulAction);
				} else {
					const addCompanyEmailIntegrationFailedAction: AddCompanyEmailIntegrationFailedAction =
						{
							type: UsersActionTypes.ADD_COMPANY_EMAIL_INTEGRATION_FAILED
						};
					dispatch(addCompanyEmailIntegrationFailedAction);
					return Promise.reject(DefaultErrorMessage);
				}
			} catch (error) {
				const addCompanyEmailIntegrationFailedAction: AddCompanyEmailIntegrationFailedAction =
					{
						type: UsersActionTypes.ADD_COMPANY_EMAIL_INTEGRATION_FAILED
					};
				dispatch(addCompanyEmailIntegrationFailedAction);

				return Promise.reject(error);
			}
		};
	},
	deleteCompanyConnectWiseIntegration: () => {
		return async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<DeleteCompanyConnectWiseIntegrationSuccessfulAction> => {
			const deleteCompanyConnectWiseIntegrationRequestedAction: DeleteCompanyConnectWiseIntegrationRequestedAction =
				{
					type: UsersActionTypes.DELETE_COMPANY_CONNECTWISE_INTEGRATION_REQUESTED
				};
			dispatch(deleteCompanyConnectWiseIntegrationRequestedAction);
			try {
				const globalState = getState();
				if (
					globalState.user &&
					globalState.user.userCompany &&
					globalState.user.userCompany.id &&
					globalState.user.userCompany.id.length > 0
				) {
					const deleteCompanyConnectWiseResponse =
						await deleteCompanyConnectWiseIntegration();
					const deleteCompanyConnectWiseIntegrationSuccessfulAction: DeleteCompanyConnectWiseIntegrationSuccessfulAction =
						{
							type: UsersActionTypes.DELETE_COMPANY_CONNECTWISE_INTEGRATION_SUCCESSFUL,
							integrations:
								deleteCompanyConnectWiseResponse.integrations
						};
					return dispatch(
						deleteCompanyConnectWiseIntegrationSuccessfulAction
					);
				} else {
					const deleteCompanyConnectWiseIntegrationFailedAction: DeleteCompanyConnectWiseIntegrationFailedAction =
						{
							type: UsersActionTypes.DELETE_COMPANY_CONNECTWISE_INTEGRATION_FAILED
						};
					dispatch(deleteCompanyConnectWiseIntegrationFailedAction);
					return Promise.reject(DefaultErrorMessage);
				}
			} catch (error) {
				const deleteCompanyConnectWiseIntegrationFailedAction: DeleteCompanyConnectWiseIntegrationFailedAction =
					{
						type: UsersActionTypes.DELETE_COMPANY_CONNECTWISE_INTEGRATION_FAILED
					};
				dispatch(deleteCompanyConnectWiseIntegrationFailedAction);

				return Promise.reject(error);
			}
		};
	},
	addCompanyConnectWiseIntegration: (
		addConnectWiseIntegrationRequest: AddConnectWiseIntegrationRequest
	) => {
		return async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<AddCompanyConnectWiseIntegrationSuccessfulAction> => {
			const addCompanyConnectWiseIntegrationRequestedAction: AddCompanyConnectWiseIntegrationRequestedAction =
				{
					type: UsersActionTypes.ADD_COMPANY_CONNECTWISE_INTEGRATION_REQUESTED
				};
			dispatch(addCompanyConnectWiseIntegrationRequestedAction);
			try {
				const globalState = getState();
				if (
					globalState.user &&
					globalState.user.userCompany &&
					globalState.user.userCompany.id &&
					globalState.user.userCompany.id.length > 0
				) {
					const addCompanyConnectWiseResponse =
						await addCompanyConnectWiseIntegration(
							addConnectWiseIntegrationRequest
						);
					const addCompanyConnectWiseIntegrationSuccessfulAction: AddCompanyConnectWiseIntegrationSuccessfulAction =
						{
							type: UsersActionTypes.ADD_COMPANY_CONNECTWISE_INTEGRATION_SUCCESSFUL,
							integrations:
								addCompanyConnectWiseResponse.integrations
						};
					return dispatch(
						addCompanyConnectWiseIntegrationSuccessfulAction
					);
				} else {
					const addCompanyConnectWiseIntegrationFailedAction: AddCompanyConnectWiseIntegrationFailedAction =
						{
							type: UsersActionTypes.ADD_COMPANY_CONNECTWISE_INTEGRATION_FAILED
						};
					dispatch(addCompanyConnectWiseIntegrationFailedAction);
					return Promise.reject(DefaultErrorMessage);
				}
			} catch (error) {
				const addCompanyConnectWiseIntegrationFailedAction: AddCompanyConnectWiseIntegrationFailedAction =
					{
						type: UsersActionTypes.ADD_COMPANY_CONNECTWISE_INTEGRATION_FAILED
					};
				dispatch(addCompanyConnectWiseIntegrationFailedAction);

				return Promise.reject(error);
			}
		};
	},
	deleteCompanyAutotaskIntegration: () => {
		return async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<DeleteCompanyAutotaskIntegrationSuccessfulAction> => {
			const deleteCompanyAutotaskIntegrationRequestedAction: DeleteCompanyAutotaskIntegrationRequestedAction =
				{
					type: UsersActionTypes.DELETE_COMPANY_AUTOTASK_INTEGRATION_REQUESTED
				};
			dispatch(deleteCompanyAutotaskIntegrationRequestedAction);
			try {
				const globalState = getState();
				if (
					globalState.user &&
					globalState.user.userCompany &&
					globalState.user.userCompany.id &&
					globalState.user.userCompany.id.length > 0
				) {
					const deleteCompanyAutotaskResponse =
						await deleteCompanyAutotaskIntegration();
					const deleteCompanyAutotaskIntegrationSuccessfulAction: DeleteCompanyAutotaskIntegrationSuccessfulAction =
						{
							type: UsersActionTypes.DELETE_COMPANY_AUTOTASK_INTEGRATION_SUCCESSFUL,
							integrations:
								deleteCompanyAutotaskResponse.integrations
						};
					return dispatch(
						deleteCompanyAutotaskIntegrationSuccessfulAction
					);
				} else {
					const deleteCompanyAutotaskIntegrationFailedAction: DeleteCompanyAutotaskIntegrationFailedAction =
						{
							type: UsersActionTypes.DELETE_COMPANY_AUTOTASK_INTEGRATION_FAILED
						};
					dispatch(deleteCompanyAutotaskIntegrationFailedAction);
					return Promise.reject(DefaultErrorMessage);
				}
			} catch (error) {
				const deleteCompanyAutotaskIntegrationFailedAction: DeleteCompanyAutotaskIntegrationFailedAction =
					{
						type: UsersActionTypes.DELETE_COMPANY_AUTOTASK_INTEGRATION_FAILED
					};
				dispatch(deleteCompanyAutotaskIntegrationFailedAction);

				return Promise.reject(error);
			}
		};
	},
	addCompanyAutotaskIntegration: (
		addAutotaskIntegrationRequest: AutotaskIntegrationResponse
	) => {
		return async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<AddCompanyAutotaskIntegrationSuccessfulAction> => {
			const addCompanyAutotaskIntegrationRequestedAction: AddCompanyAutotaskIntegrationRequestedAction =
				{
					type: UsersActionTypes.ADD_COMPANY_AUTOTASK_INTEGRATION_REQUESTED
				};
			dispatch(addCompanyAutotaskIntegrationRequestedAction);
			try {
				const globalState = getState();
				if (
					globalState.user &&
					globalState.user.userCompany &&
					globalState.user.userCompany.id &&
					globalState.user.userCompany.id.length > 0
				) {
					const addCompanyAutotaskResponse =
						await addCompanyAutotaskIntegration(
							addAutotaskIntegrationRequest
						);
					const addCompanyAutotaskIntegrationSuccessfulAction: AddCompanyAutotaskIntegrationSuccessfulAction =
						{
							type: UsersActionTypes.ADD_COMPANY_AUTOTASK_INTEGRATION_SUCCESSFUL,
							integrations:
								addCompanyAutotaskResponse.integrations
						};
					return dispatch(
						addCompanyAutotaskIntegrationSuccessfulAction
					);
				} else {
					const addCompanyAutotaskIntegrationFailedAction: AddCompanyAutotaskIntegrationFailedAction =
						{
							type: UsersActionTypes.ADD_COMPANY_AUTOTASK_INTEGRATION_FAILED
						};
					dispatch(addCompanyAutotaskIntegrationFailedAction);
					return Promise.reject(DefaultErrorMessage);
				}
			} catch (error) {
				const addCompanyAutotaskIntegrationFailedAction: AddCompanyAutotaskIntegrationFailedAction =
					{
						type: UsersActionTypes.ADD_COMPANY_AUTOTASK_INTEGRATION_FAILED
					};
				dispatch(addCompanyAutotaskIntegrationFailedAction);

				return Promise.reject(error);
			}
		};
	},
	deleteCompanyZapierIntegration: (
		deleteZapierIntegrationRequest: DeleteZapierIntegrationRequest
	) => {
		return async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<DeleteCompanyZapierIntegrationSuccessfulAction> => {
			const deleteCompanyZapierIntegrationRequestedAction: DeleteCompanyZapierIntegrationRequestedAction =
				{
					type: UsersActionTypes.DELETE_COMPANY_ZAPIER_INTEGRATION_REQUESTED
				};
			dispatch(deleteCompanyZapierIntegrationRequestedAction);
			try {
				const globalState = getState();
				if (!globalState?.user?.userCompany?.id?.length) {
					throw new Error("The user data is not available yet");
				}
				const deleteCompanyZapierResponse =
					await deleteCompanyZapierIntegration(
						globalState.user.userCompany,
						deleteZapierIntegrationRequest
					);
				const deleteCompanyZapierIntegrationSuccessfulAction: DeleteCompanyZapierIntegrationSuccessfulAction =
					{
						type: UsersActionTypes.DELETE_COMPANY_ZAPIER_INTEGRATION_SUCCESSFUL,
						integrations: deleteCompanyZapierResponse.integrations
					};
				return dispatch(deleteCompanyZapierIntegrationSuccessfulAction);
			} catch (error) {
				const deleteCompanyZapierIntegrationFailedAction: DeleteCompanyZapierIntegrationFailedAction =
					{
						type: UsersActionTypes.DELETE_COMPANY_ZAPIER_INTEGRATION_FAILED
					};
				dispatch(deleteCompanyZapierIntegrationFailedAction);

				return Promise.reject(error);
			}
		};
	},
	deleteCompanyKaseyaIntegration: () => {
		return async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<DeleteCompanyKaseyaIntegrationSuccessfulAction> => {
			const deleteCompanyKaseyaIntegrationRequestedAction: DeleteCompanyKaseyaIntegrationRequestedAction =
				{
					type: UsersActionTypes.DELETE_COMPANY_KASEYA_INTEGRATION_REQUESTED
				};
			dispatch(deleteCompanyKaseyaIntegrationRequestedAction);
			try {
				const globalState = getState();
				if (!globalState?.user?.userCompany?.id?.length) {
					throw new Error("The user data is not available");
				}
				const deleteCompanyKaseyaResponse =
					await deleteCompanyKaseyaIntegration();
				const deleteCompanyKaseyaIntegrationSuccessfulAction: DeleteCompanyKaseyaIntegrationSuccessfulAction =
					{
						type: UsersActionTypes.DELETE_COMPANY_KASEYA_INTEGRATION_SUCCESSFUL,
						integrations: deleteCompanyKaseyaResponse.integrations
					};
				return dispatch(deleteCompanyKaseyaIntegrationSuccessfulAction);
			} catch (error) {
				console.error(error);
				const deleteCompanyKaseyaIntegrationFailedAction: DeleteCompanyKaseyaIntegrationFailedAction =
					{
						type: UsersActionTypes.DELETE_COMPANY_KASEYA_INTEGRATION_FAILED
					};
				dispatch(deleteCompanyKaseyaIntegrationFailedAction);

				return Promise.reject(error);
			}
		};
	},
	addCompanyKaseyaIntegration: (
		addKaseyaIntegrationRequest: AddKaseyaIntegrationRequest
	) => {
		return async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<AddCompanyKaseyaIntegrationSuccessfulAction> => {
			const addCompanyKaseyaIntegrationRequestedAction: AddCompanyKaseyaIntegrationRequestedAction =
				{
					type: UsersActionTypes.ADD_COMPANY_KASEYA_INTEGRATION_REQUESTED
				};
			dispatch(addCompanyKaseyaIntegrationRequestedAction);
			try {
				const globalState = getState();
				if (!globalState?.user?.userCompany?.id?.length) {
					throw new Error("unable to access user data");
				}

				const addCompanyKaseyaResponse =
					await addCompanyKaseyaIntegration(
						addKaseyaIntegrationRequest
					);
				const addCompanyKaseyaIntegrationSuccessfulAction: AddCompanyKaseyaIntegrationSuccessfulAction =
					{
						type: UsersActionTypes.ADD_COMPANY_KASEYA_INTEGRATION_SUCCESSFUL,
						integrations: addCompanyKaseyaResponse.integrations
					};
				return dispatch(addCompanyKaseyaIntegrationSuccessfulAction);
			} catch (error) {
				const addCompanyKaseyaIntegrationFailedAction: AddCompanyKaseyaIntegrationFailedAction =
					{
						type: UsersActionTypes.ADD_COMPANY_KASEYA_INTEGRATION_FAILED
					};
				dispatch(addCompanyKaseyaIntegrationFailedAction);

				return Promise.reject(error);
			}
		};
	},
	deleteCompanyIngramIntegration: () => {
		return async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<DeleteCompanyIngramIntegrationSuccessfulAction> => {
			const deleteCompanyIngramIntegrationRequestedAction: DeleteCompanyIngramIntegrationRequestedAction =
				{
					type: UsersActionTypes.DELETE_COMPANY_INGRAM_INTEGRATION_REQUESTED
				};
			dispatch(deleteCompanyIngramIntegrationRequestedAction);
			try {
				const globalState = getState();
				if (!globalState?.user?.userCompany?.id?.length) {
					throw new Error("The user data is not available");
				}
				const deleteCompanyIngramResponse =
					await deleteCompanyIngramIntegration();
				const deleteCompanyIngramIntegrationSuccessfulAction: DeleteCompanyIngramIntegrationSuccessfulAction =
					{
						type: UsersActionTypes.DELETE_COMPANY_INGRAM_INTEGRATION_SUCCESSFUL,
						integrations: deleteCompanyIngramResponse.integrations
					};
				return dispatch(deleteCompanyIngramIntegrationSuccessfulAction);
			} catch (error) {
				console.error(error);
				const deleteCompanyIngramIntegrationFailedAction: DeleteCompanyIngramIntegrationFailedAction =
					{
						type: UsersActionTypes.DELETE_COMPANY_INGRAM_INTEGRATION_FAILED
					};
				dispatch(deleteCompanyIngramIntegrationFailedAction);

				return Promise.reject(error);
			}
		};
	},
	addCompanyIngramIntegration: (
		addKaseyaIntegrationRequest: AddIngramIntegrationRequest
	) => {
		return async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<AddCompanyIngramIntegrationSuccessfulAction> => {
			const addCompanyIngramIntegrationRequestedAction: AddCompanyIngramIntegrationRequestedAction =
				{
					type: UsersActionTypes.ADD_COMPANY_INGRAM_INTEGRATION_REQUESTED
				};
			dispatch(addCompanyIngramIntegrationRequestedAction);
			try {
				const globalState = getState();
				if (!globalState?.user?.userCompany?.id?.length) {
					throw new Error("unable to access user data");
				}

				const addCompanyIngramResponse =
					await addCompanyIngramIntegration(
						addKaseyaIntegrationRequest
					);
				const addCompanyIngramIntegrationSuccessfulAction: AddCompanyIngramIntegrationSuccessfulAction =
					{
						type: UsersActionTypes.ADD_COMPANY_INGRAM_INTEGRATION_SUCCESSFUL,
						integrations: addCompanyIngramResponse.integrations
					};
				return dispatch(addCompanyIngramIntegrationSuccessfulAction);
			} catch (error) {
				const addCompanyIngramIntegrationFailedAction: AddCompanyIngramIntegrationFailedAction =
					{
						type: UsersActionTypes.ADD_COMPANY_INGRAM_INTEGRATION_FAILED
					};
				dispatch(addCompanyIngramIntegrationFailedAction);

				return Promise.reject(error);
			}
		};
	},
	deleteCompanyTechDataIntegration: () => {
		return async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<DeleteCompanyTechDataIntegrationSuccessfulAction> => {
			const deleteCompanyTechDataIntegrationRequestedAction: DeleteCompanyTechDataIntegrationRequestedAction =
				{
					type: UsersActionTypes.DELETE_COMPANY_TECH_DATA_INTEGRATION_REQUESTED
				};
			dispatch(deleteCompanyTechDataIntegrationRequestedAction);
			try {
				const globalState = getState();
				if (!globalState?.user?.userCompany?.id?.length) {
					throw new Error("The user data is not available");
				}
				const deleteCompanyTechDataResponse =
					await deleteCompanyTechDataIntegration();
				const deleteCompanyTechDataIntegrationSuccessfulAction: DeleteCompanyTechDataIntegrationSuccessfulAction =
					{
						type: UsersActionTypes.DELETE_COMPANY_TECH_DATA_INTEGRATION_SUCCESSFUL,
						integrations: deleteCompanyTechDataResponse.integrations
					};
				return dispatch(
					deleteCompanyTechDataIntegrationSuccessfulAction
				);
			} catch (error) {
				console.error(error);
				const deleteCompanyIngramIntegrationFailedAction: DeleteCompanyTechDataIntegrationFailedAction =
					{
						type: UsersActionTypes.DELETE_COMPANY_TECH_DATA_INTEGRATION_FAILED
					};
				dispatch(deleteCompanyIngramIntegrationFailedAction);

				return Promise.reject(error);
			}
		};
	},
	addCompanyTechDataIntegration: (
		addTechDataIntegrationRequest: AddTechDataIntegrationRequest
	) => {
		return async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<AddCompanyTechDataIntegrationSuccessfulAction> => {
			const addCompanyTechDataIntegrationRequestedAction: AddCompanyTechDataIntegrationRequestedAction =
				{
					type: UsersActionTypes.ADD_COMPANY_TECH_DATA_INTEGRATION_REQUESTED
				};
			dispatch(addCompanyTechDataIntegrationRequestedAction);
			try {
				const globalState = getState();
				if (!globalState?.user?.userCompany?.id?.length) {
					throw new Error("unable to access user data");
				}

				const addCompanyTechDataResponse =
					await addCompanyTechDataIntegration(
						addTechDataIntegrationRequest
					);
				const addCompanyTechDataIntegrationSuccessfulAction: AddCompanyTechDataIntegrationSuccessfulAction =
					{
						type: UsersActionTypes.ADD_COMPANY_TECH_DATA_INTEGRATION_SUCCESSFUL,
						integrations: addCompanyTechDataResponse.integrations
					};
				return dispatch(addCompanyTechDataIntegrationSuccessfulAction);
			} catch (error) {
				const addCompanyIngramIntegrationFailedAction: AddCompanyIngramIntegrationFailedAction =
					{
						type: UsersActionTypes.ADD_COMPANY_INGRAM_INTEGRATION_FAILED
					};
				dispatch(addCompanyIngramIntegrationFailedAction);

				return Promise.reject(error);
			}
		};
	},
	deleteDAndHIntegration: () => {
		return async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<DeleteDAndHIntegrationSuccessfulAction> => {
			const deleteDAndHIntegrationRequestedAction: DeleteDAndHIntegrationRequestedAction =
				{
					type: UsersActionTypes.DELETE_D_AND_H_INTEGRATION_REQUESTED
				};
			dispatch(deleteDAndHIntegrationRequestedAction);
			try {
				const globalState = getState();
				if (!globalState?.user?.userCompany?.id?.length) {
					throw new Error("The user data is not available");
				}
				const deleteDAndHResponse = await deleteDAndHIntegration();
				const deleteDAndHIntegrationSuccessfulAction: DeleteDAndHIntegrationSuccessfulAction =
					{
						type: UsersActionTypes.DELETE_D_AND_H_INTEGRATION_SUCCESSFUL,
						integrations: deleteDAndHResponse.integrations
					};
				return dispatch(deleteDAndHIntegrationSuccessfulAction);
			} catch (error) {
				console.error(error);
				const deleteDAndHIntegrationFailedAction: DeleteDAndHIntegrationFailedAction =
					{
						type: UsersActionTypes.DELETE_D_AND_H_INTEGRATION_FAILED
					};
				dispatch(deleteDAndHIntegrationFailedAction);

				return Promise.reject(error);
			}
		};
	},
	addDAndHIntegration: (
		addDAndHIntegrationRequest: AddDAndHIntegrationRequest
	) => {
		return async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<AddDAndHIntegrationSuccessfulAction> => {
			const addDAndHIntegrationRequestedAction: AddDAndHIntegrationRequestedAction =
				{
					type: UsersActionTypes.ADD_D_AND_H_INTEGRATION_REQUESTED
				};
			dispatch(addDAndHIntegrationRequestedAction);
			try {
				const globalState = getState();
				if (!globalState?.user?.userCompany?.id?.length) {
					throw new Error("unable to access user data");
				}

				const addCompanyTechDataResponse = await addDAndHIntegration(
					addDAndHIntegrationRequest
				);
				const addDAndHIntegrationSuccessfulAction: AddDAndHIntegrationSuccessfulAction =
					{
						type: UsersActionTypes.ADD_D_AND_H_INTEGRATION_SUCCESSFUL,
						integrations: addCompanyTechDataResponse.integrations
					};
				return dispatch(addDAndHIntegrationSuccessfulAction);
			} catch (error) {
				const addDAndHIntegrationFailedAction: AddDAndHIntegrationFailedAction =
					{
						type: UsersActionTypes.ADD_D_AND_H_INTEGRATION_FAILED
					};
				dispatch(addDAndHIntegrationFailedAction);

				return Promise.reject(error);
			}
		};
	},
	deleteSynnexIntegration: () => {
		return async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<DeleteSynnexIntegrationSuccessfulAction> => {
			const deleteSynnexIntegrationRequestedAction: DeleteSynnexIntegrationRequestedAction =
				{
					type: UsersActionTypes.DELETE_SYNNEX_INTEGRATION_REQUESTED
				};
			dispatch(deleteSynnexIntegrationRequestedAction);
			try {
				const globalState = getState();
				if (!globalState?.user?.userCompany?.id?.length) {
					throw new Error("The user data is not available");
				}
				const deleteSynnexResponse = await deleteSynnexIntegration();
				const deleteSynnexIntegrationSuccessfulAction: DeleteSynnexIntegrationSuccessfulAction =
					{
						type: UsersActionTypes.DELETE_SYNNEX_INTEGRATION_SUCCESSFUL,
						integrations: deleteSynnexResponse.integrations
					};
				return dispatch(deleteSynnexIntegrationSuccessfulAction);
			} catch (error) {
				console.error(error);
				const deleteSynnexIntegrationFailedAction: DeleteSynnexIntegrationFailedAction =
					{
						type: UsersActionTypes.DELETE_SYNNEX_INTEGRATION_FAILED
					};
				dispatch(deleteSynnexIntegrationFailedAction);

				return Promise.reject(error);
			}
		};
	},
	addSynnexIntegration: (
		addSynnexIntegrationRequest: AddSynnexIntegrationRequest
	) => {
		return async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<AddSynnexIntegrationSuccessfulAction> => {
			const addSynnexIntegrationRequestedAction: AddSynnexIntegrationRequestedAction =
				{
					type: UsersActionTypes.ADD_SYNNEX_INTEGRATION_REQUESTED
				};
			dispatch(addSynnexIntegrationRequestedAction);
			try {
				const globalState = getState();
				if (!globalState?.user?.userCompany?.id?.length) {
					throw new Error("unable to access user data");
				}

				const addSynnexResponse = await addSynnexIntegration(
					addSynnexIntegrationRequest
				);
				const addSynnexIntegrationSuccessfulAction: AddSynnexIntegrationSuccessfulAction =
					{
						type: UsersActionTypes.ADD_SYNNEX_INTEGRATION_SUCCESSFUL,
						integrations: addSynnexResponse.integrations
					};
				return dispatch(addSynnexIntegrationSuccessfulAction);
			} catch (error) {
				const addSynnexIntegrationFailedAction: AddSynnexIntegrationFailedAction =
					{
						type: UsersActionTypes.ADD_SYNNEX_INTEGRATION_FAILED
					};
				dispatch(addSynnexIntegrationFailedAction);

				return Promise.reject(error);
			}
		};
	},
	deleteCBIntegration: () => {
		return async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<DeleteCBIntegrationSuccessfulAction> => {
			const deleteCBIntegrationRequestedAction: DeleteCBIntegrationRequestedAction =
				{
					type: UsersActionTypes.DELETE_CB_INTEGRATION_REQUESTED
				};
			dispatch(deleteCBIntegrationRequestedAction);
			try {
				const globalState = getState();
				if (!globalState?.user?.userCompany?.id?.length) {
					throw new Error("The user data is not available");
				}
				const deleteCBResponse = await deleteCBIntegration();
				const deleteCBIntegrationSuccessfulAction: DeleteCBIntegrationSuccessfulAction =
					{
						type: UsersActionTypes.DELETE_CB_INTEGRATION_SUCCESSFUL,
						integrations: deleteCBResponse.integrations
					};
				return dispatch(deleteCBIntegrationSuccessfulAction);
			} catch (error) {
				console.error(error);
				const deleteCBIntegrationFailedAction: DeleteCBIntegrationFailedAction =
					{
						type: UsersActionTypes.DELETE_CB_INTEGRATION_FAILED
					};
				dispatch(deleteCBIntegrationFailedAction);

				return Promise.reject(error);
			}
		};
	},
	addCBIntegration: (addCBIntegrationRequest: AddCBIntegrationRequest) => {
		return async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<AddCBIntegrationSuccessfulAction> => {
			const addCBIntegrationRequestedAction: AddCBIntegrationRequestedAction =
				{
					type: UsersActionTypes.ADD_CB_INTEGRATION_REQUESTED
				};
			dispatch(addCBIntegrationRequestedAction);
			try {
				const globalState = getState();
				if (!globalState?.user?.userCompany?.id?.length) {
					throw new Error("unable to access user data");
				}

				const addCBResponse = await addCBIntegration(
					addCBIntegrationRequest
				);
				const addCBIntegrationSuccessfulAction: AddCBIntegrationSuccessfulAction =
					{
						type: UsersActionTypes.ADD_CB_INTEGRATION_SUCCESSFUL,
						integrations: addCBResponse.integrations
					};
				return dispatch(addCBIntegrationSuccessfulAction);
			} catch (error) {
				const addCBIntegrationFailedAction: AddCBIntegrationFailedAction =
					{
						type: UsersActionTypes.ADD_CB_INTEGRATION_FAILED
					};
				dispatch(addCBIntegrationFailedAction);

				return Promise.reject(error);
			}
		};
	},
	addCompanySyncroIntegration: (
		addSyncroIntegrationRequest: AddSyncroIntegrationRequest
	) => {
		return async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<AddCompanySyncroIntegrationSuccessfulAction> => {
			const addCompanySyncroIntegrationRequestedAction: AddCompanySyncroIntegrationRequestedAction =
				{
					type: UsersActionTypes.ADD_COMPANY_SYNCRO_INTEGRATION_REQUESTED
				};
			dispatch(addCompanySyncroIntegrationRequestedAction);
			try {
				const globalState = getState();
				if (!globalState?.user?.userCompany?.id?.length) {
					throw new Error("unable to access user data");
				}
				const response = await addSyncroIntegration(
					addSyncroIntegrationRequest
				);
				const addCompanySyncroIntegrationSuccessfulAction: AddCompanySyncroIntegrationSuccessfulAction =
					{
						type: UsersActionTypes.ADD_COMPANY_SYNCRO_INTEGRATION_SUCCESSFUL,
						integrations: response.integrations
					};
				return dispatch(addCompanySyncroIntegrationSuccessfulAction);
			} catch (error) {
				const addCompanySyncroIntegrationFailedAction: AddCompanySyncroIntegrationFailedAction =
					{
						type: UsersActionTypes.ADD_COMPANY_SYNCRO_INTEGRATION_FAILED
					};
				dispatch(addCompanySyncroIntegrationFailedAction);

				return Promise.reject(error);
			}
		};
	},
	deleteCompanySyncroIntegration: () => {
		return async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<DeleteCompanySyncroIntegrationSuccessfulAction> => {
			const deleteCompanySyncroIntegrationRequestedAction: DeleteCompanySyncroIntegrationRequestedAction =
				{
					type: UsersActionTypes.DELETE_COMPANY_SYNCRO_INTEGRATION_REQUESTED
				};
			dispatch(deleteCompanySyncroIntegrationRequestedAction);
			try {
				const globalState = getState();
				if (!globalState?.user?.userCompany?.id?.length) {
					throw new Error("The user data is not available");
				}
				const response = await deleteSyncroIntegration();
				const deleteCompanySyncroIntegrationSuccessfulAction: DeleteCompanySyncroIntegrationSuccessfulAction =
					{
						type: UsersActionTypes.DELETE_COMPANY_SYNCRO_INTEGRATION_SUCCESSFUL,
						integrations: response.integrations
					};
				return dispatch(deleteCompanySyncroIntegrationSuccessfulAction);
			} catch (error) {
				const deleteCompanySyncroIntegrationFailedAction: DeleteCompanySyncroIntegrationFailedAction =
					{
						type: UsersActionTypes.DELETE_COMPANY_SYNCRO_INTEGRATION_FAILED
					};
				dispatch(deleteCompanySyncroIntegrationFailedAction);

				return Promise.reject(error);
			}
		};
	},
	addInfusionSoftIntegration: (
		infusionSoftMetadataRequest: InfusionSoftMetadataRequest
	) => {
		return async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<AddInfusionSoftIntegrationSuccessfulAction> => {
			const addInfusionSoftIntegrationRequestedAction: AddInfusionSoftIntegrationRequestedAction =
				{
					type: UsersActionTypes.ADD_INFUSION_SOFT_INTEGRATION_REQUESTED
				};
			dispatch(addInfusionSoftIntegrationRequestedAction);
			try {
				const globalState = getState();
				if (!globalState.user?.userCompany?.id?.length) {
					throw new Error("unable to access user data");
				}
				const response = await updateInfusionSoftMetadata(
					infusionSoftMetadataRequest
				);
				const addInfusionSoftIntegrationSuccessfulAction: AddInfusionSoftIntegrationSuccessfulAction =
					{
						type: UsersActionTypes.ADD_INFUSION_SOFT_INTEGRATION_SUCCESFUL,
						integrations: response.integrations
					};
				return dispatch(addInfusionSoftIntegrationSuccessfulAction);
			} catch (error) {
				const addInfusionSoftIntegrationFailedAction: AddInfusionSoftIntegrationFailedAction =
					{
						type: UsersActionTypes.ADD_INFUSION_SOFT_INTEGRATION_FAILED
					};
				dispatch(addInfusionSoftIntegrationFailedAction);

				return Promise.reject(error);
			}
		};
	},
	deleteInfusionSoftIntegration: () => {
		return async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<DeleteInfusionSoftIntegrationSuccessfulAction> => {
			const deleteInfusionSoftIntegrationRequestedAction: DeleteInfusionSoftIntegrationRequestedAction =
				{
					type: UsersActionTypes.DELETE_INFUSION_SOFT_INTEGRATION_REQUESTED
				};
			dispatch(deleteInfusionSoftIntegrationRequestedAction);
			try {
				const globalState = getState();
				if (!globalState.user?.userCompany?.id?.length) {
					throw new Error("The user data is not available");
				}
				const response = await deleteInfusionSoftIntegration();
				const deleteInfusionSoftIntegrationSuccessfulAction: DeleteInfusionSoftIntegrationSuccessfulAction =
					{
						type: UsersActionTypes.DELETE_INFUSION_SOFT_INTEGRATION_SUCCESFUL,
						integrations: response.integrations
					};
				return dispatch(deleteInfusionSoftIntegrationSuccessfulAction);
			} catch (error) {
				const deleteInfusionSoftIntegrationFailedAction: DeleteInfusionSoftIntegrationFailedAction =
					{
						type: UsersActionTypes.DELETE_INFUSION_SOFT_INTEGRATION_FAILED
					};
				dispatch(deleteInfusionSoftIntegrationFailedAction);

				return Promise.reject(error);
			}
		};
	},
	editQuoteLabelGlobal: (key: keyof QuoteLabels, value: string) => {
		return async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<EditQuoteDefaultLabelCompanySuccessfulAction> => {
			const company = getState().user.userCompany;
			const editedCompany: UserCompany = {
				...company,
				settings: {
					...company?.settings,
					pricing_table_default_label: {
						...DefaultQuoteLabels,
						...company?.settings.pricing_table_default_label,
						[key]: value
					}
				}
			};
			const editQuoteDefaultLabelRequestAction: EditQuoteDefaultLabelCompanyRequestedAction =
				{
					type: UsersActionTypes.EDIT_QUOTE_DEFAULT_LABEL_COMPANY_REQUESTED
				};
			dispatch(editQuoteDefaultLabelRequestAction);
			try {
				const response = await editUserCompany(editedCompany);
				const editQuoteDefaultLabelCompanySuccessfulAction: EditQuoteDefaultLabelCompanySuccessfulAction =
					{
						type: UsersActionTypes.EDIT_QUOTE_DEFAULT_LABEL_COMPANY_SUCCESSFUL,
						quoteLabels:
							response?.settings.pricing_table_default_label
					};
				return dispatch(editQuoteDefaultLabelCompanySuccessfulAction);
			} catch (error) {
				const editQuoteDefaultLabelCompanyFailedAction: EditQuoteDefaultLabelCompanyFailedAction =
					{
						type: UsersActionTypes.EDIT_QUOTE_DEFAULT_LABEL_COMPANY_FAILED
					};
				dispatch(editQuoteDefaultLabelCompanyFailedAction);

				return Promise.reject(error);
			}
		};
	},
	fetchLibrary:
		fetchLibraryRequest =>
		async (
			dispatch: Dispatch
		): Promise<FetchLibraryListSuccessfulAction> => {
			try {
				const fetchLibraryListRequested: FetchLibraryListRequestedAction =
					{
						type: UsersActionTypes.FETCH_LIBRARY_LIST_REQUESTED
					};
				dispatch(fetchLibraryListRequested);

				const libraryListResponse = await getLibraryData(
					fetchLibraryRequest
				);

				const fetchLibraryListSuccessfulAction: FetchLibraryListSuccessfulAction =
					{
						type: UsersActionTypes.FETCH_LIBRARY_LIST_SUCCESSFUL,
						libraryList: libraryListResponse.data
					};
				return dispatch(fetchLibraryListSuccessfulAction);
			} catch (error) {
				const fetchLibraryListFailedAction: FetchLibraryListFailedAction =
					{
						type: UsersActionTypes.FETCH_LIBRARY_LIST_FAILED
					};
				dispatch(fetchLibraryListFailedAction);

				return Promise.reject(error);
			}
		},
	saveBlockToLibrary:
		createLibraryItemRequest =>
		async (
			dispatch: Dispatch
		): Promise<SaveBlockToLibraryListSuccessfulAction> => {
			try {
				const saveBlockToLibraryListRequested: SaveBlockToLibraryListRequestedAction =
					{
						type: UsersActionTypes.SAVE_BLOCK_TO_LIBRARY_LIST_REQUESTED
					};
				dispatch(saveBlockToLibraryListRequested);

				await createLibraryResource(createLibraryItemRequest);

				const saveBlockToLibraryListSuccessfulAction: SaveBlockToLibraryListSuccessfulAction =
					{
						type: UsersActionTypes.SAVE_BLOCK_TO_LIBRARY_LIST_SUCCESSFUL
					};
				return dispatch(saveBlockToLibraryListSuccessfulAction);
			} catch (error) {
				const saveBlockToLibraryListFailedAction: SaveBlockToLibraryListFailedAction =
					{
						type: UsersActionTypes.SAVE_BLOCK_TO_LIBRARY_LIST_FAILED
					};
				dispatch(saveBlockToLibraryListFailedAction);

				return Promise.reject(error);
			}
		},
	deleteBlockFromLibrary:
		({ libraryItemId }: { libraryItemId: string }) =>
		async (
			dispatch: Dispatch
		): Promise<DeleteBlockToLibraryListSuccessfulAction> => {
			try {
				const deleteBlockToLibraryListRequestedAction: DeleteBlockToLibraryListRequestedAction =
					{
						type: UsersActionTypes.DELETE_BLOCK_TO_LIBRARY_LIST_REQUESTED
					};
				dispatch(deleteBlockToLibraryListRequestedAction);

				await deleteLibraryResource({ libraryItemId });

				const saveBlockToLibraryListSuccessfulAction: DeleteBlockToLibraryListSuccessfulAction =
					{
						type: UsersActionTypes.DELETE_BLOCK_TO_LIBRARY_LIST_SUCCESSFUL
					};
				return dispatch(saveBlockToLibraryListSuccessfulAction);
			} catch (error) {
				const saveBlockToLibraryListFailedAction: DeleteBlockToLibraryListFailedAction =
					{
						type: UsersActionTypes.DELETE_BLOCK_TO_LIBRARY_LIST_FAILED
					};
				dispatch(saveBlockToLibraryListFailedAction);

				return Promise.reject(error);
			}
		},
	updateLibraryResourceTags:
		({
			libraryItemId,
			tagIds
		}: {
			libraryItemId: string;
			tagIds: string[];
		}) =>
		async (
			dispatch: Dispatch
		): Promise<UpdateLibraryItemTagsSuccessfulAction> => {
			await updateLibraryResourceTags(libraryItemId, tagIds);
			const successAction: UpdateLibraryItemTagsSuccessfulAction = {
				type: UsersActionTypes.UPDATE_LIBRARY_ITEM_TAGS_SUCCESSFUL,
				libraryItemId,
				tagIds
			};
			return dispatch(successAction);
		},
	confirmCardAdded:
		() =>
		async (
			dispatch: Dispatch
		): Promise<ConfirmCardAddedSuccessfulAction> => {
			const confirmCardAddedRequestedAction: ConfirmCardAddedRequestedAction =
				{
					type: UsersActionTypes.CONFIRM_CARD_ADDED_REQUESTED
				};
			dispatch(confirmCardAddedRequestedAction);
			try {
				const confirmCardAddedResponse = await confirmCardAdded();
				invalidateBillingInfoCache();
				const confirmCardAddedSuccessfulAction: ConfirmCardAddedSuccessfulAction =
					{
						type: UsersActionTypes.CONFIRM_CARD_ADDED_SUCCESSFUL,
						billingInfo: confirmCardAddedResponse
					};
				return dispatch(confirmCardAddedSuccessfulAction);
			} catch (error) {
				const confirmCardAddedFailedAction: ConfirmCardAddedFailedAction =
					{
						type: UsersActionTypes.CONFIRM_CARD_ADDED_FAILED
					};
				dispatch(confirmCardAddedFailedAction);

				return Promise.reject(error);
			}
		},
	getChargebeePortalSession:
		() =>
		async (
			dispatch: Dispatch
		): Promise<ChargebeePortalSessionSuccessfulAction> => {
			const chargebeePortalSessionRequestedAction: ChargebeePortalSessionRequestedAction =
				{
					type: UsersActionTypes.CHARGEBEE_PORTAL_SESSION_REQUESTED
				};
			dispatch(chargebeePortalSessionRequestedAction);
			try {
				const chargebeePortalSessionResponse =
					await getChargebeePortalSession();
				const chargebeePortalSessionSuccessfulAction: ChargebeePortalSessionSuccessfulAction =
					{
						type: UsersActionTypes.CHARGEBEE_PORTAL_SESSION_SUCCESSFUL,
						portalSession: chargebeePortalSessionResponse
					};
				return dispatch(chargebeePortalSessionSuccessfulAction);
			} catch (error) {
				const chargebeePortalSessionFailedAction: ChargebeePortalSessionFailedAction =
					{
						type: UsersActionTypes.CHARGEBEE_PORTAL_SESSION_FAILED
					};
				dispatch(chargebeePortalSessionFailedAction);
				return Promise.reject(error);
			}
		},
	getBillingInfo:
		() =>
		(dispatch: Dispatch): Promise<BillingInfoSuccessfulAction> =>
			withDedupe(billingInfoDedupeSlot, async () => {
				const billingInfoRequestedAction: BillingInfoRequestedAction = {
					type: UsersActionTypes.BILLING_INFO_REQUESTED
				};
				dispatch(billingInfoRequestedAction);
				try {
					const billingInfoResponse = await getBillingInfo();
					const billingInfoSuccessfulAction: BillingInfoSuccessfulAction =
						{
							type: UsersActionTypes.BILLING_INFO_SUCCESSFUL,
							billingInfo: billingInfoResponse
						};
					return dispatch(billingInfoSuccessfulAction);
				} catch (error) {
					const billingInfoFailedAction: BillingInfoFailedAction = {
						type: UsersActionTypes.BILLING_INFO_FAILED,
						error
					};
					dispatch(billingInfoFailedAction);
					throw error;
				}
			}),
	updateBillingInfo:
		(billingInfo: UpdateBillingInfoRequest) =>
		async (dispatch: Dispatch): Promise<BillingInfoSuccessfulAction> => {
			try {
				const billingInfoResponse = await updateBillingInfo(
					billingInfo
				);
				invalidateBillingInfoCache();
				const billingInfoSuccessfulAction: BillingInfoSuccessfulAction =
					{
						type: UsersActionTypes.BILLING_INFO_SUCCESSFUL,
						billingInfo: billingInfoResponse
					};
				return dispatch(billingInfoSuccessfulAction);
			} catch (error) {
				return Promise.reject(error);
			}
		},
	getSubscriptionPlans:
		() =>
		(dispatch: Dispatch): Promise<SubscriptionPlansSuccessfulAction> =>
			withDedupe(subscriptionPlansDedupeSlot, async () => {
				const subscriptionPlansRequestedAction: SubscriptionPlansRequestedAction =
					{
						type: UsersActionTypes.SUBSCRIPTION_PLANS_REQUESTED
					};
				dispatch(subscriptionPlansRequestedAction);
				try {
					const subscriptionPlansResponse = await getPricingPlans();
					const subscriptions = subscriptionPlansResponse?.data;
					cache.save(
						ZomentumStorageKeys.SUBSCRIPTIONS,
						subscriptions
					);
					const subscriptionPlansSuccessfulAction: SubscriptionPlansSuccessfulAction =
						{
							type: UsersActionTypes.SUBSCRIPTION_PLANS_SUCCESSFUL,
							subscriptions: subscriptionPlansResponse.data
						};
					return dispatch(subscriptionPlansSuccessfulAction);
				} catch (error) {
					const subscriptionPlansFailedAction: SubscriptionPlansFailedAction =
						{
							type: UsersActionTypes.SUBSCRIPTION_PLANS_FAILED,
							error
						};
					dispatch(subscriptionPlansFailedAction);
					throw error;
				}
			}),
	getSubscriptionDetails:
		() =>
		(dispatch: Dispatch): Promise<SubscriptionDetailsSuccessfulAction> =>
			withDedupe(subscriptionDetailsDedupeSlot, async () => {
				const subscriptionDetailsRequestedAction: SubscriptionDetailsRequestedAction =
					{
						type: UsersActionTypes.SUBSCRIPTION_DETAILS_REQUESTED
					};
				dispatch(subscriptionDetailsRequestedAction);
				try {
					const subscriptionDetailsResponse =
						await getPricingPlanSummary();
					const subscriptionDetailsSuccessfulAction: SubscriptionDetailsSuccessfulAction =
						{
							type: UsersActionTypes.SUBSCRIPTION_DETAILS_SUCCESSFUL,
							subscriptionDetails:
								subscriptionDetailsResponse.data
						};
					return dispatch(subscriptionDetailsSuccessfulAction);
				} catch (error) {
					const subscriptionDetailsFailedAction: SubscriptionDetailsFailedAction =
						{
							type: UsersActionTypes.SUBSCRIPTION_DETAILS_FAILED,
							error
						};
					dispatch(subscriptionDetailsFailedAction);
					throw error;
				}
			}),
	getSubscriptionOverview:
		() =>
		(dispatch: Dispatch): Promise<SubscriptionDetailsSuccessfulAction> =>
			withDedupe(subscriptionOverviewDedupeSlot, async () => {
				dispatch<SubscriptionPlansRequestedAction>({
					type: UsersActionTypes.SUBSCRIPTION_PLANS_REQUESTED
				});
				dispatch<SubscriptionDetailsRequestedAction>({
					type: UsersActionTypes.SUBSCRIPTION_DETAILS_REQUESTED
				});
				dispatch<BillingInfoRequestedAction>({
					type: UsersActionTypes.BILLING_INFO_REQUESTED
				});
				try {
					const overviewResponse = await getSubscriptionOverview();
					const subscriptions = overviewResponse.plan_details.data;
					const subscriptionDetails =
						overviewResponse.subscription_details?.data ??
						new PricingSummary();
					cache.save(
						ZomentumStorageKeys.SUBSCRIPTIONS,
						subscriptions
					);
					dispatch<SubscriptionPlansSuccessfulAction>({
						type: UsersActionTypes.SUBSCRIPTION_PLANS_SUCCESSFUL,
						subscriptions
					});
					dispatch<BillingInfoSuccessfulAction>({
						type: UsersActionTypes.BILLING_INFO_SUCCESSFUL,
						billingInfo: overviewResponse.billing_info
					});
					return dispatch<SubscriptionDetailsSuccessfulAction>({
						type: UsersActionTypes.SUBSCRIPTION_DETAILS_SUCCESSFUL,
						subscriptionDetails
					});
				} catch (error) {
					dispatch<SubscriptionPlansFailedAction>({
						type: UsersActionTypes.SUBSCRIPTION_PLANS_FAILED,
						error
					});
					dispatch<SubscriptionDetailsFailedAction>({
						type: UsersActionTypes.SUBSCRIPTION_DETAILS_FAILED,
						error
					});
					dispatch<BillingInfoFailedAction>({
						type: UsersActionTypes.BILLING_INFO_FAILED,
						error
					});
					throw error;
				}
			}),
	addUserSubscription:
		(addUserRequest: PricingPlanUserRequest) =>
		async (
			dispatch: Dispatch
		): Promise<SubscriptionPlanAddUserSuccessfulAction> => {
			const addUserSubscriptionRequestedAction: SubscriptionPlanAddUserRequestedAction =
				{
					type: UsersActionTypes.SUBSCRIPTION_PLAN_ADD_USER_REQUESTED
				};
			dispatch(addUserSubscriptionRequestedAction);
			try {
				const addUserSubscriptionResponse = await addUserPricingPlan(
					addUserRequest
				);
				const addUserSubscriptionSuccessfulAction: SubscriptionPlanAddUserSuccessfulAction =
					{
						type: UsersActionTypes.SUBSCRIPTION_PLAN_ADD_USER_SUCCESSFUL,
						subscriptionDetails: addUserSubscriptionResponse.data
					};
				return dispatch(addUserSubscriptionSuccessfulAction);
			} catch (error) {
				const addUserSubscriptionFailedAction: SubscriptionPlanAddUserFailedAction =
					{
						type: UsersActionTypes.SUBSCRIPTION_PLAN_ADD_USER_FAILED
					};
				dispatch(addUserSubscriptionFailedAction);
				return Promise.reject(error);
			}
		},
	removeUserSubscription:
		(removeUserRequest: PricingPlanUserRequest) =>
		async (
			dispatch: Dispatch
		): Promise<SubscriptionPlanRemoveUserSuccessfulAction> => {
			const removeUserSubscriptionRequestedAction: SubscriptionPlanRemoveUserRequestedAction =
				{
					type: UsersActionTypes.SUBSCRIPTION_PLAN_REMOVE_USER_REQUESTED
				};
			dispatch(removeUserSubscriptionRequestedAction);
			try {
				const removeUserSubscriptionResponse =
					await removeUserPricingPlan(removeUserRequest);
				const removeUserSubscriptionSuccessfulAction: SubscriptionPlanRemoveUserSuccessfulAction =
					{
						type: UsersActionTypes.SUBSCRIPTION_PLAN_REMOVE_USER_SUCCESSFUL,
						subscriptionDetails: removeUserSubscriptionResponse.data
					};
				return dispatch(removeUserSubscriptionSuccessfulAction);
			} catch (error) {
				const removeUserSubscriptionFailedAction: SubscriptionPlanRemoveUserFailedAction =
					{
						type: UsersActionTypes.SUBSCRIPTION_PLAN_REMOVE_USER_FAILED
					};
				dispatch(removeUserSubscriptionFailedAction);
				return Promise.reject(error);
			}
		},
	updatePricingPlan:
		(upgradeDowngradeRequest: UpdatePricingPlanRequest) =>
		async (
			dispatch: Dispatch
		): Promise<SubscriptionPlanUpgradeDowngradeSuccessfulAction> => {
			const upgradeDowngradeSubscriptionRequestedAction: SubscriptionPlanUpgradeDowngradeRequestedAction =
				{
					type: UsersActionTypes.SUBSCRIPTION_PLAN_UPGRADE_DOWNGRADE_REQUESTED
				};
			dispatch(upgradeDowngradeSubscriptionRequestedAction);
			try {
				const pricingPlanName =
					upgradeDowngradeRequest.plan as PricingPlanNames;
				if (
					!!!pricingPlanName.length ||
					!Object.values(PricingPlanNames).includes(pricingPlanName)
				) {
					upgradeDowngradeRequest.plan = PricingPlanNames.LAUNCH;
				}

				const upgradeDowngradeSubscriptionResponse =
					await updatePricingPlan(upgradeDowngradeRequest);
				invalidateBillingAndSubscriptionCaches();
				const upgradeDowngradeSubscriptionSuccessfulAction: SubscriptionPlanUpgradeDowngradeSuccessfulAction =
					{
						type: UsersActionTypes.SUBSCRIPTION_PLAN_UPGRADE_DOWNGRADE_SUCCESSFUL,
						subscriptionDetails:
							upgradeDowngradeSubscriptionResponse.data
					};
				return dispatch(upgradeDowngradeSubscriptionSuccessfulAction);
			} catch (error) {
				const upgradeDowngradeSubscriptionFailedAction: SubscriptionPlanUpgradeDowngradeFailedAction =
					{
						type: UsersActionTypes.SUBSCRIPTION_PLAN_UPGRADE_DOWNGRADE_FAILED
					};
				dispatch(upgradeDowngradeSubscriptionFailedAction);
				return Promise.reject(error);
			}
		},
	planEstimate:
		(pricingPlanEstimateRequest: PricingPlanEstimateRequest) =>
		async (
			dispatch: Dispatch
		): Promise<SubscriptionPlanEstimateSuccessfulAction> => {
			const pricingPlanEstimateRequestedAction: SubscriptionPlanEstimateRequestedAction =
				{
					type: UsersActionTypes.SUBSCRIPTION_PLAN_ESTIMATE_REQUESTED
				};
			dispatch(pricingPlanEstimateRequestedAction);
			try {
				const pricingPlanName =
					pricingPlanEstimateRequest.plan as PricingPlanNames;
				if (
					!!!pricingPlanName.length ||
					!Object.values(PricingPlanNames).includes(pricingPlanName)
				) {
					pricingPlanEstimateRequest.plan = PricingPlanNames.LAUNCH;
				}

				const pricingPlanEstimateResponse = await planEstimate(
					pricingPlanEstimateRequest
				);
				const pricingPlanEstimateSuccessfulAction: SubscriptionPlanEstimateSuccessfulAction =
					{
						type: UsersActionTypes.SUBSCRIPTION_PLAN_ESTIMATE_SUCCESSFUL,
						subscriptionEstimate: pricingPlanEstimateResponse
					};
				return dispatch(pricingPlanEstimateSuccessfulAction);
			} catch (error) {
				const pricingPlanEstimateFailedAction: SubscriptionPlanEstimateFailedAction =
					{
						type: UsersActionTypes.SUBSCRIPTION_PLAN_ESTIMATE_FAILED
					};
				dispatch(pricingPlanEstimateFailedAction);
				return Promise.reject(error);
			}
		},
	showUpdateCardModal: () => {
		return (dispatch: Dispatch): ShowUpdateCardModalAction => {
			const showUpdateCardModalAction: ShowUpdateCardModalAction = {
				type: UsersActionTypes.SHOW_UPDATE_CARD_MODAL
			};
			return dispatch(showUpdateCardModalAction);
		};
	},
	hideUpdateCardModal: () => {
		return (dispatch: Dispatch): HideUpdateCardModalAction => {
			const hideUpdateCardModalAction: HideUpdateCardModalAction = {
				type: UsersActionTypes.HIDE_UPDATE_CARD_MODAL
			};
			return dispatch(hideUpdateCardModalAction);
		};
	},
	showBillingInfoLoader: () => {
		return (dispatch: Dispatch): ShowBillingInfoLoaderAction => {
			const showBillingInfoLoaderAction: ShowBillingInfoLoaderAction = {
				type: UsersActionTypes.SHOW_BILLING_INFO_LOADER
			};
			return dispatch(showBillingInfoLoaderAction);
		};
	},
	hideBillingInfoLoader: () => {
		return (dispatch: Dispatch): HideBillingInfoLoaderAction => {
			const hideBillingInfoLoaderAction: HideBillingInfoLoaderAction = {
				type: UsersActionTypes.HIDE_BILLING_INFO_LOADER
			};
			return dispatch(hideBillingInfoLoaderAction);
		};
	},
	addEmailAlias:
		(addEmailAliasRequest: EmailAliasRequest) =>
		async (dispatch: Dispatch): Promise<AddEmailAliasSuccessfulAction> => {
			const addEmailAliasRequestedAction: AddEmailAliasRequestedAction = {
				type: UsersActionTypes.ADD_EMAIL_ALIAS_REQUESTED
			};
			dispatch(addEmailAliasRequestedAction);
			try {
				const response = await addEmailAlias(addEmailAliasRequest);
				const addEmailAliasSuccessfulAction: AddEmailAliasSuccessfulAction =
					{
						type: UsersActionTypes.ADD_EMAIL_ALIAS_SUCCESSFUL,
						addEmailAliasResponse: response
					};
				return dispatch(addEmailAliasSuccessfulAction);
			} catch (err) {
				const addEmailAliasFailedAction: AddEmailAliasFailedAction = {
					type: UsersActionTypes.ADD_EMAIL_ALIAS_FAILED
				};
				dispatch(addEmailAliasFailedAction);
				return Promise.reject(err);
			}
		},
	deleteEmailAlias:
		(deleteEmailAliasRequest: EmailAliasRequest) =>
		async (
			dispatch: Dispatch
		): Promise<DeleteEmailAliasSuccessfulAction> => {
			const deleteEmailAliasRequestedAction: DeleteEmailAliasRequestedAction =
				{
					type: UsersActionTypes.DELETE_EMAIL_ALIAS_REQUESTED
				};
			dispatch(deleteEmailAliasRequestedAction);
			try {
				const response = await deleteEmailAlias(
					deleteEmailAliasRequest
				);
				const deleteEmailAliasSuccessfulAction: DeleteEmailAliasSuccessfulAction =
					{
						type: UsersActionTypes.DELETE_EMAIL_ALIAS_SUCCESSFUL,
						deleteEmailAliasResponse: response
					};
				return dispatch(deleteEmailAliasSuccessfulAction);
			} catch (err) {
				const deleteEmailAliasFailedAction: DeleteEmailAliasFailedAction =
					{
						type: UsersActionTypes.DELETE_EMAIL_ALIAS_FAILED
					};
				dispatch(deleteEmailAliasFailedAction);
				return Promise.reject(err);
			}
		},
	disableUser: (userId: string) => {
		return async (
			dispatch: Dispatch
		): Promise<DisableUserSuccessfulAction> => {
			const disableUserRequestedAction: DisableUserRequestedAction = {
				type: UsersActionTypes.DISABLE_USER_REQUESTED
			};
			dispatch(disableUserRequestedAction);
			try {
				const updatedUserData = await disableUser(userId);
				const disableUserSucccessfulAction: DisableUserSuccessfulAction =
					{
						type: UsersActionTypes.DISABLE_USER_SUCCESSFUL,
						userData: updatedUserData
					};
				return dispatch(disableUserSucccessfulAction);
			} catch (error) {
				const disableUserFailedAction: DisableUserFailedAction = {
					type: UsersActionTypes.DISABLE_USER_FAILED
				};
				dispatch(disableUserFailedAction);

				return Promise.reject(error);
			}
		};
	},
	enableUser: (userId: string) => {
		return async (
			dispatch: Dispatch
		): Promise<EnableUserSuccessfulAction> => {
			const enableUserRequestedAction: EnableUserRequestedAction = {
				type: UsersActionTypes.ENABLE_USER_REQUESTED
			};
			dispatch(enableUserRequestedAction);
			try {
				const updatedUserData = await enableUser(userId);
				const enableUserSucccessfulAction: EnableUserSuccessfulAction =
					{
						type: UsersActionTypes.ENABLE_USER_SUCCESSFUL,
						userData: updatedUserData
					};
				return dispatch(enableUserSucccessfulAction);
			} catch (error) {
				const enableUserFailedAction: EnableUserFailedAction = {
					type: UsersActionTypes.ENABLE_USER_FAILED
				};
				dispatch(enableUserFailedAction);

				return Promise.reject(error);
			}
		};
	},
	fetchUserCalendars: () => {
		return async (
			dispatch: Dispatch
		): Promise<FetchUserCalendarSuccessfulAction> => {
			const fetchUserCalendarRequestedAction: FetchUserCalendarRequestedAction =
				{
					type: UsersActionTypes.FETCH_USER_CALENDARS_REQUESTED
				};
			dispatch(fetchUserCalendarRequestedAction);
			try {
				const userCalendarResponse = await fetchUserCalendars();
				const fetchUserCalendarSuccessfulAction: FetchUserCalendarSuccessfulAction =
					{
						type: UsersActionTypes.FETCH_USER_CALENDARS_SUCCESSFUL,
						userCalendars: userCalendarResponse.data
					};
				return dispatch(fetchUserCalendarSuccessfulAction);
			} catch (error) {
				const fetchUserCalendarFailedAction: FetchUserCalendarFailedAction =
					{
						type: UsersActionTypes.FETCH_USER_CALENDARS_FAILED
					};
				dispatch(fetchUserCalendarFailedAction);

				return Promise.reject(error);
			}
		};
	},
	resetUserCalendar: () => {
		return (dispatch: Dispatch): ResetUserCalendarsAction => {
			const resetUserCalendarAction: ResetUserCalendarsAction = {
				type: UsersActionTypes.RESET_USER_CALENDARS
			};

			return dispatch(resetUserCalendarAction);
		};
	},
	connectCalendar: (
		connectCalendarPostRequest: ConnectCalendarPostRequest
	) => {
		return async (
			dispatch: Dispatch
		): Promise<ConnectCalendarSuccessfulAction> => {
			const connectCalendarRequestedAction: ConnectCalendarRequestedAction =
				{
					type: UsersActionTypes.CONNECT_CALENDAR_REQUESTED
				};
			dispatch(connectCalendarRequestedAction);
			try {
				await connectUserCalendar(connectCalendarPostRequest);
				const connectCalendarSuccessfulAction: ConnectCalendarSuccessfulAction =
					{
						type: UsersActionTypes.CONNECT_CALENDAR_SUCCESSFUL
					};
				return dispatch(connectCalendarSuccessfulAction);
			} catch (error) {
				const connectCalendarFailedAction: ConnectCalendarFailedAction =
					{
						type: UsersActionTypes.CONNECT_CALENDAR_FAILED
					};
				dispatch(connectCalendarFailedAction);

				return Promise.reject(error);
			}
		};
	},
	refreshIntegrations: () => {
		return async (
			dispatch: Dispatch
		): Promise<FetchCompanyIntegrationsSuccessfulAction> => {
			const fetchCompanyIntegrationRequestedAction: FetchCompanyIntegrationsRequestedAction =
				{
					type: UsersActionTypes.FETCH_COMPANY_INTEGRATIONS_REQUESTED
				};
			dispatch(fetchCompanyIntegrationRequestedAction);
			try {
				const response = await getCompanyIntegrations();
				const fetchCompanyIntegrationsSuccessfulAction: FetchCompanyIntegrationsSuccessfulAction =
					{
						type: UsersActionTypes.FETCH_COMPANY_INTEGRATIONS_SUCCESSFUL,
						integrations: response.integrations
					};
				return dispatch(fetchCompanyIntegrationsSuccessfulAction);
			} catch (error) {
				const fetchCompanyIntegrationFailedAction: FetchCompanyIntegrationsFailedAction =
					{
						type: UsersActionTypes.FETCH_COMPANY_INTEGRATIONS_FAILED
					};
				dispatch(fetchCompanyIntegrationFailedAction);

				return Promise.reject(error);
			}
		};
	},
	getCharbeeInvoicesList: (invoicePageOffset?: string[]) => {
		return async (
			dispatch: Dispatch
		): Promise<ChargebeeInvoiceListSuccessfulAction> => {
			const chargebeeInvoiceListRequestedAction: ChargebeeInvoiceListRequestedAction =
				{
					type: UsersActionTypes.CHARGEBEE_INVOICES_LIST_REQUESTED
				};
			dispatch(chargebeeInvoiceListRequestedAction);
			try {
				const chargebeeInvoicesListResponse =
					await getChargebeeInvoiceList(invoicePageOffset);
				const chargebeeInvoiceListSuccessfulAction: ChargebeeInvoiceListSuccessfulAction =
					{
						type: UsersActionTypes.CHARGEBEE_INVOICES_LIST_SUCCESSFUL,
						chargebeeInvoicesList: chargebeeInvoicesListResponse
					};
				return dispatch(chargebeeInvoiceListSuccessfulAction);
			} catch (error) {
				const chargebeeInvoiceListFailedAction: ChargebeeInvoiceListFailedAction =
					{
						type: UsersActionTypes.CHARGEBEE_INVOICES_LIST_FAILED
					};
				dispatch(chargebeeInvoiceListFailedAction);

				return Promise.reject(error);
			}
		};
	},
	getCharbeeInvoiceDetails: (invoiceId: string) => {
		return async (
			dispatch: Dispatch
		): Promise<ChargebeeInvoiceDetailsSuccessfulAction> => {
			const chargebeeInvoiceDetailsRequestedAction: ChargebeeInvoiceDetailsRequestedAction =
				{
					type: UsersActionTypes.CHARGEBEE_INVOICE_DETAILS_REQUESTED
				};
			dispatch(chargebeeInvoiceDetailsRequestedAction);
			try {
				const chargebeeInvoiceDetailsResponse =
					await getChargebeeInvoiceDetails(invoiceId);
				const chargebeeInvoiceDetailsSuccessfulAction: ChargebeeInvoiceDetailsSuccessfulAction =
					{
						type: UsersActionTypes.CHARGEBEE_INVOICE_DETAILS_SUCCESSFUL,
						chargebeeInvoice: chargebeeInvoiceDetailsResponse
					};
				return dispatch(chargebeeInvoiceDetailsSuccessfulAction);
			} catch (error) {
				const chargebeeInvoiceDetailsFailedAction: ChargebeeInvoiceDetailsFailedAction =
					{
						type: UsersActionTypes.CHARGEBEE_INVOICE_DETAILS_FAILED
					};
				dispatch(chargebeeInvoiceDetailsFailedAction);

				return Promise.reject(error);
			}
		};
	},
	getChargebeeCreditNotesList: (creditNotePageOffset?: string[]) => {
		return async (
			dispatch: Dispatch
		): Promise<ChargebeeCreditNotesListSuccessfulAction> => {
			const chargebeeCreditNotesListRequestedAction: ChargebeeCreditNotesListRequestedAction =
				{
					type: UsersActionTypes.CHARGEBEE_CREDIT_NOTES_LIST_REQUESTED
				};
			dispatch(chargebeeCreditNotesListRequestedAction);
			try {
				const chargebeeCreditNotesListResponse =
					await getChargebeeCreditNotesList(creditNotePageOffset);
				const chargebeeCreditNoteListSuccessfulAction: ChargebeeCreditNotesListSuccessfulAction =
					{
						type: UsersActionTypes.CHARGEBEE_CREDIT_NOTES_LIST_SUCCESSFUL,
						chargebeeCreditNotesList:
							chargebeeCreditNotesListResponse
					};
				return dispatch(chargebeeCreditNoteListSuccessfulAction);
			} catch (error) {
				const chargebeeCreditNotesListFailedAction: ChargebeeCreditNotesListFailedAction =
					{
						type: UsersActionTypes.CHARGEBEE_CREDIT_NOTES_LIST_FAILED
					};
				dispatch(chargebeeCreditNotesListFailedAction);

				return Promise.reject(error);
			}
		};
	},
	getChargebeeCreditNoteDetails: (creditNoteId: string) => {
		return async (
			dispatch: Dispatch
		): Promise<ChargebeeCreditNoteDetailsSuccessfulAction> => {
			const chargebeeCreditNoteDetailsRequestedAction: ChargebeeCreditNoteDetailsRequestedAction =
				{
					type: UsersActionTypes.CHARGEBEE_CREDIT_NOTE_DETAILS_REQUESTED
				};
			dispatch(chargebeeCreditNoteDetailsRequestedAction);
			try {
				const chargebeeCreditNoteDetailsResponse =
					await getChargebeeCreditNoteDetails(creditNoteId);
				const chargebeeCreditNoteDetailsSuccessfulAction: ChargebeeCreditNoteDetailsSuccessfulAction =
					{
						type: UsersActionTypes.CHARGEBEE_CREDIT_NOTE_DETAILS_SUCCESSFUL,
						chargebeeCreditNote: chargebeeCreditNoteDetailsResponse
					};
				return dispatch(chargebeeCreditNoteDetailsSuccessfulAction);
			} catch (error) {
				const chargebeeCreditNoteDetailsFailedAction: ChargebeeCreditNoteDetailsFailedAction =
					{
						type: UsersActionTypes.CHARGEBEE_CREDIT_NOTE_DETAILS_FAILED
					};
				dispatch(chargebeeCreditNoteDetailsFailedAction);

				return Promise.reject(error);
			}
		};
	},
	pauseCharbeeSubscription: (
		pauseChargebeeSubscriptionRequest: PauseChargebeeSubscriptionRequest
	) => {
		return async (
			dispatch: Dispatch
		): Promise<PauseChargebeeSubscriptionSuccessfulAction> => {
			const pauseChargebeeSubscriptionRequestedAction: PauseChargebeeSubscriptionRequestedAction =
				{
					type: UsersActionTypes.PAUSE_CHARGEBEE_SUBSCRIPTION_REQUESTED
				};
			dispatch(pauseChargebeeSubscriptionRequestedAction);
			try {
				await pauseChargebeeSubscription(
					pauseChargebeeSubscriptionRequest
				);
				const pauseChargebeeSubscriptionSuccessfulAction: PauseChargebeeSubscriptionSuccessfulAction =
					{
						type: UsersActionTypes.PAUSE_CHARGEBEE_SUBSCRIPTION_SUCCESSFUL
					};
				return dispatch(pauseChargebeeSubscriptionSuccessfulAction);
			} catch (error) {
				const pauseChargebeeSubscriptionFailedAction: PauseChargebeeSubscriptionFailedAction =
					{
						type: UsersActionTypes.PAUSE_CHARGEBEE_SUBSCRIPTION_FAILED
					};
				dispatch(pauseChargebeeSubscriptionFailedAction);

				return Promise.reject(error);
			}
		};
	},
	verifyUserEmail: (emailId: string, name: string) => {
		return async (dispatch: Dispatch): Promise<VerifyUserEmail> => {
			try {
				await verifyUserEmailService(emailId, name);
				const verifyUserEmail: VerifyUserEmail = {
					type: UsersActionTypes.VERIFY_USER_EMAIL
				};
				return dispatch(verifyUserEmail);
			} catch (error) {
				return Promise.reject(error);
			}
		};
	},
	deleteCustomEmail: (emailId: string) => {
		return async (dispatch: Dispatch): Promise<DeleteCustomEmail> => {
			try {
				await deleteCustomEmailService(emailId);
				const deleteCustomEmail: DeleteCustomEmail = {
					type: UsersActionTypes.DELETE_CUSTOM_EMAIL
				};
				return dispatch(deleteCustomEmail);
			} catch (error) {
				return Promise.reject(error);
			}
		};
	},
	addCustomDomain: (addCustomDomainObject: AddCustomDomainContract) => {
		return async (dispatch: Dispatch): Promise<AddCustomDomain> => {
			try {
				const addCustomDomainResponse = await addCustomDomainService(
					addCustomDomainObject
				);
				const addCustomDomainRequest: AddCustomDomain = {
					type: UsersActionTypes.ADD_CUSTOM_DOMAIN,
					customDomainItem: addCustomDomainResponse
				};
				return dispatch(addCustomDomainRequest);
			} catch (error) {
				return Promise.reject(error);
			}
		};
	},
	deleteCustomDomain: (deleteCustomDomainObject: AddCustomDomainContract) => {
		return async (dispatch: Dispatch): Promise<DeleteCustomDomain> => {
			try {
				await deleteCustomDomainService(deleteCustomDomainObject);
				const deleteCustomDomainRequest: DeleteCustomDomain = {
					type: UsersActionTypes.DELETE_CUSTOM_DOMAIN
				};
				return dispatch(deleteCustomDomainRequest);
			} catch (error) {
				return Promise.reject(error);
			}
		};
	},
	resendDomainVerification: (emailId: string) => {
		return async (
			dispatch: Dispatch
		): Promise<ResendDomainVerificationSuccessful> => {
			const resendDomainVerificationRequested: ResendDomainVerificationRequested =
				{
					type: UsersActionTypes.RESEND_DOMAIN_VERIFICATION_REQUESTED
				};
			dispatch(resendDomainVerificationRequested);
			try {
				await resendDomainVerificationEmail(emailId);
				const resendDomainVerificationSuccessful: ResendDomainVerificationSuccessful =
					{
						type: UsersActionTypes.RESEND_DOMAIN_VERIFICATION_SUCCESSFUL
					};
				return dispatch(resendDomainVerificationSuccessful);
			} catch (error) {
				const resendDomainVerificationFailed: ResendDomainVerificationFailed =
					{
						type: UsersActionTypes.RESEND_DOMAIN_VERIFICATION_FAILED
					};
				dispatch(resendDomainVerificationFailed);

				return Promise.reject(error);
			}
		};
	},
	updateEmailPreferences: (updatedEmailIntegration: EmailIntegration) => {
		return async (
			dispatch: Dispatch
		): Promise<UpdateEmailPreferencesSuccessful> => {
			const updateEmailPreferencesRequested: UpdateEmailPreferencesRequested =
				{
					type: UsersActionTypes.UPDATED_EMAIL_PREFERENCES_REQUESTED
				};
			dispatch(updateEmailPreferencesRequested);
			try {
				await updateEmailSettings(updatedEmailIntegration);
				const updateEmailPreferencesSuccessful: UpdateEmailPreferencesSuccessful =
					{
						type: UsersActionTypes.UPDATED_EMAIL_PREFERENCES_SUCCESSFUL
					};
				return dispatch(updateEmailPreferencesSuccessful);
			} catch (error) {
				const updateEmailPreferencesFailed: UpdateEmailPreferencesFailed =
					{
						type: UsersActionTypes.UPDATED_EMAIL_PREFERENCES_FAILED
					};
				dispatch(updateEmailPreferencesFailed);

				return Promise.reject(error);
			}
		};
	},
	fetchAllEmailSignatures: () => {
		return async (
			dispatch: Dispatch
		): Promise<FetchAllEmailSignaturesSuccessful> => {
			const fetchAllEmailSignaturesRequestedAction: FetchAllEmailSignaturesRequested =
				{
					type: UsersActionTypes.FETCH_EMAIL_SIGNATURES_REQUESTED
				};
			dispatch(fetchAllEmailSignaturesRequestedAction);
			try {
				const response = await fetchUserSignature();
				const fetchAllEmailSignaturesSuccessfulAction: FetchAllEmailSignaturesSuccessful =
					{
						type: UsersActionTypes.FETCH_EMAIL_SIGNATURES_SUCCESSFUL,
						emailSignature: response
					};
				return dispatch(fetchAllEmailSignaturesSuccessfulAction);
			} catch (error) {
				const fetchAllEmailSignaturesFailedAction: FetchAllEmailSignaturesFailed =
					{
						type: UsersActionTypes.FETCH_EMAIL_SIGNATURES_FAILED
					};
				dispatch(fetchAllEmailSignaturesFailedAction);

				return Promise.reject(error);
			}
		};
	},
	updateEmailSignatures: (updatedSignatureList: UserEmailSignature) => {
		return async (
			dispatch: Dispatch
		): Promise<UpdateEmailSignaturesSuccessful> => {
			const updateEmailSignaturesRequestedAction: UpdateEmailSignaturesRequested =
				{
					type: UsersActionTypes.UPDATE_EMAIL_SIGNATURES_REQUESTED
				};
			dispatch(updateEmailSignaturesRequestedAction);
			try {
				const response = await updateUserSignature(
					updatedSignatureList
				);
				const updateEmailSignaturesSuccessfulAction: UpdateEmailSignaturesSuccessful =
					{
						type: UsersActionTypes.UPDATE_EMAIL_SIGNATURES_SUCCESSFUL,
						emailSignature: response
					};
				return dispatch(updateEmailSignaturesSuccessfulAction);
			} catch (error) {
				const updateEmailSignaturesFailedAction: UpdateEmailSignaturesFailed =
					{
						type: UsersActionTypes.UPDATE_EMAIL_SIGNATURES_FAILED
					};
				dispatch(updateEmailSignaturesFailedAction);

				return Promise.reject(error);
			}
		};
	},
	resetUserState: () => {
		return (dispatch: Dispatch): ResetUserStateAction => {
			const resetUserStateAction: ResetUserStateAction = {
				type: UsersActionTypes.RESET_USER_STATE
			};

			return dispatch(resetUserStateAction);
		};
	},
	onboardingCallScheduled: () => {
		return async (
			dispatch: Dispatch
		): Promise<OnboardingCallScheduledSuccessfulAction> => {
			const onboardingCallScheduledRequestedAction: OnboardingCallScheduledRequestedAction =
				{
					type: UsersActionTypes.ONBOARDING_CALL_SCHEDULED_REQUESTED
				};
			dispatch(onboardingCallScheduledRequestedAction);
			try {
				await onboardingCallScheduled();
				const onboardingCallScheduledSuccessfullyAction: OnboardingCallScheduledSuccessfulAction =
					{
						type: UsersActionTypes.ONBOARDING_CALL_SCHEDULED_SUCCESSFUL,
						billingStatus:
							BillingStatusEnum.InTrialOnboardingScheduled
					};
				return dispatch(onboardingCallScheduledSuccessfullyAction);
			} catch (error) {
				const onboardingCallScheduledFailedAction: OnboardingCallScheduledFailedAction =
					{
						type: UsersActionTypes.ONBOARDING_CALL_SCHEDULED_FAILED
					};
				dispatch(onboardingCallScheduledFailedAction);

				return Promise.reject(error);
			}
		};
	},
	addDickerDataIntegration: (
		dickerDataRequest: DickerDataCreateOrUpdateRequest
	) => {
		return async (
			dispatch: Dispatch
		): Promise<AddDickerDataIntegrationSuccessfulAction> => {
			const addDickerDataIntegrationRequestAction: AddDickerDataIntegrationRequestedAction =
				{
					type: UsersActionTypes.ADD_DICKER_DATA_INTERGRATION_REQUESTED
				};

			dispatch(addDickerDataIntegrationRequestAction);

			try {
				const response = await addDickerDataIntegration(
					dickerDataRequest
				);

				const addDickerDataIntegrationSuccessfulAction: AddDickerDataIntegrationSuccessfulAction =
					{
						type: UsersActionTypes.ADD_DICKER_DATA_INTERGRATION_SUCCESSFUL,
						integrations: response.integrations
					};

				return dispatch(addDickerDataIntegrationSuccessfulAction);
			} catch (error) {
				const addDickerDataintegrationFailedAction: AddDickerDataIntegrationFailedAction =
					{
						type: UsersActionTypes.ADD_DICKER_DATA_INTERGRATION_FAILED
					};

				dispatch(addDickerDataintegrationFailedAction);

				return Promise.reject(error);
			}
		};
	},
	deleteDickerDataIntegration: () => {
		return async (
			dispatch: Dispatch
		): Promise<DeleteDickerDataIntegrationSuccessfulAction> => {
			const deleteDickerDataIntegrationRequestedAction: DeleteDickerDataIntegrationRequestedAction =
				{
					type: UsersActionTypes.DELETE_DICKER_DATA_INTEGRATION_REQUESTED
				};

			dispatch(deleteDickerDataIntegrationRequestedAction);

			try {
				const response = await deleteDickerDataIntegration();

				const deleteDickerDataIntegrationSuccessfulAction: DeleteDickerDataIntegrationSuccessfulAction =
					{
						type: UsersActionTypes.DELETE_DICKER_DATA_INTEGRATION_SUCCESSFUL,
						integrations: response.integrations
					};

				return dispatch(deleteDickerDataIntegrationSuccessfulAction);
			} catch (error) {
				const deleteDickerDataIntegrationFailedAction: DeleteDickerDataIntegrationFailedAction =
					{
						type: UsersActionTypes.DELETE_DICKER_DATA_INTEGRATION_FAILED
					};

				dispatch(deleteDickerDataIntegrationFailedAction);

				return Promise.reject(error);
			}
		};
	},
	addWestcoastIntegration: (
		westcoastRequest: AddWestcoastIntegrationRequest
	) => {
		return async (
			dispatch: Dispatch
		): Promise<AddWestcoastIntegrationSuccessfulAction> => {
			const addWestcoastIntegrationRequestAction: AddWestcoastIntegrationRequestedAction =
				{
					type: UsersActionTypes.ADD_WESTCOAST_INTERGRATION_REQUESTED
				};

			dispatch(addWestcoastIntegrationRequestAction);

			try {
				const response = await addWestcoastIntegration(
					westcoastRequest
				);

				const addWestcoastIntegrationSuccessfulAction: AddWestcoastIntegrationSuccessfulAction =
					{
						type: UsersActionTypes.ADD_WESTCOAST_INTERGRATION_SUCCESSFUL,
						integrations: response.integrations
					};

				return dispatch(addWestcoastIntegrationSuccessfulAction);
			} catch (error) {
				const addWestcoastintegrationFailedAction: AddWestcoastIntegrationFailedAction =
					{
						type: UsersActionTypes.ADD_WESTCOAST_INTERGRATION_FAILED
					};

				dispatch(addWestcoastintegrationFailedAction);

				return Promise.reject(error);
			}
		};
	},
	deleteWestcoastIntegration: () => {
		return async (
			dispatch: Dispatch
		): Promise<DeleteWestcoastIntegrationSuccessfulAction> => {
			const deleteWestcoastIntegrationRequestedAction: DeleteWestcoastIntegrationRequestedAction =
				{
					type: UsersActionTypes.DELETE_WESTCOAST_INTEGRATION_REQUESTED
				};

			dispatch(deleteWestcoastIntegrationRequestedAction);

			try {
				const response = await deleteWestcoastIntegration();

				const deleteWestcoastIntegrationSuccessfulAction: DeleteWestcoastIntegrationSuccessfulAction =
					{
						type: UsersActionTypes.DELETE_WESTCOAST_INTEGRATION_SUCCESSFUL,
						integrations: response.integrations
					};

				return dispatch(deleteWestcoastIntegrationSuccessfulAction);
			} catch (error) {
				const deleteWestcoastIntegrationFailedAction: DeleteWestcoastIntegrationFailedAction =
					{
						type: UsersActionTypes.DELETE_WESTCOAST_INTEGRATION_FAILED
					};

				dispatch(deleteWestcoastIntegrationFailedAction);

				return Promise.reject(error);
			}
		};
	},
	deleteCompanyRepairShoprIntegration: () => {
		return async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<DeleteCompanyRepairShoprIntegrationSuccessfulAction> => {
			const deleteCompanyRepairShoprIntegrationRequestedAction: DeleteCompanyRepairShoprIntegrationRequestedAction =
				{
					type: UsersActionTypes.DELETE_REPAIRSHOPR_INTEGRATION_REQUESTED
				};
			dispatch(deleteCompanyRepairShoprIntegrationRequestedAction);
			try {
				const globalState = getState();
				if (!globalState.user?.userCompany?.id?.length) {
					throw new Error("The user data is not available");
				}
				const response = await deleteRepairShoprIntegration();
				const deleteCompanySyncroIntegrationSuccessfulAction: DeleteCompanyRepairShoprIntegrationSuccessfulAction =
					{
						type: UsersActionTypes.DELETE_REPAIRSHOPR_INTEGRATION_SUCCESSFUL,
						integrations: response.integrations
					};
				return dispatch(deleteCompanySyncroIntegrationSuccessfulAction);
			} catch (error) {
				const deleteCompanySyncroIntegrationFailedAction: DeleteCompanySyncroIntegrationFailedAction =
					{
						type: UsersActionTypes.DELETE_COMPANY_SYNCRO_INTEGRATION_FAILED
					};
				dispatch(deleteCompanySyncroIntegrationFailedAction);

				return Promise.reject(error);
			}
		};
	},
	addCompanyRepairShoprIntegration: (
		addRepairShoprIntegrationRequest: AddRepairShoprIntegrationRequest
	) => {
		return async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<AddCompanyRepairShoprIntegrationSuccessfulAction> => {
			const addCompanyRepairShoprIntegrationRequestedAction: AddCompanyRepairShoprIntegrationRequestedAction =
				{
					type: UsersActionTypes.ADD_COMPANY_REPAIRSHOPR_INTEGRATION_REQUESTED
				};
			dispatch(addCompanyRepairShoprIntegrationRequestedAction);
			try {
				const globalState = getState();
				if (!globalState.user.userCompany.id.length) {
					throw new Error("unable to access user data");
				}
				const response = await addRepairShoprIntegration(
					addRepairShoprIntegrationRequest
				);
				const addCompanyRepairShoprIntegrationSuccessfulAction: AddCompanyRepairShoprIntegrationSuccessfulAction =
					{
						type: UsersActionTypes.ADD_COMPANY_REPAIRSHOPR_INTEGRATION_SUCCESSFUL,
						integrations: response.integrations
					};
				return dispatch(
					addCompanyRepairShoprIntegrationSuccessfulAction
				);
			} catch (error) {
				const addCompanyRepairShoprIntegrationFailedAction: AddCompanyRepairShoprIntegrationFailedAction =
					{
						type: UsersActionTypes.ADD_COMPANY_REPAIRSHOPR_INTEGRATION_FAILED
					};
				dispatch(addCompanyRepairShoprIntegrationFailedAction);

				return Promise.reject(error);
			}
		};
	},
	getCompanyHubspotIntegrationMetadata: () => {
		return async (
			dispatch: Dispatch
		): Promise<GetCompanyHubspotIntegrationMetadataSuccessfulAction> => {
			const getCompanyHubspotIntegrationMetadataRequestedAction: GetCompanyHubspotIntegrationMetadataRequestedAction =
				{
					type: UsersActionTypes.GET_COMPANY_HUBSPOT_INTEGRATION_METADATA_REQUESTED
				};
			dispatch(getCompanyHubspotIntegrationMetadataRequestedAction);
			try {
				const response = await getHubspotMetadata();
				const getCompanyHubspotIntegrationMetadataSuccessfulAction: GetCompanyHubspotIntegrationMetadataSuccessfulAction =
					{
						type: UsersActionTypes.GET_COMPANY_HUBSPOT_INTEGRATION_METADATA_SUCCESSFUL,
						hubspotMetadata: response
					};
				return dispatch(
					getCompanyHubspotIntegrationMetadataSuccessfulAction
				);
			} catch (error) {
				const getCompanyHubspotIntegrationMetadataFailedAction: GetCompanyHubspotIntegrationMetadataFailedAction =
					{
						type: UsersActionTypes.GET_COMPANY_HUBSPOT_INTEGRATION_METADATA_FAILED
					};
				dispatch(getCompanyHubspotIntegrationMetadataFailedAction);
				return Promise.reject(error);
			}
		};
	},

	getQuickbooksIntegrationMetadata: () => {
		return async (
			dispatch: Dispatch
		): Promise<GetQuickbooksIntegrationMetadataSuccessfulAction> => {
			const getQuickbooksIntegrationMetadataRequestedAction: GetQuickbooksIntegrationMetadataRequestedAction =
				{
					type: UsersActionTypes.GET_QUICKBOOKS_ONLINE_INTEGRATION_METADATA_REQUESTED
				};
			dispatch(getQuickbooksIntegrationMetadataRequestedAction);
			try {
				const quickbooksMetadata = await getQuickbooksMetadata();
				const getQuickbooksIntegrationMetadataSuccessfulAction: GetQuickbooksIntegrationMetadataSuccessfulAction =
					{
						type: UsersActionTypes.GET_QUICKBOOKS_ONLINE_INTEGRATION_METADATA_SUCCESSFUL,
						quickbooksMetadata: quickbooksMetadata
					};
				return dispatch(
					getQuickbooksIntegrationMetadataSuccessfulAction
				);
			} catch (error) {
				const getQuickbooksIntegrationMetadataFailedAction: GetQuickbooksIntegrationMetadataFailedAction =
					{
						type: UsersActionTypes.GET_QUICKBOOKS_ONLINE_INTEGRATION_METADATA_FAILED
					};
				dispatch(getQuickbooksIntegrationMetadataFailedAction);
				return Promise.reject(error);
			}
		};
	},
	addCompanyHubspotIntegration: (
		addHubspotIntegrationRequest: AddHubspotIntegrationRequest
	) => {
		return async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<AddCompanyHubspotIntegrationSuccessfulAction> => {
			const addCompanyHubspotIntegrationRequestedAction: AddCompanyHubspotIntegrationRequestedAction =
				{
					type: UsersActionTypes.ADD_COMPANY_HUBSPOT_INTEGRATION_REQUESTED
				};
			dispatch(addCompanyHubspotIntegrationRequestedAction);
			try {
				const globalState = getState();
				if (!globalState.user.userCompany.id.length) {
					throw new Error("unable to access user data");
				}
				const response = await addHubspotIntegration(
					addHubspotIntegrationRequest
				);
				const addCompanyHubspotIntegrationSuccessfulAction: AddCompanyHubspotIntegrationSuccessfulAction =
					{
						type: UsersActionTypes.ADD_COMPANY_HUBSPOT_INTEGRATION_SUCCESSFUL,
						integrations: response.integrations
					};
				return dispatch(addCompanyHubspotIntegrationSuccessfulAction);
			} catch (error) {
				const addCompanyHubspotIntegrationFailedAction: AddCompanyHubspotIntegrationFailedAction =
					{
						type: UsersActionTypes.ADD_COMPANY_HUBSPOT_INTEGRATION_FAILED
					};
				dispatch(addCompanyHubspotIntegrationFailedAction);
				return Promise.reject(error);
			}
		};
	},
	addQuickbooksIntegration: (
		addQuickbooksIntegrationRequest: AddQuickbooksIntegrationRequest
	) => {
		return async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<AddQuickbooksIntegrationSuccessfulAction> => {
			const addQuickbooksIntegrationRequestedAction: AddQuickbooksIntegrationRequestedAction =
				{
					type: UsersActionTypes.ADD_QUICKBOOKS_ONLINE_INTEGRATION_REQUESTED
				};
			dispatch(addQuickbooksIntegrationRequestedAction);
			try {
				const globalState = getState();
				if (!globalState.user.userCompany.id.length) {
					throw new Error("unable to access user data");
				}
				const quickbooksIntegrationResponse =
					await addQuickbooksIntegration(
						addQuickbooksIntegrationRequest
					);
				const addQuickbooksIntegrationSuccessfulAction: AddQuickbooksIntegrationSuccessfulAction =
					{
						type: UsersActionTypes.ADD_QUICKBOOKS_ONLINE_INTEGRATION_SUCCESSFUL,
						integrations: quickbooksIntegrationResponse.integrations
					};
				return dispatch(addQuickbooksIntegrationSuccessfulAction);
			} catch (error) {
				const addQuickbooksIntegrationFailedAction: AddQuickbooksIntegrationFailedAction =
					{
						type: UsersActionTypes.ADD_QUICKBOOKS_ONLINE_INTEGRATION_FAILED
					};
				dispatch(addQuickbooksIntegrationFailedAction);
				return Promise.reject(error);
			}
		};
	},
	deleteCompanyHubspotIntegration: () => {
		return async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<DeleteCompanyHubspotIntegrationSuccessfulAction> => {
			const deleteCompanyHubspotIntegrationRequestedAction: DeleteCompanyHubspotIntegrationRequestedAction =
				{
					type: UsersActionTypes.DELETE_COMPANY_HUBSPOT_INTEGRATION_REQUESTED
				};
			dispatch(deleteCompanyHubspotIntegrationRequestedAction);
			try {
				const globalState = getState();
				if (!globalState.user?.userCompany?.id?.length) {
					throw new Error("The user data is not available");
				}
				const response = await deleteHubspotIntegration();
				const deleteCompanyHubspotIntegrationSuccessfulAction: DeleteCompanyHubspotIntegrationSuccessfulAction =
					{
						type: UsersActionTypes.DELETE_COMPANY_HUBSPOT_INTEGRATION_SUCCESSFUL,
						integrations: response.integrations
					};
				return dispatch(
					deleteCompanyHubspotIntegrationSuccessfulAction
				);
			} catch (error) {
				const deleteCompanyHubspotIntegrationFailedAction: DeleteCompanyHubspotIntegrationFailedAction =
					{
						type: UsersActionTypes.DELETE_COMPANY_HUBSPOT_INTEGRATION_FAILED
					};
				dispatch(deleteCompanyHubspotIntegrationFailedAction);

				return Promise.reject(error);
			}
		};
	},
	addStripeConnection: () => {
		return async (dispatch: Dispatch): Promise<string> => {
			try {
				const addStripeConnectionRequestedAction: AddStripeConnectionRequestedAction =
					{
						type: UsersActionTypes.ADD_STRIPE_CONNECTION_REQUESTED
					};
				dispatch(addStripeConnectionRequestedAction);
				const redirectUrl = await addStripeConnection();
				const addStripeConnectionSuccessfulAction: AddStripeConnectionSuccessfulAction =
					{
						type: UsersActionTypes.ADD_STRIPE_CONNECTION_SUCCESSFUL
					};
				dispatch(addStripeConnectionSuccessfulAction);
				return redirectUrl;
			} catch (error) {
				const addStripeConnectionFailedAction: AddStripeConnectionFailedAction =
					{
						type: UsersActionTypes.ADD_STRIPE_CONNECTION_FAILED
					};
				dispatch(addStripeConnectionFailedAction);
				return Promise.reject(error);
			}
		};
	},
	deleteStripeConnection: () => {
		return async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<DeleteStripeConnectionSuccessfulAction> => {
			try {
				const globalState = getState();
				const deleteStripeConnectionRequestedAction: DeleteStripeConnectionRequestedAction =
					{
						type: UsersActionTypes.DELETE_STRIPE_CONNECTION_REQUESTED
					};
				dispatch(deleteStripeConnectionRequestedAction);
				const company = await deleteStripeUser();
				const deleteStripeConnectionSuccessfulAction: DeleteStripeConnectionSuccessfulAction =
					{
						type: UsersActionTypes.DELETE_STRIPE_CONNECTION_SUCCESSFUL,
						company: {
							...globalState.user.userCompany,
							...company
						}
					};
				return dispatch(deleteStripeConnectionSuccessfulAction);
			} catch (error) {
				const deleteStripeConnectionFailedAction: DeleteStripeConnectionFailedAction =
					{
						type: UsersActionTypes.DELETE_STRIPE_CONNECTION_FAILED
					};
				dispatch(deleteStripeConnectionFailedAction);
				return Promise.reject(error);
			}
		};
	},
	deletePaymentsConnection: () => {
		return async (dispatch: Dispatch): Promise<void> => {
			try {
				const company = await deletePaymentsUser();
				const deletePaymentsConnectionSuccessfulAction: DeletePaymentsConnectionSuccessfulAction =
					{
						type: UsersActionTypes.DELETE_PAYMENTS_CONNECTION_SUCCESSFUL,
						company
					};
				dispatch(deletePaymentsConnectionSuccessfulAction);
			} catch (error) {
				console.error(error);
			}
		};
	},
	deleteQuickbooksIntegration: () => {
		return async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<DeleteQuickbooksIntegrationSuccessfulAction> => {
			const deleteQuickbooksIntegrationRequestedAction: DeleteQuickbooksIntegrationRequestedAction =
				{
					type: UsersActionTypes.DELETE_QUICKBOOKS_ONLINE_INTEGRATION_REQUESTED
				};
			dispatch(deleteQuickbooksIntegrationRequestedAction);
			try {
				const globalState = getState();
				if (!globalState.user?.userCompany?.id?.length) {
					throw new Error("unable to access user data");
				}
				const response = await deleteQuickbooksIntegration();
				const deleteQuickbooksIntegrationSuccessfulAction: DeleteQuickbooksIntegrationSuccessfulAction =
					{
						type: UsersActionTypes.DELETE_QUICKBOOKS_ONLINE_INTEGRATION_SUCCESSFUL,
						integrations: response.integrations
					};
				return dispatch(deleteQuickbooksIntegrationSuccessfulAction);
			} catch (error) {
				const deleteQuickbooksIntegrationFailedAction: DeleteQuickbooksIntegrationFailedAction =
					{
						type: UsersActionTypes.DELETE_QUICKBOOKS_ONLINE_INTEGRATION_FAILED
					};
				dispatch(deleteQuickbooksIntegrationFailedAction);
				return Promise.reject(error);
			}
		};
	},
	testHaloPSACredentials: (
		haloPSAConnectFormValue: HaloPSAConnectFormValue
	) => {
		return async (
			dispatch: Dispatch
		): Promise<TestHaloPSACredentialsSuccessfulAction> => {
			const testHaloPSACredentialsRequestedAction: TestHaloPSACredentialsRequestedAction =
				{
					type: UsersActionTypes.TEST_HALOPSA_CREDENTIALS_REQUESTED
				};
			dispatch(testHaloPSACredentialsRequestedAction);

			try {
				const response = await testHaloPSACredentials(
					haloPSAConnectFormValue
				);

				const testHaloPSACredentialsSuccessfulAction: TestHaloPSACredentialsSuccessfulAction =
					{
						type: UsersActionTypes.TEST_HALOPSA_CREDENTIALS_SUCCESSFUL,
						...response
					};
				return dispatch(testHaloPSACredentialsSuccessfulAction);
			} catch (error) {
				const testHaloPSACredentialsFailedAction: TestHaloPSACredentialsFailedAction =
					{
						type: UsersActionTypes.TEST_HALOPSA_CREDENTIALS_FAILED
					};
				dispatch(testHaloPSACredentialsFailedAction);
				return Promise.reject(error);
			}
		};
	},
	fetchHaloPSAMetadata: (haloPSAAuthDetails: HaloPSAAuthDetails) => {
		return async (
			dispatch: Dispatch
		): Promise<FetchHaloPSAMetadataSuccessfulAction> => {
			const fetchHaloPSAMetadataRequestedAction: FetchHaloPSAMetadataRequestedAction =
				{
					type: UsersActionTypes.FETCH_HALOPSA_METADATA_REQUESTED
				};
			dispatch(fetchHaloPSAMetadataRequestedAction);

			try {
				const metadata = await fetchHaloPSAMetadata(haloPSAAuthDetails);

				const fetchHaloPSAMetadataSuccessfulAction: FetchHaloPSAMetadataSuccessfulAction =
					{
						type: UsersActionTypes.FETCH_HALOPSA_METADATA_SUCCESSFUL,
						metadata
					};
				return dispatch(fetchHaloPSAMetadataSuccessfulAction);
			} catch (error) {
				const fetchHaloPSAMetadataFailedAction: FetchHaloPSAMetadataFailedAction =
					{
						type: UsersActionTypes.FETCH_HALOPSA_METADATA_FAILED
					};
				dispatch(fetchHaloPSAMetadataFailedAction);
				return Promise.reject(error);
			}
		};
	},
	importHaloPSATaxInfo: (
		taxRegionId: string,
		deactivateExisting: boolean,
		authDetails: HaloPSAConnectFormValue
	) => {
		return async (
			dispatch: Dispatch
		): Promise<ImportHaloTaxInfoSuccessfulAction> => {
			const importHaloTaxInfoRequestedAction: ImportHaloTaxInfoRequestedAction =
				{
					type: UsersActionTypes.IMPORT_HALO_TAX_INFO_REQUESTED
				};
			dispatch(importHaloTaxInfoRequestedAction);

			try {
				const taxInfo = await importHaloPSATaxes(
					taxRegionId,
					deactivateExisting,
					authDetails
				);

				const importHaloTaxInfoSuccessfulAction: ImportHaloTaxInfoSuccessfulAction =
					{
						type: UsersActionTypes.IMPORT_HALO_TAX_INFO_SUCCESSFUL,
						taxInfo
					};
				return dispatch(importHaloTaxInfoSuccessfulAction);
			} catch (error) {
				const importHaloTaxInfoFailedAction: ImportHaloTaxInfoFailedlAction =
					{
						type: UsersActionTypes.IMPORT_HALO_TAX_INFO_FAILED
					};
				dispatch(importHaloTaxInfoFailedAction);
				return Promise.reject(error);
			}
		};
	},
	addHaloIntegration: (payload: HaloIntegrationRequest) => {
		return async (
			dispatch: Dispatch
		): Promise<AddHaloIntegrationSuccessfulAction> => {
			const addHaloIntegrationRequestedAction: AddHaloIntegrationRequestedAction =
				{
					type: UsersActionTypes.ADD_HALO_INTEGRATION_REQUESTED
				};
			dispatch(addHaloIntegrationRequestedAction);

			try {
				const integrations = await addHaloIntegration(payload);
				const addHaloIntegrationSuccessfulAction: AddHaloIntegrationSuccessfulAction =
					{
						type: UsersActionTypes.ADD_HALO_INTEGRATION_SUCCESSFUL,
						newIntegrations: integrations.integrations
					};

				return dispatch(addHaloIntegrationSuccessfulAction);
			} catch (error) {
				const addHaloIntegrationFailedAction: AddHaloIntegrationFailedlAction =
					{
						type: UsersActionTypes.ADD_HALO_INTEGRATION_FAILED
					};
				dispatch(addHaloIntegrationFailedAction);
				return Promise.reject(error);
			}
		};
	},
	deleteHaloPSAIntegration: () => {
		return async (
			dispatch: Dispatch
		): Promise<DeleteHaloPSAIntegrationSuccessfulAction> => {
			const deleteHaloPSAIntegrationRequestedAction: DeleteHaloPSAIntegrationRequestedAction =
				{
					type: UsersActionTypes.DELETE_HALO_INTEGRATION_REQUESTED
				};
			dispatch(deleteHaloPSAIntegrationRequestedAction);

			try {
				const updatedIntegrations = await deleteHaloPSAIntegration();
				const deleteHaloPSAIntegrationSuccessfulAction: DeleteHaloPSAIntegrationSuccessfulAction =
					{
						type: UsersActionTypes.DELETE_HALO_INTEGRATION_SUCCESSFUL,
						integrations: updatedIntegrations.integrations
					};
				return dispatch(deleteHaloPSAIntegrationSuccessfulAction);
			} catch (error) {
				const deleteHaloPSAIntegrationFailedAction: DeleteHaloPSAIntegrationFailedAction =
					{
						type: UsersActionTypes.DELETE_HALO_INTEGRATION_FAILED
					};
				dispatch(deleteHaloPSAIntegrationFailedAction);
				return Promise.reject(error);
			}
		};
	},
	fetchHaloPSAAuthDetails: () => {
		return async (
			dispatch: Dispatch
		): Promise<FetchHaloPSAAuthDetailsSuccessfulAction> => {
			const fetchHaloPSAAuthDetailsRequestedAction: FetchHaloPSAAuthDetailsRequestedAction =
				{
					type: UsersActionTypes.FETCH_HALOPSA_AUTH_DETAILS_REQUESTED
				};
			dispatch(fetchHaloPSAAuthDetailsRequestedAction);

			try {
				const authDetails = await fetchHaloPSAAuthDetails();
				const fetchHaloPSAAuthDetailsSuccessfulAction: FetchHaloPSAAuthDetailsSuccessfulAction =
					{
						type: UsersActionTypes.FETCH_HALOPSA_AUTH_DETAILS_SUCCESSFUL,
						authDetails
					};
				return dispatch(fetchHaloPSAAuthDetailsSuccessfulAction);
			} catch (error) {
				const fetchHaloPSAAuthDetailsFailedAction: FetchHaloPSAAuthDetailsFailedAction =
					{
						type: UsersActionTypes.FETCH_HALOPSA_AUTH_DETAILS_FAILED
					};
				dispatch(fetchHaloPSAAuthDetailsFailedAction);
				return Promise.reject(error);
			}
		};
	},
	addCustomDistributor: (payload: CustomDistributorUpdateRequest) => {
		return async (
			dispatch: Dispatch
		): Promise<AddCustomDistributorSuccessfulAction> => {
			const addCustomDistributorRequestedAction: AddCustomDistributorRequestedAction =
				{
					type: UsersActionTypes.ADD_CUSTOM_DISTRIBUTOR_REQUESTED
				};
			dispatch(addCustomDistributorRequestedAction);
			try {
				const companyDetails = await addCustomDistributor(payload);
				const addCustomDistributorSuccessfulAction: AddCustomDistributorSuccessfulAction =
					{
						type: UsersActionTypes.ADD_CUSTOM_DISTRIBUTOR_SUCCESSFUL,
						integrations: companyDetails.integrations
					};
				return dispatch(addCustomDistributorSuccessfulAction);
			} catch (error) {
				const addCustomDistributorFailedAction: AddCustomDistributorFailedAction =
					{
						type: UsersActionTypes.ADD_CUSTOM_DISTRIBUTOR_FAILED
					};
				dispatch(addCustomDistributorFailedAction);
				return Promise.reject(error);
			}
		};
	},

	deleteCustomDistributor: (integrationId: string) => {
		return async (
			dispatch: Dispatch
		): Promise<DeleteCustomDistributorSuccessfulAction> => {
			const deleteCustomDistributorRequestedAction: DeleteCustomDistributorRequestedAction =
				{
					type: UsersActionTypes.DELETE_CUSTOM_DISTRIBUTOR_REQUESTED
				};
			dispatch(deleteCustomDistributorRequestedAction);
			try {
				const response = await deleteCustomDistributor(integrationId);
				const deleteCustomDistributorSuccessfulAction: DeleteCustomDistributorSuccessfulAction =
					{
						type: UsersActionTypes.DELETE_CUSTOM_DISTRIBUTOR_SUCCESSFUL,
						integrations: response.integrations
					};
				return dispatch(deleteCustomDistributorSuccessfulAction);
			} catch (error) {
				const deleteCustomDistributorFailedAction: DeleteCustomDistributorFailedAction =
					{
						type: UsersActionTypes.DELETE_CUSTOM_DISTRIBUTOR_FAILED
					};
				dispatch(deleteCustomDistributorFailedAction);
				return Promise.reject(error);
			}
		};
	},
	connectDatekDistributor: (payload: { supplier_id: string }) => {
		return async (
			dispatch: Dispatch
		): Promise<ConnectDatekDistributorSuccessfulAction> => {
			const connectDatekDistributorRequestedAction: ConnectDatekDistributorRequestedAction =
				{
					type: UsersActionTypes.CONNECT_DATEK_DISTRIBUTOR_REQUESTED
				};
			dispatch(connectDatekDistributorRequestedAction);
			try {
				const response = await connectDatekDistributor(
					payload.supplier_id
				);
				const connectDatekDistributorSuccessfulAction: ConnectDatekDistributorSuccessfulAction =
					{
						type: UsersActionTypes.CONNECT_DATEK_DISTRIBUTOR_SUCCESSFUL,
						integrations: response.integrations
					};
				return dispatch(connectDatekDistributorSuccessfulAction);
			} catch (error) {
				const connectDatekDistributorFailedAction: ConnectDatekDistributorFailedAction =
					{
						type: UsersActionTypes.CONNECT_DATEK_DISTRIBUTOR_FAILED
					};
				dispatch(connectDatekDistributorFailedAction);
				return Promise.reject(error);
			}
		};
	},
	subscriptionPlanEstimateReset: () => {
		return (dispatch: Dispatch): SubscriptionPlanEstimateResetAction => {
			const subscriptionPlanEstimateReset: SubscriptionPlanEstimateResetAction =
				{
					type: UsersActionTypes.SUBSCRIPTION_PLAN_ESTIMATE_RESET
				};

			return dispatch(subscriptionPlanEstimateReset);
		};
	},
	editCompanyPreferences: (
		companyId: string,
		preferences: PreferenceSettings
	) => {
		return async (
			dispatch: Dispatch
		): Promise<EditCompanyPreferencesSuccessfulAction> => {
			const editCompanyUserRequestedAction: EditCompanyPreferencesRequestedAction =
				{
					type: UsersActionTypes.EDIT_COMPANY_PREFERENCES_REQUESTED
				};
			dispatch(editCompanyUserRequestedAction);
			try {
				const userCompany = await editCompanyPreferences(
					companyId,
					preferences
				);
				const editCompanyPreferencesSuccessfulAction: EditCompanyPreferencesSuccessfulAction =
					{
						type: UsersActionTypes.EDIT_COMPANY_PREFERENCES_SUCCESSFUL,
						userCompany
					};
				return dispatch(editCompanyPreferencesSuccessfulAction);
			} catch (error) {
				const editCompanyPreferencesFailedAction: EditCompanyPreferencesFailedAction =
					{
						type: UsersActionTypes.EDIT_COMPANY_PREFERENCES_FAILED
					};
				dispatch(editCompanyPreferencesFailedAction);
				return Promise.reject(error);
			}
		};
	},
	updateTranslateLanguage: (language: string) => {
		return async (
			dispatch: Dispatch
		): Promise<UpdateTranslateLanguageSuccessfulAction> => {
			try {
				const userCompany = await updateTranslateLanguage(language);
				const updateTranslateLanguageSuccessfulAction: UpdateTranslateLanguageSuccessfulAction =
					{
						type: UsersActionTypes.UDPATE_TRANSLATE_LANGUAGE_SUCCESSFUL,
						editUserCompany: userCompany
					};
				return dispatch(updateTranslateLanguageSuccessfulAction);
			} catch (error) {
				return Promise.reject(error);
			}
		};
	},
	updateInitialSyncStatus: (verb: NotificationVerb) => {
		return async (dispatch: Dispatch) => {
			const integrationToUpdate = NotificationVerbToReduxKeyMapping[verb];
			const updateInitialSyncStatusAction: UpdateInitialSyncStatusAction =
				{
					type: UsersActionTypes.UPDATE_INITIAL_SYNC_STATUS,
					integrationToUpdate
				};
			return dispatch(updateInitialSyncStatusAction);
		};
	},

	updateIntegrationPageDetails: (
		integrationPageDetailsUpdateRequest: IntegrationPageDetailsUpdateRequest
	) => {
		return async (dispatch: Dispatch) => {
			const companyDetailsResponse = await updateIntegrationPageDetails(
				integrationPageDetailsUpdateRequest
			);
			const updateIntegrationPageDetailsAction: UpdateIntegrationPageDetailsAction =
				{
					type: UsersActionTypes.UPDATE_INTEGRATIION_PAGE_DETAILS,
					companyId: integrationPageDetailsUpdateRequest.id,
					integrations: companyDetailsResponse.integrations
				};

			return dispatch(updateIntegrationPageDetailsAction);
		};
	},
	updateAIWriterTrialInfo: (aiWriterTrialInfo: TrialInfoAIWriter) => {
		return (dispatch: Dispatch): AIWriterInfoUpdateToCompanyDataAction => {
			const aIWriterInfoUpdateToCompanyDataAction: AIWriterInfoUpdateToCompanyDataAction =
				{
					type: UsersActionTypes.AI_WRITER_TRIAL_INFO_UPDATED,
					aiWriterTrialInfo: aiWriterTrialInfo
				};
			return dispatch(aIWriterInfoUpdateToCompanyDataAction);
		};
	},

	deleteAmazonDistributorIntegration: () => {
		return async (
			dispatch: Dispatch
		): Promise<DeleteAmazonDistributorSuccessfulAction> => {
			const deleteAmazonRequestedAction: DeleteAmazonDistributorRequestedAction =
				{
					type: UsersActionTypes.DELETE_AMAZON_DISTRIBUTOR_REQUESTED
				};
			dispatch(deleteAmazonRequestedAction);
			try {
				const deleteAmazonResponse = await deleteAmazonIntegration();
				const deleteAmazonSuccessfulAction: DeleteAmazonDistributorSuccessfulAction =
					{
						type: UsersActionTypes.DELETE_AMAZON_DISTRIBUTOR_SUCCESSFUL,
						integrations: deleteAmazonResponse.integrations
					};
				return dispatch(deleteAmazonSuccessfulAction);
			} catch (error) {
				console.error(error);
				const deleteAmazonFailedAction: DeleteAmazonDistributorFailedAction =
					{
						type: UsersActionTypes.DELETE_AMAZON_DISTRIBUTOR_FAILED
					};
				dispatch(deleteAmazonFailedAction);

				return Promise.reject(error);
			}
		};
	},
	getAmazonMetadata: () => {
		return async (
			dispatch: Dispatch
		): Promise<GetAmazonMetadataSuccessfulAction> => {
			const getAmazonMetadataRequestedAction: GetAmazonMetadataRequestedAction =
				{
					type: UsersActionTypes.GET_AMAZON_META_DATA_REQUESTED
				};
			dispatch(getAmazonMetadataRequestedAction);
			try {
				const response = await getAmazonMetadata();
				const getAmazonMetadataSuccessfulAction: GetAmazonMetadataSuccessfulAction =
					{
						type: UsersActionTypes.GET_AMAZON_META_DATA_SUCCESSFUL,
						amazonMetaData: response
					};
				return dispatch(getAmazonMetadataSuccessfulAction);
			} catch (error) {
				const getAmazonMetadataFailedAction: GetAmazonMetadataFailedAction =
					{
						type: UsersActionTypes.GET_AMAZON_META_DATA_FAILED
					};
				dispatch(getAmazonMetadataFailedAction);
				return Promise.reject(error);
			}
		};
	},
	updateAmazonProductAppliedFilter: (
		amazonAppliedFilters: AmazonProductAppliedFilters
	) => {
		return async (dispatch: Dispatch) => {
			const updateAmazonProductAppliedFilter: UpdateAmazonProductAppliedFilterAction =
				{
					type: UsersActionTypes.UPDATE_AMAZON_APPLIED_FILTER,
					amazonAppliedFilters
				};
			return dispatch(updateAmazonProductAppliedFilter);
		};
	}
};

export default UsersActionCreators;
